import React, { useState, useEffect, useCallback, useRef } from "react";
import { useParams, Link, useNavigate, useSearchParams } from "react-router-dom";
import API from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useAutoSave } from "@/hooks/use-idle-timeout";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CellInput } from "@/components/ui/cell-input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  ArrowLeft, Layers, Calculator, Shield, CheckCircle, FileText, Upload, RefreshCw, Download, Plus, Trash2, AlertTriangle, Send, Copy, History, Users, Clock, Pencil, Calendar, Save, XCircle, Share2, Link2, Globe, Mail, ExternalLink, RotateCcw, Eye, ChevronUp, ChevronDown, MoreHorizontal
} from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { HealthScoreBadge } from "@/components/HealthScoreBadge";
import { useConfirm } from "@/components/ConfirmDialog";
import { InfoTooltip } from "@/components/InfoTooltip";
import { useCurrency } from "@/lib/currency";

const STATUS_COLORS = {
  Draft: "bg-slate-800 text-slate-300 border-slate-700",
  "Ready for Review": "bg-blue-500/10 text-blue-400 border-blue-500/20",
  "Under Review": "bg-amber-500/10 text-amber-400 border-amber-500/20",
  "Revision Required": "bg-orange-500/10 text-orange-400 border-orange-500/20",
  Approved: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20",
  Rejected: "bg-rose-500/10 text-rose-400 border-rose-500/20",
  Issued: "bg-sky-500/10 text-sky-400 border-sky-500/20",
  Closed: "bg-slate-800 text-slate-400 border-slate-700",
};

const DEFAULT_ROLES = ["Director", "Associate", "Senior Engineer", "Engineer", "CAD/BIM Technician"];
const RISK_CATEGORIES = ["Scope Risk", "Programme Risk", "Coordination Risk", "Existing Services Risk", "Stakeholder Risk"];
const ALL_DISCIPLINES = ["Mechanical", "Electrical", "Public Health", "General"];

export default function ProjectView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isDirector, canViewTab, hasPermission } = useAuth();
  const cur = useCurrency();
  const [project, setProject] = useState(null);
  const [scope, setScope] = useState(null);
  const [pricing, setPricing] = useState(null);
  const [risk, setRisk] = useState(null);
  const [proposals, setProposals] = useState([]);
  const [files, setFiles] = useState([]);
  const [audit, setAudit] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchParams] = useSearchParams();
  const urlTab = searchParams.get("tab");
  const urlRequestId = searchParams.get("requestId");
  // Default to first available tab, or URL-specified tab
  const defaultTab = urlTab || (canViewTab("overview") ? "overview" : canViewTab("scope") ? "scope" : "timesheets");
  const [activeTab, setActiveTab] = useState(defaultTab);
  const [focusedRequestId, setFocusedRequestId] = useState(urlRequestId || null);
  const [permissions, setPermissions] = useState(null);
  const [activeRoles, setActiveRoles] = useState(DEFAULT_ROLES);

  const ROLES = activeRoles;

  const fetchAll = useCallback(async () => {
    try {
      const [pRes, sRes, prRes, rRes, propRes, fRes, aRes, permRes, rolesRes] = await Promise.all([
        API.get(`/projects/${id}`),
        API.get(`/projects/${id}/scope`),
        API.get(`/projects/${id}/pricing`),
        API.get(`/projects/${id}/risk`),
        API.get(`/projects/${id}/proposals`),
        API.get(`/projects/${id}/files`),
        API.get(`/projects/${id}/audit`),
        API.get(`/projects/${id}/permissions`),
        API.get(`/projects/${id}/roles`),
      ]);
      setProject(pRes.data);
      setScope(sRes.data);
      setPricing(prRes.data);
      setRisk(rRes.data);
      setProposals(propRes.data);
      setFiles(fRes.data);
      setAudit(aRes.data);
      setPermissions(permRes.data);
      // Use project-level roles (active only)
      const projectRoles = (rolesRes.data?.roles || []).filter(r => r.active !== false);
      if (projectRoles.length > 0) setActiveRoles(projectRoles.map(r => r.role));
    } catch (e) {
      toast.error("Failed to load project");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  if (loading) return <div className="animate-pulse space-y-4"><div className="h-10 w-64 bg-slate-800 rounded" /><div className="h-96 bg-slate-800 rounded-lg" /></div>;
  if (!project) return <div className="text-center py-12"><p className="text-slate-500">Project not found</p><Link to="/projects" className="text-blue-500">Back to projects</Link></div>;

  const handleDuplicate = async () => {
    try {
      const res = await API.post(`/projects/${id}/duplicate`);
      toast.success("Project duplicated");
      navigate(`/projects/${res.data.id}`);
    } catch { toast.error("Failed to duplicate"); }
  };

  return (
    <div data-testid="project-view" className="space-y-4 md:space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-center gap-3 md:gap-4 min-w-0">
          <Link to="/projects" className="text-slate-400 hover:text-slate-400 flex-shrink-0" data-testid="back-to-projects"><ArrowLeft className="w-5 h-5" /></Link>
          <div className="min-w-0">
            <h1 className="font-heading text-2xl md:text-3xl font-bold tracking-tight text-slate-100 truncate">{project.name}</h1>
            <p className="text-xs md:text-sm text-muted-foreground mt-0.5 truncate">{project.client_name} — {project.sector || "N/A"} {project.reference_number ? `— ${project.reference_number}` : ""}</p>
          </div>
        </div>
        <div className="flex items-center gap-2 md:gap-3 flex-shrink-0 ml-8 sm:ml-0">
          {isDirector && <Button variant="outline" size="sm" onClick={handleDuplicate} className="border-slate-800" data-testid="duplicate-project-btn"><Copy className="w-3.5 h-3.5 mr-1.5" /><span className="hidden sm:inline">Duplicate</span></Button>}
          <span className={`inline-flex items-center px-2.5 md:px-3 py-1 rounded-full text-xs md:text-sm font-medium border whitespace-nowrap ${STATUS_COLORS[project.status]}`} data-testid="project-status">{project.status}</span>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} data-testid="project-tabs">
        <TabsList className="bg-slate-800 border border-slate-800 tab-scroll flex w-full overflow-x-auto">
          {canViewTab("overview") && <TabsTrigger value="overview" data-testid="tab-overview" className="whitespace-nowrap">Overview</TabsTrigger>}
          {canViewTab("scope") && <TabsTrigger value="scope" data-testid="tab-scope" className="whitespace-nowrap"><Layers className="w-3.5 h-3.5 mr-1.5" />Scope</TabsTrigger>}
          {canViewTab("risk") && <TabsTrigger value="risk" data-testid="tab-risk" className="whitespace-nowrap"><Shield className="w-3.5 h-3.5 mr-1.5" />Risk</TabsTrigger>}
          {canViewTab("pricing") && <TabsTrigger value="pricing" data-testid="tab-pricing" className="whitespace-nowrap"><Calculator className="w-3.5 h-3.5 mr-1.5" />Pricing</TabsTrigger>}
          {canViewTab("approval") && <TabsTrigger value="approval" data-testid="tab-approval" className="whitespace-nowrap"><CheckCircle className="w-3.5 h-3.5 mr-1.5" />Approval</TabsTrigger>}
          {canViewTab("proposals") && <TabsTrigger value="proposals" data-testid="tab-proposals" className="whitespace-nowrap"><FileText className="w-3.5 h-3.5 mr-1.5" />Proposals</TabsTrigger>}
          {canViewTab("files") && <TabsTrigger value="files" data-testid="tab-files" className="whitespace-nowrap"><Upload className="w-3.5 h-3.5 mr-1.5" />Files</TabsTrigger>}
          {canViewTab("pricing") && <TabsTrigger value="requests" data-testid="tab-requests" className="whitespace-nowrap"><Mail className="w-3.5 h-3.5 mr-1.5" />Requests</TabsTrigger>}
          {canViewTab("timesheets") && <TabsTrigger value="timesheets" data-testid="tab-timesheets" className="whitespace-nowrap"><Clock className="w-3.5 h-3.5 mr-1.5" />Timesheets</TabsTrigger>}
        </TabsList>

        {canViewTab("overview") && <TabsContent value="overview"><OverviewTab project={project} pricing={pricing} risk={risk} isDirector={isDirector} permissions={permissions} onUpdate={fetchAll} /></TabsContent>}
        {canViewTab("scope") && <TabsContent value="scope"><ScopeTab projectId={id} project={project} scope={scope} onUpdate={fetchAll} permissions={permissions} activeRoles={ROLES} /></TabsContent>}
        {canViewTab("risk") && <TabsContent value="risk"><RiskTab projectId={id} risk={risk} pricing={pricing} onUpdate={fetchAll} permissions={permissions} /></TabsContent>}
        {canViewTab("pricing") && <TabsContent value="pricing"><PricingTab projectId={id} pricing={pricing} onUpdate={fetchAll} isDirector={isDirector} permissions={permissions} activeRoles={ROLES} /></TabsContent>}
        {canViewTab("approval") && <TabsContent value="approval"><ApprovalTab projectId={id} project={project} audit={audit} onUpdate={fetchAll} isDirector={isDirector} permissions={permissions} /></TabsContent>}
        {canViewTab("proposals") && <TabsContent value="proposals"><ProposalsTab projectId={id} proposals={proposals} project={project} onUpdate={fetchAll} permissions={permissions} /></TabsContent>}
        {canViewTab("files") && <TabsContent value="files"><FilesTab projectId={id} files={files} onUpdate={fetchAll} permissions={permissions} /></TabsContent>}
        {canViewTab("pricing") && <TabsContent value="requests"><PricingRequestsTab projectId={id} project={project} scope={scope} onUpdate={fetchAll} permissions={permissions} activeRoles={ROLES} focusedRequestId={focusedRequestId} setFocusedRequestId={setFocusedRequestId} /></TabsContent>}
        {canViewTab("timesheets") && <TabsContent value="timesheets"><TimesheetsTab projectId={id} project={project} pricing={pricing} permissions={permissions} activeRoles={ROLES} /></TabsContent>}
      </Tabs>
    </div>
  );
}

function OverviewTab({ project, pricing, risk, isDirector, permissions, onUpdate }) {
  const cur = useCurrency();
  const [healthScore, setHealthScore] = useState(null);
  const [teamData, setTeamData] = useState({ owner_id: "", owner_name: "", team: [] });
  const [allUsers, setAllUsers] = useState([]);
  const [showTeamEditor, setShowTeamEditor] = useState(false);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({});
  const [availableTechSystems, setAvailableTechSystems] = useState([]);
  const size = project.size_metrics || {};

  const SECTORS = ["Commercial", "Residential", "Healthcare", "Education", "Industrial", "Retail", "Mixed Use", "Data Centre", "Leisure"];
  const PROJECT_TYPES = ["New Build", "Refurbishment", "Fit Out", "Extension", "Conversion", "Renovation"];
  const PROCUREMENT_ROUTES = ["Traditional", "Design & Build", "Construction Management", "Management Contracting", "PFI/PPP", "Negotiated", "Framework", "Two Stage"];
  const MODELLING_METHODS = ["BIM", "2D CAD"];

  useEffect(() => {
    if (isDirector) {
      API.get(`/projects/${project.id}/health-score`).then(r => setHealthScore(r.data)).catch(() => {});
    }
    API.get(`/projects/${project.id}/team`).then(r => setTeamData(r.data)).catch(() => {});
    API.get("/admin/technical-systems").then(r => setAvailableTechSystems(r.data.filter(s => s.active))).catch(() => {});
  }, [project.id, isDirector, pricing, risk]);

  const startEditing = () => {
    setForm({
      name: project.name || "",
      client_name: project.client_name || "",
      location: project.location || "",
      sector: project.sector || "",
      project_type: project.project_type || "",
      procurement_route: project.procurement_route || "",
      stage_framework: project.stage_framework || "RIBA",
      modelling_method: project.modelling_method || "BIM",
      disciplines: project.disciplines || [],
      construction_value: project.construction_value || 0,
      notes: project.notes || "",
      size_metrics: {
        floor_area: size.floor_area || 0,
        number_of_floors: size.number_of_floors || 0,
        residential_units: size.residential_units || 0,
        plant_rooms: size.plant_rooms || 0,
        risers: size.risers || 0,
        technical_systems: Array.isArray(size.technical_systems) ? size.technical_systems : (typeof size.technical_systems === "number" ? [] : []),
      },
    });
    setEditing(true);
  };

  const saveChanges = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      await API.put(`/projects/${project.id}`, form);
      toast.success("Project updated");
      setEditing(false);
      onUpdate();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  // Auto-save on global auto-save event (if in edit mode)
  useAutoSave(() => {
    if (editing && !saving) {
      API.put(`/projects/${project.id}`, form)
        .then(() => toast.info("Auto-saved", { duration: 2000 }))
        .catch(() => {});
    }
  });

  const toggleDiscipline = (disc) => {
    const current = form.disciplines || [];
    const next = current.includes(disc) ? current.filter(d => d !== disc) : [...current, disc];
    setForm(prev => ({ ...prev, disciplines: next }));
  };

  const openTeamEditor = async () => {
    if (!showTeamEditor) {
      try {
        const r = await API.get("/users");
        setAllUsers(r.data);
      } catch {}
    }
    setShowTeamEditor(!showTeamEditor);
  };

  const addTeamMember = (userId) => {
    const u = allUsers.find(x => x.id === userId);
    if (!u || teamData.team.some(m => m.user_id === userId)) return;
    const updated = [...teamData.team, { user_id: u.id, name: u.name, email: u.email, role: u.role }];
    saveTeam(updated);
  };

  const removeTeamMember = (userId) => {
    const updated = teamData.team.filter(m => m.user_id !== userId);
    saveTeam(updated);
  };

  const saveTeam = async (team) => {
    try {
      const r = await API.put(`/projects/${project.id}/team`, { team });
      setTeamData(r.data);
      toast.success("Team updated");
      onUpdate();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update team");
    }
  };

  const ROLE_COLORS = {
    Admin: "bg-purple-500/10 text-purple-400 border-purple-500/20",
    Director: "bg-blue-500/10 text-blue-400 border-blue-500/20",
    "Senior Engineer": "bg-emerald-500/10 text-emerald-400 border-emerald-500/20",
    Engineer: "bg-slate-800 text-slate-300 border-slate-800",
  };

  const TIER_NAMES = { 1: "T1", 2: "T2", 3: "T3", 4: "T4", 5: "T5" };
  const TIER_COLORS = {
    1: "bg-slate-800 text-slate-400 border-slate-700",
    2: "bg-amber-500/10 text-amber-400 border-amber-500/20",
    3: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20",
    4: "bg-blue-500/10 text-blue-400 border-blue-500/20",
    5: "bg-purple-500/10 text-purple-400 border-purple-500/20",
  };

  const [memberSearch, setMemberSearch] = useState("");
  const [showMemberDropdown, setShowMemberDropdown] = useState(false);

  const inputClass = "mt-1 bg-slate-900 border-slate-800 text-sm font-medium";

  return (
    <div className="space-y-6" data-testid="overview-tab">
      {isDirector && healthScore && healthScore.color !== "gray" && (
        <HealthScoreBadge healthScore={healthScore} />
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card className="bg-slate-900 border border-slate-800 shadow-sm">
          <CardHeader className="border-b border-slate-800 bg-slate-800/50 py-3 px-4">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-semibold">Project Details</CardTitle>
              {!editing ? (
                <Button variant="outline" size="sm" onClick={startEditing} className="border-slate-800 text-xs h-7" data-testid="edit-overview-btn">
                  <Pencil className="w-3 h-3 mr-1" />Edit
                </Button>
              ) : (
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => setEditing(false)} className="border-slate-800 text-xs h-7" data-testid="cancel-edit-btn">Cancel</Button>
                  <Button size="sm" onClick={saveChanges} disabled={saving} className="bg-blue-600 hover:bg-blue-500 text-white text-xs h-7" data-testid="save-overview-btn">
                    <Save className="w-3 h-3 mr-1" />{saving ? "..." : "Save"}
                  </Button>
                </div>
              )}
            </div>
          </CardHeader>
          <CardContent className="p-4">
            {!editing ? (
              <div className="divide-y divide-slate-800">
                {[["Project Name", project.name], ["Client", project.client_name], ["Reference", project.reference_number], ["Location", project.location], ["Sector", project.sector], ["Type", project.project_type], ["Procurement", project.procurement_route], ["Framework", project.stage_framework], ["Modelling", project.modelling_method || "BIM"], ["Disciplines", (project.disciplines || []).join(", ")], ["Construction Value", project.construction_value ? `${cur.symbol}${project.construction_value.toLocaleString()}` : "—"]].map(([l, v]) => (
                  <div key={l} className="flex justify-between py-2"><span className="text-sm text-slate-500">{l}</span><span className="text-sm font-medium">{v || "—"}</span></div>
                ))}
                {project.notes && <div className="py-2"><span className="text-sm text-slate-500">Notes</span><p className="text-sm mt-1 text-slate-300">{project.notes}</p></div>}
              </div>
            ) : (
              <div className="space-y-3">
                <div><Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Project Name</Label><Input value={form.name} onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))} className={inputClass} data-testid="edit-name" /></div>
                <div><Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Client Name</Label><Input value={form.client_name} onChange={(e) => setForm(prev => ({ ...prev, client_name: e.target.value }))} className={inputClass} data-testid="edit-client" /></div>
                <div><Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Location</Label><Input value={form.location} onChange={(e) => setForm(prev => ({ ...prev, location: e.target.value }))} className={inputClass} data-testid="edit-location" /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Sector</Label>
                    <Select value={form.sector} onValueChange={(v) => setForm(prev => ({ ...prev, sector: v }))}>
                      <SelectTrigger className={inputClass} data-testid="edit-sector"><SelectValue /></SelectTrigger>
                      <SelectContent>{SECTORS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Project Type</Label>
                    <Select value={form.project_type} onValueChange={(v) => setForm(prev => ({ ...prev, project_type: v }))}>
                      <SelectTrigger className={inputClass} data-testid="edit-type"><SelectValue /></SelectTrigger>
                      <SelectContent>{PROJECT_TYPES.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Procurement Route</Label>
                    <Select value={form.procurement_route} onValueChange={(v) => setForm(prev => ({ ...prev, procurement_route: v }))}>
                      <SelectTrigger className={inputClass} data-testid="edit-procurement"><SelectValue /></SelectTrigger>
                      <SelectContent>{PROCUREMENT_ROUTES.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Modelling Method</Label>
                    <Select value={form.modelling_method} onValueChange={(v) => setForm(prev => ({ ...prev, modelling_method: v }))}>
                      <SelectTrigger className={inputClass} data-testid="edit-modelling"><SelectValue /></SelectTrigger>
                      <SelectContent>{MODELLING_METHODS.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Disciplines</Label>
                  <div className="flex gap-2 mt-1.5">
                    {ALL_DISCIPLINES.map(d => {
                      const active = (form.disciplines || []).includes(d);
                      return (
                        <button key={d} onClick={() => toggleDiscipline(d)} data-testid={`edit-disc-${d.charAt(0).toLowerCase()}`}
                          className={`px-3 py-1 rounded text-xs font-medium border transition-all ${active ? "bg-blue-500/10 border-blue-500/30 text-blue-400" : "bg-slate-800 border-slate-700 text-slate-500 hover:border-slate-600"}`}
                        >{d}</button>
                      );
                    })}
                  </div>
                </div>
                <div><Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Construction Value ({cur.symbol})</Label><CellInput type="number" value={form.construction_value} onChange={(e) => setForm(prev => ({ ...prev, construction_value: parseFloat(e.target.value) || 0 }))} className={inputClass} data-testid="edit-cv" /></div>
                <div><Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Notes</Label><textarea value={form.notes} onChange={(e) => setForm(prev => ({ ...prev, notes: e.target.value }))} className="mt-1 w-full bg-slate-900 border border-slate-800 text-sm rounded-md px-3 py-2 resize-none h-20 focus:outline-none focus:ring-1 focus:ring-blue-500" data-testid="edit-notes" /></div>
              </div>
            )}
          </CardContent>
        </Card>
        <div className="space-y-6">
          <Card className="bg-slate-900 border border-slate-800 shadow-sm">
            <CardHeader className="border-b border-slate-800 bg-slate-800/50 py-3 px-4"><CardTitle className="text-sm font-semibold">Size Metrics</CardTitle></CardHeader>
            <CardContent className="p-4">
              {!editing ? (
                <div className="divide-y divide-slate-800">
                  {[["Floor Area", size.floor_area ? `${size.floor_area.toLocaleString()} sqm` : "—"], ["Floors", size.number_of_floors || "—"], ["Residential Units", size.residential_units || "—"], ["Plant Rooms", size.plant_rooms || "—"], ["Risers", size.risers || "—"]].map(([l, v]) => (
                    <div key={l} className="flex justify-between py-2"><span className="text-sm text-slate-500">{l}</span><span className="text-sm font-mono font-medium">{v}</span></div>
                  ))}
                  <div className="py-2">
                    <span className="text-sm text-slate-500">Technical Systems</span>
                    <div className="mt-1.5 flex flex-wrap gap-1">
                      {Array.isArray(size.technical_systems) && size.technical_systems.length > 0
                        ? size.technical_systems.map(s => <Badge key={s} variant="outline" className="border-slate-700 text-[10px] h-5 px-1.5">{s}</Badge>)
                        : <span className="text-sm font-mono font-medium">—</span>
                      }
                    </div>
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  <div><Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Floor Area (sqm)</Label><CellInput type="number" value={form.size_metrics?.floor_area || ""} onChange={(e) => setForm(prev => ({ ...prev, size_metrics: { ...prev.size_metrics, floor_area: parseFloat(e.target.value) || 0 } }))} className={inputClass} data-testid="edit-floor-area" /></div>
                  <div><Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Number of Floors</Label><CellInput type="number" value={form.size_metrics?.number_of_floors || ""} onChange={(e) => setForm(prev => ({ ...prev, size_metrics: { ...prev.size_metrics, number_of_floors: parseInt(e.target.value) || 0 } }))} className={inputClass} data-testid="edit-floors" /></div>
                  <div><Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Residential Units</Label><CellInput type="number" value={form.size_metrics?.residential_units || ""} onChange={(e) => setForm(prev => ({ ...prev, size_metrics: { ...prev.size_metrics, residential_units: parseInt(e.target.value) || 0 } }))} className={inputClass} data-testid="edit-units" /></div>
                  <div><Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Plant Rooms</Label><CellInput type="number" value={form.size_metrics?.plant_rooms || ""} onChange={(e) => setForm(prev => ({ ...prev, size_metrics: { ...prev.size_metrics, plant_rooms: parseInt(e.target.value) || 0 } }))} className={inputClass} data-testid="edit-plant-rooms" /></div>
                  <div><Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Risers</Label><CellInput type="number" value={form.size_metrics?.risers || ""} onChange={(e) => setForm(prev => ({ ...prev, size_metrics: { ...prev.size_metrics, risers: parseInt(e.target.value) || 0 } }))} className={inputClass} data-testid="edit-risers" /></div>
                  <div>
                    <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2 block">Technical Systems</Label>
                    {["Mechanical", "Electrical", "Public Health"].map(group => {
                      const groupSystems = availableTechSystems.filter(s => s.group === group);
                      if (groupSystems.length === 0) return null;
                      return (
                        <div key={group} className="mb-2">
                          <p className="text-[10px] text-slate-500 uppercase tracking-wider font-semibold mb-1">{group}</p>
                          <div className="flex flex-wrap gap-1.5">
                            {groupSystems.map(sys => {
                              const selected = (form.size_metrics?.technical_systems || []).includes(sys.name);
                              return (
                                <button key={sys.id} type="button" onClick={() => setForm(prev => {
                                  const current = prev.size_metrics?.technical_systems || [];
                                  const next = selected ? current.filter(n => n !== sys.name) : [...current, sys.name];
                                  return { ...prev, size_metrics: { ...prev.size_metrics, technical_systems: next } };
                                })} className={`text-[10px] px-2 py-1 rounded-md border font-medium transition-colors ${selected ? "bg-blue-600/20 border-blue-600 text-blue-300" : "bg-slate-800 border-slate-700 text-slate-500 hover:text-slate-300"}`} data-testid={`tech-sys-${sys.name.replace(/[\s\/]/g, "-").toLowerCase()}`}>
                                  {sys.name}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Team Management */}
          <Card className="bg-slate-900 border border-slate-800 shadow-sm" data-testid="team-panel">
            <CardHeader className="border-b border-slate-800 bg-slate-800/50 py-3 px-4">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-semibold">Project Team</CardTitle>
                {isDirector && (
                  <Button variant="outline" size="sm" onClick={openTeamEditor} className="border-slate-800 text-xs h-7" data-testid="manage-team-btn">
                    {showTeamEditor ? "Done" : "Manage"}
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent className="p-4 space-y-3">
              {/* Owner */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 rounded-full bg-blue-500/15 text-blue-400 flex items-center justify-center text-xs font-bold">
                    {(teamData.owner_name || "?").charAt(0)}
                  </div>
                  <div>
                    <p className="text-sm font-medium text-slate-100">{teamData.owner_name || "Unknown"}</p>
                  </div>
                </div>
                <span className="text-[10px] font-bold uppercase tracking-wider text-blue-400 bg-blue-500/10 px-2 py-0.5 rounded border border-blue-500/20">Owner</span>
              </div>

              {/* Team Members */}
              {teamData.team.map(m => {
                const mUser = allUsers.find(u => u.id === m.user_id);
                const mTier = mUser?.permission_tier || 1;
                return (
                  <div key={m.user_id} className="flex items-center justify-between" data-testid={`team-member-${m.user_id}`}>
                    <div className="flex items-center gap-2">
                      <div className="w-7 h-7 rounded-full bg-slate-800 text-slate-400 flex items-center justify-center text-xs font-bold">
                        {(m.name || "?").charAt(0)}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-slate-100">{m.name}</p>
                        <p className="text-[10px] text-slate-500">{m.email}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded border ${ROLE_COLORS[m.role] || ROLE_COLORS.Engineer}`}>{m.role}</span>
                      <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded border ${TIER_COLORS[mTier]}`}>{TIER_NAMES[mTier]}</span>
                      {showTeamEditor && (
                        <button onClick={() => removeTeamMember(m.user_id)} className="text-slate-500 hover:text-red-400 transition-colors ml-1" data-testid={`remove-member-${m.user_id}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}

              {teamData.team.length === 0 && !showTeamEditor && (
                <p className="text-xs text-slate-500 text-center py-2">No team members assigned</p>
              )}

              {/* Add member — type-to-search dropdown */}
              {showTeamEditor && (
                <div className="pt-3 border-t border-slate-800 space-y-2" data-testid="add-member-section">
                  <p className="text-[10px] text-slate-500 uppercase tracking-wider font-semibold">Add Team Member</p>
                  <div className="relative">
                    <Input
                      value={memberSearch}
                      onChange={e => { setMemberSearch(e.target.value); setShowMemberDropdown(true); }}
                      onFocus={() => setShowMemberDropdown(true)}
                      placeholder="Type to search or click to browse..."
                      className="h-8 text-xs bg-slate-900 border-slate-800"
                      data-testid="add-member-search"
                    />
                    {showMemberDropdown && (
                      <div className="absolute z-50 mt-1 w-full max-h-48 overflow-y-auto bg-slate-900 border border-slate-700 rounded-lg shadow-xl">
                        {allUsers
                          .filter(u => u.id !== teamData.owner_id && !teamData.team.some(m => m.user_id === u.id))
                          .filter(u => !memberSearch || u.name?.toLowerCase().includes(memberSearch.toLowerCase()) || u.email?.toLowerCase().includes(memberSearch.toLowerCase()))
                          .map(u => {
                            const uTier = u.permission_tier || 1;
                            return (
                              <button
                                key={u.id}
                                onClick={() => { addTeamMember(u.id); setMemberSearch(""); setShowMemberDropdown(false); }}
                                className="w-full flex items-center justify-between px-3 py-2 hover:bg-slate-800 transition-colors text-left border-b border-slate-800/50 last:border-0"
                                data-testid={`user-option-${u.id}`}
                              >
                                <div className="flex items-center gap-2">
                                  <div className="w-6 h-6 rounded-full bg-slate-800 text-slate-400 flex items-center justify-center text-[10px] font-bold">
                                    {(u.name || "?").charAt(0)}
                                  </div>
                                  <div>
                                    <p className="text-xs font-medium text-slate-200">{u.name}</p>
                                    <p className="text-[10px] text-slate-500">{u.email}</p>
                                  </div>
                                </div>
                                <div className="flex items-center gap-1">
                                  <span className={`text-[9px] font-semibold px-1 py-0.5 rounded border ${ROLE_COLORS[u.role] || ROLE_COLORS.Engineer}`}>{u.role}</span>
                                  <span className={`text-[9px] font-semibold px-1 py-0.5 rounded border ${TIER_COLORS[uTier]}`}>{TIER_NAMES[uTier]}</span>
                                </div>
                              </button>
                            );
                          })}
                        {allUsers.filter(u => u.id !== teamData.owner_id && !teamData.team.some(m => m.user_id === u.id)).length === 0 && (
                          <p className="px-3 py-2 text-xs text-slate-500">All users are already on this team</p>
                        )}
                      </div>
                    )}
                  </div>
                  {showMemberDropdown && <div className="fixed inset-0 z-40" onClick={() => setShowMemberDropdown(false)} />}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Staff Levels */}
          <StaffLevelsPanel projectId={project.id} project={project} isDirector={isDirector} permissions={permissions} onUpdate={onUpdate} />
        </div>
      </div>
    </div>
  );
}

function StaffLevelsPanel({ projectId, project, isDirector, permissions, onUpdate }) {
  const [projectRoles, setProjectRoles] = useState([]);
  const [adminRoles, setAdminRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showAddRole, setShowAddRole] = useState(false);
  const [customRoleName, setCustomRoleName] = useState("");
  const [hoursCheck, setHoursCheck] = useState(null);
  const [editingRateRole, setEditingRateRole] = useState(null);
  const [rateForm, setRateForm] = useState({ charge_rate: 0, cost_rate: 0 });
  const confirm = useConfirm();

  const canEdit = permissions?.can_edit_project !== false;

  const fetchRoles = useCallback(() => {
    Promise.all([
      API.get(`/projects/${projectId}/roles`),
      API.get("/admin/rates"),
    ]).then(([rolesRes, ratesRes]) => {
      setProjectRoles(rolesRes.data?.roles || []);
      setAdminRoles(ratesRes.data.filter(r => r.archived !== true));
    }).catch(() => {}).finally(() => setLoading(false));
  }, [projectId]);

  useEffect(() => { fetchRoles(); }, [fetchRoles]);

  // Fetch hours check when entering edit mode
  useEffect(() => {
    if (editing) {
      API.get(`/projects/${projectId}/roles/hours-check`).then(res => setHoursCheck(res.data)).catch(() => {});
    }
  }, [editing, projectId]);

  const activeRoles = projectRoles.filter(r => r.active);
  const inactiveRoles = projectRoles.filter(r => !r.active);
  const availableAdminRoles = adminRoles.filter(ar => !projectRoles.some(pr => pr.role === ar.role));

  const toggleRole = async (roleName, currentActive) => {
    if (currentActive) {
      const roleHours = hoursCheck?.hours_by_role?.[roleName] || 0;
      const roleItems = hoursCheck?.items_by_role?.[roleName] || 0;
      let description = `Remove "${roleName}" from this project?`;
      if (roleHours > 0) {
        description += `\n\nThis role has ${roleHours} hours allocated across ${roleItems} deliverable${roleItems !== 1 ? "s" : ""}. These hours will be preserved but hidden from the scope builder and pricing.`;
      } else {
        description += `\n\nNo hours are currently allocated to this role.`;
      }
      const ok = await confirm({ title: "Remove Staff Level", description, confirmText: roleHours > 0 ? "Remove Anyway" : "Remove", variant: roleHours > 0 ? "destructive" : "warning" });
      if (!ok) return;
    }
    setSaving(true);
    try {
      const updated = projectRoles.map(r =>
        r.role === roleName ? { ...r, active: !currentActive } : r
      );
      await API.put(`/projects/${projectId}/roles`, { roles: updated });
      setProjectRoles(updated);
      toast.success(currentActive ? `${roleName} removed from project` : `${roleName} added back to project`);
      onUpdate();
    } catch { toast.error("Failed to update staff levels"); }
    finally { setSaving(false); }
  };

  const addRole = async (roleName) => {
    setSaving(true);
    try {
      const res = await API.post(`/projects/${projectId}/roles/add`, { role: roleName });
      setProjectRoles(res.data.roles);
      toast.success(`${roleName} added to project`);
      setShowAddRole(false);
      setCustomRoleName("");
      onUpdate();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to add role"); }
    finally { setSaving(false); }
  };

  const addCustomRole = () => {
    const name = customRoleName.trim();
    if (!name) { toast.error("Enter a role name"); return; }
    if (projectRoles.some(r => r.role.toLowerCase() === name.toLowerCase())) {
      toast.error("This role already exists in the project");
      return;
    }
    addRole(name);
  };

  const reorderRole = async (index, direction) => {
    const active = [...activeRoles];
    const swapIdx = index + direction;
    if (swapIdx < 0 || swapIdx >= active.length) return;
    [active[index], active[swapIdx]] = [active[swapIdx], active[index]];
    const updated = active.map((r, i) => ({ ...r, sort_order: i, active: true }));
    const inactive = inactiveRoles.map((r, i) => ({ ...r, sort_order: active.length + i }));
    const all = [...updated, ...inactive];
    setSaving(true);
    try {
      await API.put(`/projects/${projectId}/roles`, { roles: all });
      setProjectRoles(all);
      onUpdate();
    } catch { toast.error("Failed to reorder"); }
    finally { setSaving(false); }
  };

  const startEditRate = (r) => {
    setEditingRateRole(r.role);
    setRateForm({
      charge_rate: r.custom_charge_rate ?? r.charge_rate ?? 0,
      cost_rate: r.custom_cost_rate ?? r.cost_rate ?? 0,
    });
    // Fetch hours for impact preview
    if (!hoursCheck) {
      API.get(`/projects/${projectId}/roles/hours-check`).then(res => setHoursCheck(res.data)).catch(() => {});
    }
  };

  const getRateImpact = (roleName) => {
    if (!hoursCheck) return null;
    const roleHours = hoursCheck.hours_by_role?.[roleName] || 0;
    if (roleHours === 0) return null;
    const roleData = projectRoles.find(r => r.role === roleName);
    if (!roleData) return null;
    const currentCharge = roleData.charge_rate || 0;
    const newCharge = parseFloat(rateForm.charge_rate) || 0;
    const currentCost = roleData.cost_rate || 0;
    const newCost = parseFloat(rateForm.cost_rate) || 0;
    const feeDiff = (newCharge - currentCharge) * roleHours;
    const costDiff = (newCost - currentCost) * roleHours;
    const marginDiff = feeDiff - costDiff;
    return { roleHours, feeDiff, costDiff, marginDiff, currentFee: currentCharge * roleHours, newFee: newCharge * roleHours };
  };

  const saveRateOverride = async (roleName) => {
    setSaving(true);
    try {
      const roleData = projectRoles.find(r => r.role === roleName);
      const adminCharge = roleData?.admin_charge_rate;
      const adminCost = roleData?.admin_cost_rate;
      const updated = projectRoles.map(r => {
        if (r.role !== roleName) return r;
        const customCharge = (adminCharge !== null && parseFloat(rateForm.charge_rate) === adminCharge) ? undefined : parseFloat(rateForm.charge_rate);
        const customCost = (adminCost !== null && parseFloat(rateForm.cost_rate) === adminCost) ? undefined : parseFloat(rateForm.cost_rate);
        return {
          ...r,
          custom_charge_rate: customCharge !== undefined ? customCharge : null,
          custom_cost_rate: customCost !== undefined ? customCost : null,
          charge_rate: customCharge !== undefined ? customCharge : (adminCharge ?? r.charge_rate),
          cost_rate: customCost !== undefined ? customCost : (adminCost ?? r.cost_rate),
        };
      });
      await API.put(`/projects/${projectId}/roles`, { roles: updated });
      setProjectRoles(updated);
      setEditingRateRole(null);
      toast.success(`Rates updated for ${roleName}`);
      // Check if proposals exist and warn about staleness
      try {
        const proposalsRes = await API.get(`/projects/${projectId}/proposals`);
        if (proposalsRes.data && proposalsRes.data.length > 0) {
          toast.warning("Existing proposals were generated with previous rates — recalculate pricing and regenerate to reflect new rates.", { duration: 6000 });
        }
      } catch {}
      // Re-fetch to get synced admin rates
      fetchRoles();
      onUpdate();
    } catch { toast.error("Failed to save rate override"); }
    finally { setSaving(false); }
  };

  const clearRateOverride = async (roleName) => {
    setSaving(true);
    try {
      const updated = projectRoles.map(r => {
        if (r.role !== roleName) return r;
        const { custom_charge_rate, custom_cost_rate, ...rest } = r;
        return { ...rest, custom_charge_rate: null, custom_cost_rate: null };
      });
      await API.put(`/projects/${projectId}/roles`, { roles: updated });
      toast.success(`${roleName} reset to admin rates`);
      fetchRoles();
      onUpdate();
    } catch { toast.error("Failed to reset rates"); }
    finally { setSaving(false); }
  };

  if (loading) return <Card className="bg-slate-900 border border-slate-800 shadow-sm animate-pulse"><CardContent className="p-6"><div className="h-20 bg-slate-800 rounded" /></CardContent></Card>;

  const hasAnyOverride = (r) => r.custom_charge_rate != null || r.custom_cost_rate != null;

  return (
    <Card className="bg-slate-900 border border-slate-800 shadow-sm" data-testid="staff-levels-panel">
      <CardHeader className="border-b border-slate-800 bg-slate-800/50 py-3 px-4">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-semibold">Staff Levels</CardTitle>
          {canEdit && (
            <Button variant="outline" size="sm" onClick={() => { setEditing(!editing); setEditingRateRole(null); }} className="border-slate-800 text-xs h-7" data-testid="manage-staff-levels-btn">
              {editing ? "Done" : "Manage"}
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="p-4 space-y-2">
        {activeRoles.length === 0 && (
          <p className="text-xs text-slate-500 text-center py-3">No staff levels configured for this project.</p>
        )}

        {[...activeRoles].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0)).map((r, idx) => (
          <div key={r.role} data-testid={`staff-level-${r.role.replace(/\s/g, "-").toLowerCase()}`}>
            <div className="flex items-center justify-between group">
              <div className="flex items-center gap-2 min-w-0 flex-1">
                <div className={`w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold shrink-0 ${hasAnyOverride(r) ? "bg-amber-500/10 text-amber-400" : "bg-blue-500/10 text-blue-400"}`}>
                  {r.role.charAt(0)}
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-slate-200 truncate">{r.role}</p>
                  <div className="flex items-center gap-1.5">
                    <p className="text-[10px] text-slate-500">{r.charge_rate > 0 ? `£${r.charge_rate}/hr` : "No rate set"}</p>
                    {hasAnyOverride(r) && (
                      <span className="text-[9px] font-semibold px-1 py-0 rounded border bg-amber-500/10 text-amber-400 border-amber-500/20">Override</span>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                {r.is_admin_role && !hasAnyOverride(r) && (
                  <span className="text-[9px] font-semibold px-1.5 py-0.5 rounded border bg-blue-500/10 text-blue-400 border-blue-500/20">Admin</span>
                )}
                {editing && (
                  <>
                    <button onClick={() => startEditRate(r)} className="text-slate-600 hover:text-slate-300 p-0.5" title="Edit rates" data-testid={`edit-rate-${r.role.replace(/\s/g, "-").toLowerCase()}`}>
                      <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                    </button>
                    <button onClick={() => reorderRole(idx, -1)} disabled={idx === 0 || saving} className="text-slate-600 hover:text-slate-300 p-0.5 disabled:opacity-30" data-testid={`move-up-${r.role.replace(/\s/g, "-").toLowerCase()}`}>
                      <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" /></svg>
                    </button>
                    <button onClick={() => reorderRole(idx, 1)} disabled={idx === activeRoles.length - 1 || saving} className="text-slate-600 hover:text-slate-300 p-0.5 disabled:opacity-30" data-testid={`move-down-${r.role.replace(/\s/g, "-").toLowerCase()}`}>
                      <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                    </button>
                    <button onClick={() => toggleRole(r.role, true)} disabled={saving} className="text-slate-500 hover:text-red-400 transition-colors ml-1 p-0.5" data-testid={`remove-role-${r.role.replace(/\s/g, "-").toLowerCase()}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </>
                )}
              </div>
            </div>
            {/* Inline rate editor */}
            {editingRateRole === r.role && (
              <div className="ml-9 mt-2 p-3 rounded-md bg-slate-800/60 border border-slate-700/50 space-y-2" data-testid={`rate-editor-${r.role.replace(/\s/g, "-").toLowerCase()}`}>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-[10px] text-slate-500 uppercase tracking-wider font-semibold">Charge Rate (£/hr)</label>
                    <Input
                      type="number" min="0" step="0.01"
                      value={rateForm.charge_rate}
                      onChange={e => setRateForm(f => ({ ...f, charge_rate: e.target.value }))}
                      className="h-7 text-xs bg-slate-900 border-slate-700 mt-0.5"
                      data-testid={`charge-rate-input-${r.role.replace(/\s/g, "-").toLowerCase()}`}
                    />
                    {r.is_admin_role && r.admin_charge_rate != null && (
                      <p className="text-[9px] text-slate-600 mt-0.5">Admin: £{r.admin_charge_rate}</p>
                    )}
                  </div>
                  <div>
                    <label className="text-[10px] text-slate-500 uppercase tracking-wider font-semibold">Cost Rate (£/hr)</label>
                    <Input
                      type="number" min="0" step="0.01"
                      value={rateForm.cost_rate}
                      onChange={e => setRateForm(f => ({ ...f, cost_rate: e.target.value }))}
                      className="h-7 text-xs bg-slate-900 border-slate-700 mt-0.5"
                      data-testid={`cost-rate-input-${r.role.replace(/\s/g, "-").toLowerCase()}`}
                    />
                    {r.is_admin_role && r.admin_cost_rate != null && (
                      <p className="text-[9px] text-slate-600 mt-0.5">Admin: £{r.admin_cost_rate}</p>
                    )}
                  </div>
                </div>
                {/* Rate Change Impact Preview */}
                {(() => {
                  const impact = getRateImpact(r.role);
                  if (!impact || (impact.feeDiff === 0 && impact.costDiff === 0)) return null;
                  return (
                    <div className="p-2 rounded bg-slate-900/80 border border-slate-700/30 space-y-1" data-testid={`rate-impact-${r.role.replace(/\s/g, "-").toLowerCase()}`}>
                      <p className="text-[9px] text-slate-500 uppercase tracking-wider font-semibold">Fee Impact Preview</p>
                      <div className="grid grid-cols-3 gap-2 text-center">
                        <div>
                          <p className="text-[9px] text-slate-500">Current Fee</p>
                          <p className="text-xs font-mono text-slate-300">£{impact.currentFee.toLocaleString("en-GB", { minimumFractionDigits: 0 })}</p>
                        </div>
                        <div>
                          <p className="text-[9px] text-slate-500">New Fee</p>
                          <p className="text-xs font-mono text-slate-200">£{impact.newFee.toLocaleString("en-GB", { minimumFractionDigits: 0 })}</p>
                        </div>
                        <div>
                          <p className="text-[9px] text-slate-500">Difference</p>
                          <p className={`text-xs font-mono font-semibold ${impact.feeDiff > 0 ? "text-emerald-400" : impact.feeDiff < 0 ? "text-rose-400" : "text-slate-400"}`}>
                            {impact.feeDiff > 0 ? "+" : ""}£{impact.feeDiff.toLocaleString("en-GB", { minimumFractionDigits: 0 })}
                          </p>
                        </div>
                      </div>
                      <p className="text-[9px] text-slate-600 text-center">{impact.roleHours}h scoped for {r.role}</p>
                    </div>
                  );
                })()}
                <div className="flex items-center gap-2 pt-1">
                  <Button size="sm" onClick={() => saveRateOverride(r.role)} disabled={saving} className="bg-blue-600 hover:bg-blue-500 text-white h-6 text-[10px] px-2" data-testid={`save-rate-${r.role.replace(/\s/g, "-").toLowerCase()}`}>
                    Save
                  </Button>
                  <button onClick={() => setEditingRateRole(null)} className="text-[10px] text-slate-500 hover:text-slate-300">Cancel</button>
                  {hasAnyOverride(r) && r.is_admin_role && (
                    <button onClick={() => { clearRateOverride(r.role); setEditingRateRole(null); }} className="text-[10px] text-amber-400 hover:text-amber-300 ml-auto" data-testid={`reset-rate-${r.role.replace(/\s/g, "-").toLowerCase()}`}>
                      Reset to Admin
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        ))}

        {/* Inactive roles (shown in edit mode) */}
        {editing && inactiveRoles.length > 0 && (
          <div className="pt-3 border-t border-slate-800">
            <p className="text-[10px] text-slate-500 uppercase tracking-wider font-semibold mb-2">Removed</p>
            {inactiveRoles.map(r => {
              const roleHours = hoursCheck?.hours_by_role?.[r.role] || 0;
              return (
                <div key={r.role} className="flex items-center justify-between py-1 opacity-60" data-testid={`inactive-role-${r.role.replace(/\s/g, "-").toLowerCase()}`}>
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs text-slate-400">{r.role}</span>
                    {roleHours > 0 && (
                      <span className="text-[9px] px-1 py-0 rounded bg-amber-500/10 text-amber-400 border border-amber-500/20">{roleHours}h hidden</span>
                    )}
                  </div>
                  <button onClick={() => toggleRole(r.role, false)} disabled={saving} className="text-xs text-blue-400 hover:text-blue-300 px-2 py-0.5 rounded hover:bg-blue-500/10" data-testid={`restore-role-${r.role.replace(/\s/g, "-").toLowerCase()}`}>
                    Restore
                  </button>
                </div>
              );
            })}
          </div>
        )}

        {/* Add role section */}
        {editing && (
          <div className="pt-3 border-t border-slate-800 space-y-2" data-testid="add-role-section">
            <p className="text-[10px] text-slate-500 uppercase tracking-wider font-semibold">Add Staff Level</p>

            {availableAdminRoles.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {availableAdminRoles.map(ar => (
                  <button key={ar.role} onClick={() => addRole(ar.role)} disabled={saving}
                    className="text-xs px-2.5 py-1 rounded-md border border-slate-700 text-slate-400 hover:border-blue-500/40 hover:text-blue-400 hover:bg-blue-500/5 transition-all"
                    data-testid={`add-admin-role-${ar.role.replace(/\s/g, "-").toLowerCase()}`}>
                    + {ar.role}
                  </button>
                ))}
              </div>
            )}

            <div className="flex gap-2">
              <Input
                value={customRoleName}
                onChange={e => setCustomRoleName(e.target.value)}
                placeholder="Custom role name..."
                className="h-8 text-xs bg-slate-900 border-slate-800 flex-1"
                data-testid="custom-role-input"
                onKeyDown={e => { if (e.key === "Enter") addCustomRole(); }}
              />
              <Button size="sm" onClick={addCustomRole} disabled={saving || !customRoleName.trim()} className="bg-blue-600 hover:bg-blue-500 text-white h-8 text-xs" data-testid="add-custom-role-btn">
                <Plus className="w-3 h-3 mr-1" />Add
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function ScopeTab({ projectId, project, scope, onUpdate, permissions, activeRoles = [] }) {
  const ROLES = activeRoles.length > 0 ? activeRoles : DEFAULT_ROLES;
  const cur = useCurrency();
  const confirm = useConfirm();
  const canEdit = permissions?.can_edit_scope !== false;
  const [saving, setSaving] = useState(false);
  const [loadingTemplate, setLoadingTemplate] = useState(false);
  const [quickPrice, setQuickPrice] = useState(null);
  const [hoursPerDay, setHoursPerDay] = useState(8);
  const [scopeDisciplineFilter, setScopeDisciplineFilter] = useState("All");

  // Local copy of scope items — edits go here first, then debounce-save to API
  const [localItems, setLocalItems] = useState(scope?.items || []);
  const [localExclusions, setLocalExclusions] = useState(scope?.exclusions || []);
  const [localAssumptions, setLocalAssumptions] = useState(scope?.assumptions || []);
  const saveTimerRef = useRef(null);

  // Sync from parent when scope prop changes (e.g. after template load)
  useEffect(() => {
    setLocalItems(scope?.items || []);
    setLocalExclusions(scope?.exclusions || []);
    setLocalAssumptions(scope?.assumptions || []);
  }, [scope]);

  const items = localItems;
  const exclusions = localExclusions;
  const assumptions = localAssumptions;

  // Fetch hours_per_day from admin settings
  useEffect(() => {
    API.get("/admin/settings").then(r => {
      setHoursPerDay(parseFloat(r.data?.hours_per_day) || 8);
    }).catch(() => {});
  }, []);

  // Debounced save — waits 800ms after last edit before hitting the API
  const debouncedSave = useCallback((updatedItems, excl, assump) => {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(async () => {
      setSaving(true);
      try {
        await API.put(`/projects/${projectId}/scope`, { items: updatedItems, exclusions: excl, assumptions: assump });
      } catch { toast.error("Failed to save scope"); }
      finally { setSaving(false); }
    }, 800);
  }, [projectId]);

  // Cleanup timer on unmount
  useEffect(() => { return () => { if (saveTimerRef.current) clearTimeout(saveTimerRef.current); }; }, []);

  // Quick edit — updates local state instantly, debounces API save
  const quickSave = useCallback((updatedItems, excl, assump) => {
    setLocalItems(updatedItems);
    setLocalExclusions(excl);
    setLocalAssumptions(assump);
    debouncedSave(updatedItems, excl, assump);
  }, [debouncedSave]);

  // Full save + refresh — for structural changes (template load, stage delete, etc.)
  const fullSave = async (updatedItems, excl, assump) => {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    setSaving(true);
    try {
      await API.put(`/projects/${projectId}/scope`, { items: updatedItems, exclusions: excl, assumptions: assump });
      onUpdate();
    } catch { toast.error("Failed to save scope"); }
    finally { setSaving(false); }
  };

  // Fetch Quick Price based on project data + current scope hours
  useEffect(() => {
    if (!project) return;
    const totalScopeHours = items.filter(i => i.included).reduce((sum, i) => {
      const unitMult = i.effort_unit === "days" ? hoursPerDay : 1;
      return sum + Object.values(i.hours || {}).reduce((s, h) => s + (h || 0) * unitMult, 0);
    }, 0);
    const fetchQP = async () => {
      try {
        const r = await API.post("/projects/quick-price", {
          sector: project.sector || "",
          floor_area: project.size_metrics?.floor_area || 0,
          procurement_route: project.procurement_route || "",
          complexity_level: 1.0,
        });
        setQuickPrice({ ...r.data, currentScopeHours: totalScopeHours });
      } catch { setQuickPrice(null); }
    };
    fetchQP();
  }, [project, items, hoursPerDay]);

  const loadTemplate = async () => {
    setLoadingTemplate(true);
    try {
      await API.post(`/projects/${projectId}/scope/load-template`);
      toast.success("Scope template loaded");
      onUpdate();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load template");
    } finally {
      setLoadingTemplate(false);
    }
  };

  const toggleItem = (itemId) => {
    const updated = items.map((i) => i.id === itemId ? { ...i, included: !i.included } : i);
    quickSave(updated, exclusions, assumptions);
  };

  const toggleOptional = (itemId) => {
    const updated = items.map((i) => i.id === itemId ? { ...i, is_optional: !i.is_optional } : i);
    quickSave(updated, exclusions, assumptions);
  };

  const updateHours = (itemId, role, value) => {
    const updated = items.map((i) => i.id === itemId ? { ...i, hours: { ...i.hours, [role]: parseFloat(value) || 0 } } : i);
    quickSave(updated, exclusions, assumptions);
  };

  const addOptionalService = () => {
    const newItem = {
      id: crypto.randomUUID(),
      name: "New Optional Service",
      discipline: "Mechanical",
      stage: "Stage 4",
      description: "",
      quantity: 1,
      hours: {},
      included: true,
      is_optional: true,
      classification: "Default",
      effort_unit: "hours",
    };
    quickSave([...items, newItem], exclusions, assumptions);
  };

  const setStageUnit = (stage, newUnit) => {
    const updated = items.map(i => {
      if (i.stage !== stage || i.is_optional) return i;
      const oldUnit = i.effort_unit || "hours";
      if (oldUnit === newUnit) return i;
      const converted = {};
      for (const [role, val] of Object.entries(i.hours || {})) {
        if (!val) { converted[role] = val; continue; }
        converted[role] = newUnit === "days"
          ? parseFloat((val / hoursPerDay).toFixed(4))
          : parseFloat((val * hoursPerDay).toFixed(4));
      }
      return { ...i, effort_unit: newUnit, hours: converted };
    });
    quickSave(updated, exclusions, assumptions);
  };

  const setItemUnit = (itemId, newUnit) => {
    const updated = items.map(i => {
      if (i.id !== itemId) return i;
      const oldUnit = i.effort_unit || "hours";
      if (oldUnit === newUnit) return i;
      const converted = {};
      for (const [role, val] of Object.entries(i.hours || {})) {
        if (!val) { converted[role] = val; continue; }
        converted[role] = newUnit === "days"
          ? parseFloat((val / hoursPerDay).toFixed(4))
          : parseFloat((val * hoursPerDay).toFixed(4));
      }
      return { ...i, effort_unit: newUnit, hours: converted };
    });
    quickSave(updated, exclusions, assumptions);
  };

  const removeItem = (itemId) => {
    const updated = items.filter(i => i.id !== itemId);
    fullSave(updated, exclusions, assumptions);
  };

  const updateItemField = (itemId, field, value) => {
    const updated = items.map(i => i.id === itemId ? { ...i, [field]: value } : i);
    quickSave(updated, exclusions, assumptions);
  };

  // saveScope is kept for structural changes that need full refresh (template load, delete stage)
  const saveScope = async (updatedItems, excl, assump) => {
    await fullSave(updatedItems, excl, assump);
  };

  const baseItems = items.filter(i => !i.is_optional);
  const optionalItems = items.filter(i => i.is_optional);
  const stages = [...new Set(baseItems.map((i) => i.stage))].sort();

  const deleteStage = async (stage) => {
    const stageItems = baseItems.filter(i => i.stage === stage);
    const ok = await confirm({ title: "Delete Stage", description: `Delete all ${stageItems.length} deliverables from "${stage}"? This will add them to exclusions.`, confirmText: "Delete Stage", variant: "destructive" });
    if (!ok) return;
    try {
      const encoded = stage.replace(/ /g, "_");
      await API.delete(`/projects/${projectId}/scope/stage/${encoded}`);
      toast.success(`${stage} removed from scope`);
      onUpdate();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to delete stage"); }
  };

  const clearStageHours = async (stage) => {
    const stageItems = baseItems.filter(i => i.stage === stage && i.included);
    const filledCells = stageItems.reduce((count, i) => count + Object.values(i.hours || {}).filter(v => v > 0).length, 0);
    if (filledCells === 0) { toast.info("No hours to clear in this stage"); return; }
    const ok = await confirm({ title: "Clear Stage Hours", description: `Clear all hours/days values in "${stage}"?\n\nThis will reset ${filledCells} cell(s) across ${stageItems.length} deliverable(s) to zero.\n\nDeliverables, toggles, and other settings will not be affected.`, confirmText: "Clear Hours", variant: "warning" });
    if (!ok) return;
    const updated = items.map(i => {
      if (i.stage !== stage || i.is_optional) return i;
      return { ...i, hours: {} };
    });
    quickSave(updated, exclusions, assumptions);
    toast.success(`Cleared all hours in ${stage}`);
  };

  const clearAllHours = async () => {
    const includedItems = items.filter(i => i.included);
    const filledCells = includedItems.reduce((count, i) => count + Object.values(i.hours || {}).filter(v => v > 0).length, 0);
    if (filledCells === 0) { toast.info("No hours to clear"); return; }
    const ok = await confirm({ title: "Clear All Hours", description: `Clear ALL hours/days values across every stage?\n\nThis will reset ${filledCells} cell(s) across ${includedItems.length} deliverable(s) to zero.\n\nDeliverables, toggles, and other settings will not be affected.`, confirmText: "Clear All", variant: "warning" });
    if (!ok) return;
    const updated = items.map(i => ({ ...i, hours: {} }));
    quickSave(updated, exclusions, assumptions);
    toast.success("Cleared all hours across all stages");
  };

  // Custom scope / CSV / Template state
  const [addStageDialog, setAddStageDialog] = useState(false);
  const [newStageName, setNewStageName] = useState("");
  const [newStageDiscipline, setNewStageDiscipline] = useState("Mechanical");
  const [csvImportDialog, setCsvImportDialog] = useState(false);
  const [hoursImportDialog, setHoursImportDialog] = useState(false);
  const [hoursCsvText, setHoursCsvText] = useState("");
  const [hoursImportResult, setHoursImportResult] = useState(null);
  const [hoursImporting, setHoursImporting] = useState(false);
  const [csvText, setCsvText] = useState("");
  const [csvPreview, setCsvPreview] = useState(null);
  const [csvMode, setCsvMode] = useState("replace");
  const [savedTemplates, setSavedTemplates] = useState([]);
  const [loadTemplateDialog, setLoadTemplateDialog] = useState(false);
  const [saveTemplateDialog, setSaveTemplateDialog] = useState(false);
  const [templateName, setTemplateName] = useState("");
  const [templateDesc, setTemplateDesc] = useState("");
  const [mergePreview, setMergePreview] = useState(null); // {template, diff}

  useEffect(() => {
    API.get("/scope-templates").then(r => setSavedTemplates(r.data || [])).catch(() => {});
  }, []);

  const addCustomStage = () => {
    if (!newStageName.trim()) { toast.error("Enter a stage name"); return; }
    const item = {
      id: crypto.randomUUID(), name: "New Deliverable", discipline: newStageDiscipline,
      stage: newStageName.trim(), description: "", quantity: 1, hours: {},
      included: true, is_optional: false, classification: "Default", effort_unit: "hours", sub_items: [],
    };
    quickSave([...items, item], exclusions, assumptions);
    setAddStageDialog(false);
    setNewStageName("");
    toast.success(`Added stage "${newStageName.trim()}"`);
  };

  const parseCSV = (text) => {
    const lines = text.trim().split("\n");
    if (lines.length < 2) { toast.error("CSV must have a header row and at least one data row"); return; }
    const headerLine = lines[0].toLowerCase();
    const sep = headerLine.includes("\t") ? "\t" : headerLine.includes("|") ? "|" : ",";
    const headers = lines[0].split(sep).map(h => h.trim().toLowerCase().replace(/[^a-z_]/g, ""));
    const rows = [];
    const errors = [];
    for (let i = 1; i < lines.length; i++) {
      if (!lines[i].trim()) continue;
      const cols = lines[i].split(sep).map(c => c.trim());
      const row = {};
      headers.forEach((h, idx) => { row[h] = cols[idx] || ""; });
      if (!row.stage && !row.discipline && !row.deliverable) { errors.push(`Row ${i}: All fields empty`); continue; }
      rows.push(row);
    }
    setCsvPreview({ headers, rows, errors });
  };

  const importCSV = async () => {
    if (!csvPreview?.rows?.length) return;
    setSaving(true);
    try {
      const r = await API.post(`/projects/${projectId}/scope/import-csv`, { rows: csvPreview.rows, mode: csvMode });
      toast.success(`Imported ${r.data.items_imported} deliverables`);
      if (r.data.errors?.length) toast.warning(`${r.data.errors.length} row(s) had issues`);
      onUpdate();
      setCsvImportDialog(false); setCsvText(""); setCsvPreview(null);
    } catch (e) { toast.error(e.response?.data?.detail || "Import failed"); }
    finally { setSaving(false); }
  };

  const saveAsTemplate = async () => {
    if (!templateName.trim()) { toast.error("Enter a template name"); return; }
    try {
      await API.post("/scope-templates", { name: templateName.trim(), description: templateDesc.trim(), stage_framework: project?.stage_framework || "RIBA", items, exclusions, assumptions });
      toast.success("Scope saved as template");
      setSaveTemplateDialog(false); setTemplateName(""); setTemplateDesc("");
      API.get("/scope-templates").then(r => setSavedTemplates(r.data || []));
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to save template"); }
  };

  const loadSavedTemplate = async (tplId, mode) => {
    if (mode === "merge") {
      // Show diff preview before merging
      const tpl = savedTemplates.find(t => t.id === tplId);
      if (!tpl) return;
      const tplItems = tpl.items || [];
      const existingKeys = new Set(items.map(i => `${i.stage}|${i.discipline}|${i.name}`));
      const tplKeys = new Set(tplItems.map(i => `${i.stage}|${i.discipline}|${i.name}`));
      const newItems = tplItems.filter(i => !existingKeys.has(`${i.stage}|${i.discipline}|${i.name}`));
      const existingOnly = items.filter(i => !tplKeys.has(`${i.stage}|${i.discipline}|${i.name}`));
      const shared = items.filter(i => tplKeys.has(`${i.stage}|${i.discipline}|${i.name}`));
      setMergePreview({ template: tpl, tplId, newItems, existingOnly, shared, totalAfter: items.length + newItems.length });
      return;
    }
    // Replace mode — confirm if scope exists
    if (items.length > 0) {
      const ok = await confirm({ title: "Replace Scope", description: `This will replace your existing ${items.length} deliverables with the template. Continue?`, confirmText: "Replace", variant: "destructive" });
      if (!ok) return;
    }
    setSaving(true);
    try {
      await API.post(`/projects/${projectId}/scope/load-saved-template`, { template_id: tplId, mode });
      toast.success("Template loaded"); onUpdate(); setLoadTemplateDialog(false);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to load template"); }
    finally { setSaving(false); }
  };

  const confirmMerge = async () => {
    if (!mergePreview) return;
    setSaving(true);
    try {
      await API.post(`/projects/${projectId}/scope/load-saved-template`, { template_id: mergePreview.tplId, mode: "merge" });
      toast.success(`Merged: +${mergePreview.newItems.length} new deliverables`);
      onUpdate(); setMergePreview(null); setLoadTemplateDialog(false);
    } catch (e) { toast.error(e.response?.data?.detail || "Merge failed"); }
    finally { setSaving(false); }
  };

  const downloadCSV = () => {
    if (items.length === 0) { toast.info("No scope items to export"); return; }
    const header = "stage,discipline,deliverable,sub_deliverable,classification,quantity";
    const rows = [];
    for (const item of items) {
      const subs = item.sub_items || [];
      if (subs.length === 0) {
        rows.push([item.stage, item.discipline, item.name, "", item.classification || "Default", item.quantity || 1].map(v => `"${String(v).replace(/"/g, '""')}"`).join(","));
      } else {
        for (const sub of subs) {
          rows.push([item.stage, item.discipline, item.name, sub.name, item.classification || "Default", item.quantity || 1].map(v => `"${String(v).replace(/"/g, '""')}"`).join(","));
        }
      }
    }
    const csv = [header, ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `scope_${projectId.substring(0, 8)}_${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${items.length} deliverables to CSV`);
  };

  const downloadHoursTemplate = async () => {
    try {
      const r = await API.get(`/projects/${projectId}/scope/hours-template-csv`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `hours_template_${projectId.substring(0, 8)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Hours template downloaded");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to download hours template");
    }
  };

  const importHoursCSV = async () => {
    if (!hoursCsvText.trim()) { toast.error("Paste or enter CSV data"); return; }
    setHoursImporting(true);
    try {
      const lines = hoursCsvText.trim().split("\n");
      const headerLine = lines[0].toLowerCase();
      const sep = headerLine.includes("\t") ? "\t" : headerLine.includes("|") ? "|" : ",";
      const parsedLines = lines.map(l => l.split(sep).map(c => c.trim().replace(/^"|"$/g, "")));
      const headers = parsedLines[0].map(h => h.trim().toLowerCase());
      const rows = parsedLines.slice(1).filter(l => l.length >= 3 && l.some(c => c)).map(cols => {
        const obj = {};
        headers.forEach((h, i) => { obj[h] = cols[i] || ""; });
        return obj;
      });
      const r = await API.post(`/projects/${projectId}/scope/import-hours-csv`, { rows });
      setHoursImportResult(r.data);
      // Refresh scope items with the updated hours
      const scopeR = await API.get(`/projects/${projectId}/scope`);
      const updated = scopeR.data.items || scopeR.data || [];
      quickSave(updated, exclusions, assumptions);
      toast.success(`Imported hours for ${r.data.matched} deliverable${r.data.matched !== 1 ? "s" : ""}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to import hours CSV");
    } finally {
      setHoursImporting(false);
    }
  };


  // Add scope item dialog state
  const [addItemDialog, setAddItemDialog] = useState({ open: false, stage: "" });
  const [newItem, setNewItem] = useState({ name: "", discipline: "Mechanical", classification: "Default", description: "" });
  const [expandedSubItems, setExpandedSubItems] = useState([]);

  const openAddItemDialog = (stage) => {
    setNewItem({ name: "", discipline: "Mechanical", classification: "Default", description: "" });
    setAddItemDialog({ open: true, stage });
  };

  const addScopeItem = () => {
    if (!newItem.name.trim()) { toast.error("Please enter a deliverable name"); return; }
    const stageItems = baseItems.filter(i => i.stage === addItemDialog.stage);
    const stageUnit = stageItems.length > 0 ? (stageItems[0].effort_unit || "hours") : "hours";
    const item = {
      id: crypto.randomUUID(),
      name: newItem.name.trim(),
      discipline: newItem.discipline,
      stage: addItemDialog.stage,
      description: newItem.description.trim(),
      quantity: 1,
      hours: {},
      included: true,
      is_optional: false,
      classification: newItem.classification,
      effort_unit: stageUnit,
    };
    quickSave([...items, item], exclusions, assumptions);
    setAddItemDialog({ open: false, stage: "" });
    toast.success(`Added "${item.name}" to ${addItemDialog.stage}`);
  };

  const addSubItem = (itemId) => {
    const updated = items.map(i => {
      if (i.id !== itemId) return i;
      const subs = i.sub_items || [];
      const newSub = { id: crypto.randomUUID(), name: "New sub-item", order: subs.length };
      return { ...i, sub_items: [...subs, newSub] };
    });
    quickSave(updated, exclusions, assumptions);
    if (!expandedSubItems.includes(itemId)) setExpandedSubItems(prev => [...prev, itemId]);
  };

  const updateSubItem = (itemId, subId, name) => {
    const updated = items.map(i => {
      if (i.id !== itemId) return i;
      return { ...i, sub_items: (i.sub_items || []).map(s => s.id === subId ? { ...s, name } : s) };
    });
    quickSave(updated, exclusions, assumptions);
  };

  const removeSubItem = (itemId, subId) => {
    const updated = items.map(i => {
      if (i.id !== itemId) return i;
      return { ...i, sub_items: (i.sub_items || []).filter(s => s.id !== subId).map((s, idx) => ({ ...s, order: idx })) };
    });
    quickSave(updated, exclusions, assumptions);
  };

  const moveSubItem = (itemId, subId, direction) => {
    const updated = items.map(i => {
      if (i.id !== itemId) return i;
      const subs = [...(i.sub_items || [])];
      const idx = subs.findIndex(s => s.id === subId);
      if (idx < 0) return i;
      const newIdx = idx + direction;
      if (newIdx < 0 || newIdx >= subs.length) return i;
      [subs[idx], subs[newIdx]] = [subs[newIdx], subs[idx]];
      return { ...i, sub_items: subs.map((s, j) => ({ ...s, order: j })) };
    });
    quickSave(updated, exclusions, assumptions);
  };

  return (
    <div className="space-y-6" data-testid="scope-tab">
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-3">
            <h2 className="font-heading text-xl font-semibold text-slate-100">Scope of Services</h2>
            <span className="text-[11px] font-mono px-2.5 py-1 rounded-md border border-blue-500/30 bg-blue-500/10 text-blue-400 font-semibold" data-testid="scope-framework-badge">
              {project?.stage_framework || "RIBA"} Framework
            </span>
          </div>
          <p className="text-sm text-muted-foreground">{baseItems.filter((i) => i.included).length} of {baseItems.length} deliverables included{optionalItems.length > 0 ? ` + ${optionalItems.length} optional` : ""}</p>
        </div>
        <div className="flex gap-2 items-center">
          {canEdit && (
            <>
              <Button variant="outline" onClick={addOptionalService} data-testid="add-optional-btn" className="border-indigo-300 text-sky-400 hover:bg-sky-500/10">
                <Plus className="w-4 h-4 mr-2" />Optional Service
              </Button>
              <Button variant="outline" onClick={() => setAddStageDialog(true)} data-testid="add-custom-stage-btn" className="border-slate-700 text-slate-300 hover:bg-slate-800">
                <Plus className="w-4 h-4 mr-2" />Custom Stage
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" className="border-slate-700 text-slate-300 hover:bg-slate-800" data-testid="scope-more-actions-btn">
                    <MoreHorizontal className="w-4 h-4 mr-2" />More Actions
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-52 bg-slate-900 border-slate-700">
                  <DropdownMenuItem onClick={() => setCsvImportDialog(true)} data-testid="csv-import-btn" className="cursor-pointer">
                    <Upload className="w-4 h-4 mr-2" />CSV Import
                  </DropdownMenuItem>
                  {items.length > 0 && (
                    <DropdownMenuItem onClick={downloadCSV} data-testid="csv-download-btn" className="cursor-pointer">
                      <Download className="w-4 h-4 mr-2" />CSV Export
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator className="bg-slate-700" />
                  {items.length > 0 && (
                    <DropdownMenuItem onClick={downloadHoursTemplate} data-testid="download-hours-template-btn" className="cursor-pointer text-cyan-400">
                      <Download className="w-4 h-4 mr-2" />Download Hours Template
                    </DropdownMenuItem>
                  )}
                  {items.length > 0 && (
                    <DropdownMenuItem onClick={() => setHoursImportDialog(true)} data-testid="import-hours-btn" className="cursor-pointer text-cyan-400">
                      <Upload className="w-4 h-4 mr-2" />Import Hours (CSV)
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator className="bg-slate-700" />
                  {items.length > 0 && (
                    <DropdownMenuItem onClick={() => setSaveTemplateDialog(true)} data-testid="save-template-btn" className="cursor-pointer text-emerald-400">
                      <Save className="w-4 h-4 mr-2" />Save as Template
                    </DropdownMenuItem>
                  )}
                  {savedTemplates.length > 0 && (
                    <DropdownMenuItem onClick={() => setLoadTemplateDialog(true)} data-testid="load-saved-template-btn" className="cursor-pointer text-amber-400">
                      <Download className="w-4 h-4 mr-2" />Load Saved Template
                    </DropdownMenuItem>
                  )}
                  {items.length > 0 && (
                    <>
                      <DropdownMenuSeparator className="bg-slate-700" />
                      <DropdownMenuItem onClick={clearAllHours} data-testid="clear-all-hours-btn" className="cursor-pointer text-rose-400">
                        <Trash2 className="w-4 h-4 mr-2" />Clear All Hours
                      </DropdownMenuItem>
                    </>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
              <Button onClick={loadTemplate} disabled={loadingTemplate} data-testid="load-template-btn" className="bg-blue-600 hover:bg-blue-500 text-white">
                <RefreshCw className={`w-4 h-4 mr-2 ${loadingTemplate ? "animate-spin" : ""}`} />
                {items.length > 0 ? "Reload Template" : "Load Template"}
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Quick Price Guidance during scope editing */}
      {quickPrice?.available && (
        <Card className="bg-slate-900 border border-blue-500/20 shadow-sm" data-testid="scope-quick-price-panel">
          <CardContent className="p-4">
            <div className="flex items-start gap-4">
              <div className="w-9 h-9 rounded-lg bg-blue-500/10 flex items-center justify-center flex-shrink-0">
                <Calculator className="w-4.5 h-4.5 text-blue-500" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <h4 className="text-sm font-heading font-semibold text-slate-100">Quick Price Guidance</h4>
                  <span className="text-[10px] bg-blue-500/10 text-blue-400 border border-blue-500/20 px-1.5 py-0.5 rounded font-medium">{quickPrice.message}</span>
                </div>
                <div className="flex items-center gap-6 mt-2">
                  <div>
                    <p className="text-[10px] text-slate-400 uppercase tracking-wider">Indicative Fee</p>
                    <p className="text-lg font-heading font-bold text-slate-100">
                      {cur.symbol}{Number(quickPrice.suggestion.typical_fee).toLocaleString("en-GB", { maximumFractionDigits: 0 })}
                    </p>
                  </div>
                  <div className="h-10 w-px bg-slate-800" />
                  <div>
                    <p className="text-[10px] text-slate-400 uppercase tracking-wider">Fee Range</p>
                    <p className="text-sm font-mono text-slate-400">
                      {cur.symbol}{Number(quickPrice.suggestion.low_fee).toLocaleString("en-GB", { maximumFractionDigits: 0 })}
                      <span className="mx-1 text-slate-300">—</span>
                      {cur.symbol}{Number(quickPrice.suggestion.high_fee).toLocaleString("en-GB", { maximumFractionDigits: 0 })}
                    </p>
                  </div>
                  <div className="h-10 w-px bg-slate-800" />
                  <div>
                    <p className="text-[10px] text-slate-400 uppercase tracking-wider">Avg Hours</p>
                    <p className="text-sm font-mono text-slate-400">{Number(quickPrice.suggestion.avg_hours).toLocaleString()}</p>
                  </div>
                  {quickPrice.currentScopeHours > 0 && (
                    <>
                      <div className="h-10 w-px bg-slate-800" />
                      <div>
                        <p className="text-[10px] text-slate-400 uppercase tracking-wider">Your Scope Hours</p>
                        <p className={`text-sm font-mono font-semibold ${
                          quickPrice.currentScopeHours > quickPrice.suggestion.avg_hours * 1.2 ? "text-rose-400" :
                          quickPrice.currentScopeHours < quickPrice.suggestion.avg_hours * 0.8 ? "text-amber-400" :
                          "text-emerald-400"
                        }`}>
                          {quickPrice.currentScopeHours.toLocaleString()}
                          <span className="text-xs font-normal text-slate-400 ml-1">
                            ({quickPrice.suggestion.avg_hours > 0 ? ((quickPrice.currentScopeHours / quickPrice.suggestion.avg_hours) * 100).toFixed(0) : "—"}% of avg)
                          </span>
                        </p>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {baseItems.length === 0 && optionalItems.length === 0 ? (
        <Card className="bg-slate-900 border border-slate-800"><CardContent className="p-12 text-center"><p className="text-sm text-slate-400">No scope items yet. Load a template to get started.</p></CardContent></Card>
      ) : (
        <>
          {/* Discipline Filter */}
          {(() => {
            const allDisciplines = [...new Set(baseItems.map(i => i.discipline).filter(Boolean))].sort();
            return allDisciplines.length > 1 && (
              <div className="flex items-center gap-2 flex-wrap" data-testid="scope-discipline-filter">
                <span className="text-xs font-medium text-slate-500 uppercase tracking-wider">Filter:</span>
                {["All", ...allDisciplines].map(d => (
                  <button key={d} onClick={() => setScopeDisciplineFilter(d)}
                    className={`text-xs font-semibold px-3 py-1.5 rounded-md border transition-all ${scopeDisciplineFilter === d ? "bg-blue-600 text-white border-blue-600 shadow-sm" : "bg-slate-800 text-slate-400 border-slate-700 hover:border-slate-600 hover:text-slate-300"}`}
                    data-testid={`filter-discipline-${d.replace(/\s/g, "-").toLowerCase()}`}>
                    {d} {d !== "All" && <span className="ml-1 text-[10px] opacity-70">({baseItems.filter(i => i.discipline === d).length})</span>}
                  </button>
                ))}
              </div>
            );
          })()}
          {/* Scope Progress Tracker */}
          {(() => {
            const totalItems = baseItems.filter(i => scopeDisciplineFilter === "All" || i.discipline === scopeDisciplineFilter).length;
            const filledItems = baseItems.filter(i => (scopeDisciplineFilter === "All" || i.discipline === scopeDisciplineFilter) && i.hours && Object.values(i.hours).some(h => h > 0)).length;
            const pct = totalItems > 0 ? Math.round((filledItems / totalItems) * 100) : 0;
            if (totalItems === 0) return null;
            return (
              <div className="flex items-center gap-3 px-1" data-testid="scope-progress-overall">
                <span className="text-xs font-medium text-slate-500 uppercase tracking-wider whitespace-nowrap">Hours Progress</span>
                <div className="flex-1 h-2 bg-slate-800 rounded-full overflow-hidden max-w-xs">
                  <div className={`h-full rounded-full transition-all duration-500 ${pct === 100 ? "bg-emerald-500" : pct > 0 ? "bg-blue-500" : "bg-slate-700"}`} style={{ width: `${pct}%` }} />
                </div>
                <span className={`text-xs font-semibold tabular-nums ${pct === 100 ? "text-emerald-400" : pct > 0 ? "text-blue-400" : "text-slate-500"}`} data-testid="scope-progress-label">{filledItems} of {totalItems} deliverables ({pct}%)</span>
              </div>
            );
          })()}
          {stages.map((stage) => {
            const stageItems = baseItems.filter(i => i.stage === stage && (scopeDisciplineFilter === "All" || i.discipline === scopeDisciplineFilter));
            if (stageItems.length === 0) return null;
            const stageUnit = stageItems.length > 0 && stageItems.every(i => (i.effort_unit || "hours") === "days") ? "days" : stageItems.every(i => (i.effort_unit || "hours") === "hours") ? "hours" : "mixed";
            const filledCount = stageItems.filter(i => i.hours && Object.values(i.hours).some(h => h > 0)).length;
            const totalCount = stageItems.length;
            const fillPct = totalCount > 0 ? Math.round((filledCount / totalCount) * 100) : 0;
            return (
            <Card key={stage} className="bg-slate-900 border border-slate-800 shadow-sm overflow-hidden">
              <CardHeader className="border-b border-slate-800 bg-slate-800/50 py-3 px-4 flex flex-row items-center justify-between">
                <div className="flex items-center gap-3">
                  <CardTitle className="text-sm font-semibold">{stage} <span className="text-xs text-slate-500 font-normal ml-2">({stageItems.length} items)</span></CardTitle>
                  <div className="flex items-center gap-2" data-testid={`stage-progress-${stage.replace(/ /g, "-").toLowerCase()}`}>
                    <div className="w-20 h-1.5 bg-slate-700 rounded-full overflow-hidden">
                      <div className={`h-full rounded-full transition-all duration-300 ${fillPct === 100 ? "bg-emerald-500" : fillPct > 0 ? "bg-blue-500" : "bg-slate-600"}`} style={{ width: `${fillPct}%` }} />
                    </div>
                    <span className={`text-[10px] font-medium tabular-nums ${fillPct === 100 ? "text-emerald-400" : fillPct > 0 ? "text-blue-400" : "text-slate-500"}`}>{filledCount}/{totalCount}</span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {canEdit && (
                    <button onClick={() => openAddItemDialog(stage)} className="flex items-center gap-1 text-[10px] font-semibold text-blue-400 hover:text-blue-300 px-2 py-1 rounded hover:bg-blue-500/10 transition-colors" data-testid={`add-item-stage-${stage.replace(/ /g, "-").toLowerCase()}`} title={`Add deliverable to ${stage}`}>
                      <Plus className="w-3 h-3" />Add
                    </button>
                  )}
                  {canEdit && (
                    <button onClick={() => clearStageHours(stage)} className="flex items-center gap-1 text-[10px] font-semibold text-slate-500 hover:text-amber-400 px-2 py-1 rounded hover:bg-amber-500/10 transition-colors" data-testid={`clear-stage-${stage.replace(/ /g, "-").toLowerCase()}`} title={`Clear all hours in ${stage}`}>
                      <XCircle className="w-3 h-3" />Clear
                    </button>
                  )}
                  {canEdit && (
                    <div className="flex items-center gap-1 bg-slate-800 rounded-md p-0.5" data-testid={`stage-unit-toggle-${stage.replace(/ /g, "-").toLowerCase()}`}>
                      <button
                        onClick={() => setStageUnit(stage, "hours")}
                        className={`text-[10px] font-semibold px-2.5 py-1 rounded transition-all ${stageUnit === "hours" ? "bg-blue-600 text-white shadow-sm" : "text-slate-500 hover:text-slate-300"}`}
                        data-testid={`stage-unit-hours-${stage.replace(/ /g, "-").toLowerCase()}`}
                      >
                        Hours
                      </button>
                      <button
                        onClick={() => setStageUnit(stage, "days")}
                        className={`text-[10px] font-semibold px-2.5 py-1 rounded transition-all ${stageUnit === "days" ? "bg-blue-600 text-white shadow-sm" : "text-slate-500 hover:text-slate-300"}`}
                        data-testid={`stage-unit-days-${stage.replace(/ /g, "-").toLowerCase()}`}
                      >
                        Days
                      </button>
                    </div>
                  )}
                  {canEdit && (
                    <button onClick={() => deleteStage(stage)} className="text-slate-600 hover:text-rose-400 transition-colors p-1 rounded hover:bg-slate-800" data-testid={`delete-stage-${stage.replace(/ /g, "-").toLowerCase()}`} title={`Delete all ${stage} items`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              </CardHeader>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-800/50">
                      <TableHead className="text-xs uppercase tracking-wider font-semibold text-slate-500 px-3 w-8"></TableHead>
                      <TableHead className="text-xs uppercase tracking-wider font-semibold text-slate-500 px-3">Deliverable</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider font-semibold text-slate-500 px-3">Discipline</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider font-semibold text-slate-500 px-3 w-24">Class</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider font-semibold text-slate-500 px-2 text-center w-16">Unit</TableHead>
                      {ROLES.map((r) => <TableHead key={r} className="text-xs uppercase tracking-wider font-semibold text-slate-500 px-2 text-center w-20"><span>{r.split(" ")[0]}</span></TableHead>)}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {stageItems.map((item) => {
                      const itemUnit = item.effort_unit || "hours";
                      const hasSubs = (item.sub_items || []).length > 0;
                      const isSubExpanded = expandedSubItems.includes(item.id);
                      return (
                      <React.Fragment key={item.id}>
                      <TableRow className={`${!item.included ? "opacity-40" : ""}`} data-testid={`scope-item-${item.id}`}>
                        <TableCell className="px-3"><Checkbox checked={item.included} onCheckedChange={() => toggleItem(item.id)} data-testid={`toggle-${item.id}`} /></TableCell>
                        <TableCell className="px-3">
                          <div className="flex items-start gap-1.5">
                            {canEdit && (
                              <button onClick={() => setExpandedSubItems(prev => prev.includes(item.id) ? prev.filter(x => x !== item.id) : [...prev, item.id])} className="mt-0.5 text-slate-600 hover:text-slate-300 transition-colors flex-shrink-0" data-testid={`expand-subs-${item.id}`}>
                                <span className={`text-[10px] inline-block transition-transform ${isSubExpanded ? "rotate-90" : ""}`}>&#9654;</span>
                              </button>
                            )}
                            <div className="min-w-0">
                              <p className="text-sm font-medium">{item.name}</p>
                              {item.description && <p className="text-xs text-slate-400 mt-0.5">{item.description}</p>}
                              {hasSubs && !isSubExpanded && <p className="text-[10px] text-slate-600 mt-0.5">{item.sub_items.length} sub-item{item.sub_items.length > 1 ? "s" : ""}</p>}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="px-3 text-sm text-slate-400">
                          {canEdit ? (
                            <Select value={item.discipline} onValueChange={(val) => updateItemField(item.id, "discipline", val)}>
                              <SelectTrigger className="h-7 w-[110px] text-xs bg-slate-900 border-slate-800" data-testid={`discipline-select-${item.id}`}>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {ALL_DISCIPLINES.map(d => (
                                  <SelectItem key={d} value={d}>{d}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          ) : item.discipline}
                        </TableCell>
                        <TableCell className="px-3">
                          <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${item.classification === "Mandatory" ? "bg-rose-500/10 text-rose-400" : item.classification === "Recommended" ? "bg-blue-500/10 text-blue-400" : "bg-slate-800/50 text-slate-500"}`}>{item.classification || "Default"}</span>
                        </TableCell>
                        <TableCell className="px-2 text-center">
                          {canEdit ? (
                            <button
                              onClick={() => setItemUnit(item.id, itemUnit === "hours" ? "days" : "hours")}
                              className={`text-[10px] font-bold px-2 py-0.5 rounded-full border transition-all ${itemUnit === "days" ? "bg-amber-500/10 text-amber-400 border-amber-500/30" : "bg-slate-800 text-slate-500 border-slate-700"}`}
                              disabled={!item.included}
                              data-testid={`unit-toggle-${item.id}`}
                            >
                              {itemUnit === "days" ? "days" : "hrs"}
                            </button>
                          ) : (
                            <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${itemUnit === "days" ? "text-amber-400" : "text-slate-500"}`}>
                              {itemUnit === "days" ? "days" : "hrs"}
                            </span>
                          )}
                        </TableCell>
                        {ROLES.map((role) => (
                          <TableCell key={role} className="px-2">
                            <Input
                              type="number"
                              value={item.hours?.[role] || ""}
                              onChange={(e) => updateHours(item.id, role, e.target.value)}
                              className="w-16 h-8 text-center font-mono text-xs bg-slate-900 border-slate-800"
                              disabled={!item.included}
                              data-testid={`hours-${item.id}-${role.split(" ")[0].toLowerCase()}`}
                            />
                          </TableCell>
                        ))}
                      </TableRow>
                      {isSubExpanded && (
                        <TableRow className="bg-slate-800/10">
                          <TableCell colSpan={5 + ROLES.length} className="px-6 py-2">
                            <div className="space-y-1.5 ml-4 border-l-2 border-slate-800 pl-3">
                              {[...(item.sub_items || [])].sort((a, b) => (a.order || 0) - (b.order || 0)).map((sub, idx) => (
                                <div key={sub.id} className="flex items-center gap-2 group" data-testid={`sub-item-${sub.id}`}>
                                  <span className="text-[10px] text-slate-600 w-4 text-right">{idx + 1}.</span>
                                  {canEdit ? (
                                    <Input value={sub.name} onChange={e => updateSubItem(item.id, sub.id, e.target.value)} className="h-6 text-xs bg-transparent border-transparent hover:border-slate-700 focus:border-slate-600 flex-1 px-1.5" data-testid={`sub-item-name-${sub.id}`} />
                                  ) : (
                                    <span className="text-xs text-slate-400 flex-1">{sub.name}</span>
                                  )}
                                  {canEdit && (
                                    <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                                      <button onClick={() => moveSubItem(item.id, sub.id, -1)} disabled={idx === 0} className="text-slate-600 hover:text-slate-300 disabled:opacity-20 p-0.5" data-testid={`move-up-${sub.id}`}><ChevronUp className="w-3 h-3" /></button>
                                      <button onClick={() => moveSubItem(item.id, sub.id, 1)} disabled={idx === (item.sub_items || []).length - 1} className="text-slate-600 hover:text-slate-300 disabled:opacity-20 p-0.5" data-testid={`move-down-${sub.id}`}><ChevronDown className="w-3 h-3" /></button>
                                      <button onClick={() => removeSubItem(item.id, sub.id)} className="text-slate-600 hover:text-rose-400 p-0.5" data-testid={`remove-sub-${sub.id}`}><Trash2 className="w-3 h-3" /></button>
                                    </div>
                                  )}
                                </div>
                              ))}
                              {canEdit && (
                                <button onClick={() => addSubItem(item.id)} className="flex items-center gap-1 text-[10px] text-slate-500 hover:text-blue-400 transition-colors mt-1 py-0.5" data-testid={`add-sub-${item.id}`}>
                                  <Plus className="w-3 h-3" />Add sub-item
                                </button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      )}
                      </React.Fragment>
                    );})}
                  </TableBody>
                </Table>
                {canEdit && (
                  <div className="border-t border-slate-800 px-4 py-2">
                    <button onClick={() => openAddItemDialog(stage)} className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-blue-400 transition-colors py-1" data-testid={`add-item-bottom-${stage.replace(/ /g, "-").toLowerCase()}`}>
                      <Plus className="w-3.5 h-3.5" />Add deliverable to {stage}
                    </button>
                  </div>
                )}
              </CardContent>
            </Card>
          );})}

          {/* Optional Services */}
          {optionalItems.filter(i => scopeDisciplineFilter === "All" || i.discipline === scopeDisciplineFilter).length > 0 && (
            <Card className="bg-slate-900 border-2 border-sky-500/20 shadow-sm overflow-hidden">
              <CardHeader className="border-b border-sky-500/20 bg-sky-500/10/30 py-3 px-4">
                <CardTitle className="text-sm font-semibold text-indigo-800">Optional Services ({optionalItems.length})</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-sky-500/10/50">
                      <TableHead className="text-xs uppercase tracking-wider font-semibold text-indigo-600 px-3 w-8"></TableHead>
                      <TableHead className="text-xs uppercase tracking-wider font-semibold text-indigo-600 px-3">Service</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider font-semibold text-indigo-600 px-3">Description</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider font-semibold text-indigo-600 px-2 text-center w-16">Unit</TableHead>
                      {ROLES.map((r) => <TableHead key={r} className="text-xs uppercase tracking-wider font-semibold text-indigo-600 px-2 text-center w-20"><span>{r.split(" ")[0]}</span></TableHead>)}
                      <TableHead className="w-8"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {optionalItems.filter(i => scopeDisciplineFilter === "All" || i.discipline === scopeDisciplineFilter).map(item => {
                      const itemUnit = item.effort_unit || "hours";
                      return (
                      <TableRow key={item.id} className={!item.included ? "opacity-40" : ""} data-testid={`optional-item-${item.id}`}>
                        <TableCell className="px-3"><Checkbox checked={item.included} onCheckedChange={() => toggleItem(item.id)} /></TableCell>
                        <TableCell className="px-3">
                          <Input value={item.name} onChange={(e) => updateItemField(item.id, "name", e.target.value)} className="h-8 text-sm bg-slate-900 border-slate-800" data-testid={`opt-name-${item.id}`} />
                        </TableCell>
                        <TableCell className="px-3">
                          <Input value={item.description || ""} onChange={(e) => updateItemField(item.id, "description", e.target.value)} className="h-8 text-sm bg-slate-900 border-slate-800" placeholder="Description" />
                        </TableCell>
                        <TableCell className="px-2 text-center">
                          <button
                            onClick={() => setItemUnit(item.id, itemUnit === "hours" ? "days" : "hours")}
                            className={`text-[10px] font-bold px-2 py-0.5 rounded-full border transition-all ${itemUnit === "days" ? "bg-amber-500/10 text-amber-400 border-amber-500/30" : "bg-slate-800 text-slate-500 border-slate-700"}`}
                            disabled={!item.included}
                            data-testid={`unit-toggle-${item.id}`}
                          >
                            {itemUnit === "days" ? "days" : "hrs"}
                          </button>
                        </TableCell>
                        {ROLES.map(role => (
                          <TableCell key={role} className="px-2">
                            <CellInput type="number" value={item.hours?.[role] || ""} onChange={(e) => updateHours(item.id, role, e.target.value)} className="w-16 h-8 text-center font-mono text-xs bg-slate-900 border-slate-800" disabled={!item.included} />
                          </TableCell>
                        ))}
                        <TableCell className="px-2"><button onClick={() => removeItem(item.id)} className="text-slate-400 hover:text-red-500"><Trash2 className="w-4 h-4" /></button></TableCell>
                      </TableRow>
                    );})}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {assumptions.length > 0 && (
        <Card className="bg-slate-900 border border-slate-800 shadow-sm">
          <CardHeader className="border-b border-slate-800 bg-slate-800/50 py-3 px-4"><CardTitle className="text-sm font-semibold">Assumptions</CardTitle></CardHeader>
          <CardContent className="p-4"><ul className="space-y-1">{assumptions.map((a, i) => <li key={`assumption-${i}-${a.slice(0,20)}`} className="text-sm text-slate-400 flex gap-2"><span className="text-orange-400">-</span>{a}</li>)}</ul></CardContent>
        </Card>
      )}

      {exclusions.length > 0 && (
        <Card className="bg-slate-900 border border-slate-800 shadow-sm">
          <CardHeader className="border-b border-slate-800 bg-slate-800/50 py-3 px-4"><CardTitle className="text-sm font-semibold">Exclusions</CardTitle></CardHeader>
          <CardContent className="p-4"><ul className="space-y-1">{exclusions.map((e, i) => <li key={`exclusion-${i}-${e.slice(0,20)}`} className="text-sm text-slate-400 flex gap-2"><span className="text-red-400">-</span>{e}</li>)}</ul></CardContent>
        </Card>
      )}
      {saving && <p className="text-xs text-slate-400">Saving...</p>}

      {/* Add Scope Item Dialog */}
      <Dialog open={addItemDialog.open} onOpenChange={(open) => setAddItemDialog(prev => ({ ...prev, open }))}>
        <DialogContent className="bg-slate-900 border-slate-800 sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-base font-semibold text-slate-100">Add Deliverable to {addItemDialog.stage}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-xs text-slate-500 uppercase tracking-wider">Deliverable Name *</Label>
              <Input value={newItem.name} onChange={e => setNewItem(p => ({ ...p, name: e.target.value }))} placeholder="e.g. Ductwork Layout Drawings" className="mt-1.5 bg-slate-800 border-slate-700 text-sm" data-testid="add-item-name" autoFocus onKeyDown={e => e.key === "Enter" && newItem.name.trim() && addScopeItem()} />
            </div>
            <div>
              <Label className="text-xs text-slate-500 uppercase tracking-wider">Discipline</Label>
              <Select value={newItem.discipline} onValueChange={v => setNewItem(p => ({ ...p, discipline: v }))}>
                <SelectTrigger className="mt-1.5 bg-slate-800 border-slate-700 text-sm" data-testid="add-item-discipline">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Mechanical">Mechanical</SelectItem>
                  <SelectItem value="Electrical">Electrical</SelectItem>
                  <SelectItem value="Public Health">Public Health</SelectItem>
                  <SelectItem value="General">General</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-slate-500 uppercase tracking-wider">Classification</Label>
              <Select value={newItem.classification} onValueChange={v => setNewItem(p => ({ ...p, classification: v }))}>
                <SelectTrigger className="mt-1.5 bg-slate-800 border-slate-700 text-sm" data-testid="add-item-classification">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Default">Default</SelectItem>
                  <SelectItem value="Mandatory">Mandatory</SelectItem>
                  <SelectItem value="Recommended">Recommended</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-slate-500 uppercase tracking-wider">Description (optional)</Label>
              <Input value={newItem.description} onChange={e => setNewItem(p => ({ ...p, description: e.target.value }))} placeholder="Brief description of the deliverable" className="mt-1.5 bg-slate-800 border-slate-700 text-sm" data-testid="add-item-description" />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setAddItemDialog({ open: false, stage: "" })} className="border-slate-700 text-slate-300 hover:bg-slate-800" data-testid="add-item-cancel">Cancel</Button>
            <Button onClick={addScopeItem} disabled={!newItem.name.trim()} className="bg-blue-600 hover:bg-blue-500 text-white" data-testid="add-item-confirm">
              <Plus className="w-4 h-4 mr-1.5" />Add Deliverable
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Custom Stage Dialog */}
      <Dialog open={addStageDialog} onOpenChange={setAddStageDialog}>
        <DialogContent className="bg-slate-900 border-slate-800 sm:max-w-md" data-testid="add-stage-dialog">
          <DialogHeader><DialogTitle className="text-base font-semibold text-slate-100">Add Custom Stage</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-xs text-slate-500 uppercase tracking-wider">Stage Name *</Label>
              <Input value={newStageName} onChange={e => setNewStageName(e.target.value)} placeholder="e.g. Feasibility Study" className="mt-1.5 bg-slate-800 border-slate-700 text-sm" data-testid="custom-stage-name" autoFocus onKeyDown={e => e.key === "Enter" && addCustomStage()} />
            </div>
            <div>
              <Label className="text-xs text-slate-500 uppercase tracking-wider">Initial Discipline</Label>
              <Select value={newStageDiscipline} onValueChange={setNewStageDiscipline}>
                <SelectTrigger className="mt-1.5 bg-slate-800 border-slate-700 text-sm" data-testid="custom-stage-discipline"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="Mechanical">Mechanical</SelectItem><SelectItem value="Electrical">Electrical</SelectItem><SelectItem value="Public Health">Public Health</SelectItem><SelectItem value="General">General</SelectItem></SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setAddStageDialog(false)} className="border-slate-700">Cancel</Button>
            <Button onClick={addCustomStage} disabled={!newStageName.trim()} className="bg-blue-600 hover:bg-blue-500 text-white" data-testid="confirm-add-stage"><Plus className="w-4 h-4 mr-1.5" />Add Stage</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* CSV Import Dialog */}
      <Dialog open={csvImportDialog} onOpenChange={v => { setCsvImportDialog(v); if (!v) { setCsvPreview(null); setCsvText(""); } }}>
        <DialogContent className="bg-slate-900 border-slate-800 sm:max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="csv-import-dialog">
          <DialogHeader><DialogTitle className="text-base font-semibold text-slate-100">Import Scope from CSV</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-xs text-slate-500 uppercase tracking-wider">CSV Data</Label>
              <p className="text-[10px] text-slate-600 mt-0.5 mb-1.5">Headers: stage, discipline, deliverable, sub_deliverable, classification, quantity (comma, tab, or pipe separated)</p>
              <Textarea value={csvText} onChange={e => setCsvText(e.target.value)} placeholder={"stage,discipline,deliverable,sub_deliverable,classification,quantity\nStage 3,Mechanical,Ventilation Design,Ventilation layouts,Default,1\nStage 3,Mechanical,Ventilation Design,Plantroom layouts,Default,1\nStage 3,Electrical,Lighting Design,Lighting layouts,Recommended,1"} className="bg-slate-800 border-slate-700 text-xs font-mono min-h-[120px]" data-testid="csv-textarea" />
            </div>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={() => parseCSV(csvText)} disabled={!csvText.trim()} className="border-slate-700 text-xs" data-testid="csv-parse-btn">Preview Import</Button>
              <Select value={csvMode} onValueChange={setCsvMode}>
                <SelectTrigger className="w-32 bg-slate-800 border-slate-700 h-8 text-xs" data-testid="csv-mode-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700"><SelectItem value="replace">Replace Scope</SelectItem><SelectItem value="merge">Merge with Existing</SelectItem></SelectContent>
              </Select>
            </div>
            {csvPreview && (
              <div>
                <p className="text-xs text-slate-400 mb-2">{csvPreview.rows.length} rows parsed. {csvPreview.errors.length > 0 && <span className="text-amber-400">{csvPreview.errors.length} error(s)</span>}</p>
                {csvPreview.errors.length > 0 && <div className="bg-amber-500/10 border border-amber-500/20 rounded-md p-2 mb-2">{csvPreview.errors.map((e, i) => <p key={`csv-err-${i}`} className="text-[10px] text-amber-400">{e}</p>)}</div>}
                <div className="max-h-[200px] overflow-auto rounded-md border border-slate-800">
                  <Table>
                    <TableHeader><TableRow className="bg-slate-800/50">
                      {csvPreview.headers.map(h => <TableHead key={h} className="text-[10px] px-2 py-1 font-semibold text-slate-500">{h}</TableHead>)}
                    </TableRow></TableHeader>
                    <TableBody>
                      {csvPreview.rows.slice(0, 10).map((row, i) => (
                        <TableRow key={`csv-row-${i}`}>{csvPreview.headers.map(h => <TableCell key={h} className="text-[10px] px-2 py-1 text-slate-400">{row[h] || "—"}</TableCell>)}</TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  {csvPreview.rows.length > 10 && <p className="text-[10px] text-slate-500 text-center py-1">... and {csvPreview.rows.length - 10} more rows</p>}
                </div>
                {csvMode === "replace" && items.length > 0 && <p className="text-[10px] text-amber-400 mt-2">Warning: This will replace your existing {items.length} scope items.</p>}
              </div>
            )}
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => { setCsvImportDialog(false); setCsvPreview(null); setCsvText(""); }} className="border-slate-700">Cancel</Button>
            <Button onClick={importCSV} disabled={!csvPreview?.rows?.length || saving} className="bg-blue-600 hover:bg-blue-500 text-white" data-testid="csv-confirm-import">
              <Upload className="w-4 h-4 mr-1.5" />Import {csvPreview?.rows?.length || 0} Items
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* Import Hours CSV Dialog */}
      <Dialog open={hoursImportDialog} onOpenChange={v => { setHoursImportDialog(v); if (!v) { setHoursCsvText(""); setHoursImportResult(null); } }}>
        <DialogContent className="bg-slate-900 border-slate-800 sm:max-w-2xl" data-testid="hours-import-dialog">
          <DialogHeader>
            <DialogTitle className="text-base font-semibold text-slate-100">Import Staff Hours from CSV</DialogTitle>
            <p className="text-xs text-slate-500 mt-1">Paste CSV data with columns: stage, discipline, deliverable, and role names (Director, Associate, etc.). Hours will be matched to existing deliverables.</p>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="flex gap-2 items-center">
              <Button variant="outline" size="sm" onClick={downloadHoursTemplate} className="text-xs border-cyan-800 text-cyan-400 hover:bg-cyan-500/10" data-testid="download-hours-template-inline-btn">
                <Download className="w-3 h-3 mr-1.5" />Download Template
              </Button>
              <span className="text-[10px] text-slate-600">Fill in the template and paste below</span>
            </div>
            <textarea
              data-testid="hours-csv-textarea"
              value={hoursCsvText}
              onChange={e => setHoursCsvText(e.target.value)}
              className="w-full h-48 rounded-md bg-slate-800 border border-slate-700 text-slate-300 text-xs font-mono p-3 resize-none focus:ring-1 focus:ring-cyan-500 focus:border-cyan-500"
              placeholder={'stage,discipline,deliverable,Director,Associate,Senior Engineer,Engineer,CAD/BIM Technician\n"Stage 2","Mechanical","Heating Design Drawings",2,4,8,16,12'}
            />
            {hoursImportResult && (
              <div className="rounded-md border border-slate-700 bg-slate-800/50 p-3 space-y-1.5">
                <p className="text-xs font-medium text-slate-200">
                  <span className="text-emerald-400">{hoursImportResult.matched}</span> matched, <span className="text-amber-400">{hoursImportResult.unmatched?.length || 0}</span> unmatched out of {hoursImportResult.total_rows} rows
                </p>
                {hoursImportResult.unmatched?.length > 0 && (
                  <div className="max-h-24 overflow-y-auto">
                    {hoursImportResult.unmatched.map((msg, i) => <p key={`unmatched-${i}`} className="text-[10px] text-amber-400/80">{msg}</p>)}
                  </div>
                )}
                {hoursImportResult.errors?.length > 0 && (
                  <div className="max-h-24 overflow-y-auto">
                    {hoursImportResult.errors.map((msg, i) => <p key={`err-${i}`} className="text-[10px] text-red-400/80">{msg}</p>)}
                  </div>
                )}
              </div>
            )}
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setHoursImportDialog(false)} className="border-slate-700">Cancel</Button>
            <Button onClick={importHoursCSV} disabled={hoursImporting || !hoursCsvText.trim()} className="bg-cyan-600 hover:bg-cyan-500 text-white" data-testid="import-hours-submit-btn">
              {hoursImporting ? "Importing..." : "Import Hours"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Save as Template Dialog */}
      <Dialog open={saveTemplateDialog} onOpenChange={setSaveTemplateDialog}>
        <DialogContent className="bg-slate-900 border-slate-800 sm:max-w-md" data-testid="save-template-dialog">
          <DialogHeader><DialogTitle className="text-base font-semibold text-slate-100">Save Scope as Template</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-xs text-slate-500 uppercase tracking-wider">Template Name *</Label>
              <Input value={templateName} onChange={e => setTemplateName(e.target.value)} placeholder="e.g. Standard Office Fit-out" className="mt-1.5 bg-slate-800 border-slate-700 text-sm" data-testid="template-name-input" autoFocus />
            </div>
            <div>
              <Label className="text-xs text-slate-500 uppercase tracking-wider">Description (optional)</Label>
              <Input value={templateDesc} onChange={e => setTemplateDesc(e.target.value)} placeholder="Brief description" className="mt-1.5 bg-slate-800 border-slate-700 text-sm" data-testid="template-desc-input" />
            </div>
            <p className="text-[10px] text-slate-500">This will save {items.length} deliverables, {exclusions.length} exclusions, {assumptions.length} assumptions.</p>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setSaveTemplateDialog(false)} className="border-slate-700">Cancel</Button>
            <Button onClick={saveAsTemplate} disabled={!templateName.trim()} className="bg-emerald-600 hover:bg-emerald-500 text-white" data-testid="confirm-save-template"><Save className="w-4 h-4 mr-1.5" />Save Template</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Load Saved Template Dialog */}
      <Dialog open={loadTemplateDialog} onOpenChange={setLoadTemplateDialog}>
        <DialogContent className="bg-slate-900 border-slate-800 sm:max-w-lg" data-testid="load-template-dialog">
          <DialogHeader><DialogTitle className="text-base font-semibold text-slate-100">Load Saved Template</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2 max-h-[400px] overflow-y-auto">
            {savedTemplates.map(tpl => (
              <div key={tpl.id} className="p-3 rounded-md border border-slate-800 bg-slate-800/30">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm font-semibold text-slate-200">{tpl.name}</p>
                    {tpl.description && <p className="text-xs text-slate-500 mt-0.5">{tpl.description}</p>}
                    <p className="text-[10px] text-slate-600 mt-0.5">{tpl.items?.length || 0} items · {tpl.stage_framework} · by {tpl.created_by}</p>
                  </div>
                  <div className="flex gap-1">
                    <Button size="sm" variant="outline" onClick={() => loadSavedTemplate(tpl.id, "replace")} className="border-slate-700 h-7 text-[10px]" data-testid={`load-tpl-replace-${tpl.id}`}>Replace</Button>
                    <Button size="sm" variant="outline" onClick={() => loadSavedTemplate(tpl.id, "merge")} className="border-slate-700 h-7 text-[10px]" data-testid={`load-tpl-merge-${tpl.id}`}>Merge</Button>
                  </div>
                </div>
              </div>
            ))}
            {savedTemplates.length === 0 && <p className="text-xs text-slate-500 text-center py-4">No saved templates yet.</p>}
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setLoadTemplateDialog(false)} className="border-slate-700">Close</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Merge Preview / Scope Comparison Dialog */}
      <Dialog open={!!mergePreview} onOpenChange={v => { if (!v) setMergePreview(null); }}>
        <DialogContent className="bg-slate-900 border-slate-800 sm:max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="merge-preview-dialog">
          <DialogHeader><DialogTitle className="text-base font-semibold text-slate-100">Merge Preview — {mergePreview?.template?.name}</DialogTitle></DialogHeader>
          {mergePreview && (
            <div className="space-y-4 py-2">
              {/* Summary */}
              <div className="grid grid-cols-3 gap-3">
                <div className="p-3 rounded-md bg-slate-800/50 border border-slate-800 text-center">
                  <p className="text-lg font-mono font-bold text-slate-200">{items.length}</p>
                  <p className="text-[10px] text-slate-500 uppercase tracking-wider">Current Items</p>
                </div>
                <div className="p-3 rounded-md bg-emerald-500/5 border border-emerald-800/50 text-center">
                  <p className="text-lg font-mono font-bold text-emerald-400">+{mergePreview.newItems.length}</p>
                  <p className="text-[10px] text-emerald-500 uppercase tracking-wider">New from Template</p>
                </div>
                <div className="p-3 rounded-md bg-blue-500/5 border border-blue-800/50 text-center">
                  <p className="text-lg font-mono font-bold text-blue-400">{mergePreview.totalAfter}</p>
                  <p className="text-[10px] text-blue-500 uppercase tracking-wider">After Merge</p>
                </div>
              </div>

              {/* New items from template */}
              {mergePreview.newItems.length > 0 && (
                <div>
                  <p className="text-[10px] text-emerald-400 uppercase tracking-wider font-semibold mb-1.5 flex items-center gap-1"><Plus className="w-3 h-3" /> Will Be Added ({mergePreview.newItems.length})</p>
                  <div className="space-y-1 max-h-[150px] overflow-y-auto">
                    {mergePreview.newItems.map((item, i) => (
                      <div key={`${item.stage}-${item.discipline}-${item.name}`} className="flex items-center gap-2 py-1 px-2.5 rounded bg-emerald-500/5 border border-emerald-800/30 text-xs">
                        <Badge variant="outline" className="border-slate-700 text-[9px] h-4 px-1.5 shrink-0">{item.stage}</Badge>
                        <span className="text-slate-400 shrink-0">{item.discipline}</span>
                        <span className="text-slate-200 font-medium truncate">{item.name}</span>
                        {(item.sub_items || []).length > 0 && <span className="text-[9px] text-slate-600">({item.sub_items.length} subs)</span>}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {mergePreview.newItems.length === 0 && (
                <p className="text-xs text-slate-500 text-center py-2 bg-slate-800/30 rounded-md">No new items — template scope is already fully present in current scope.</p>
              )}

              {/* Shared items (already exist) */}
              {mergePreview.shared.length > 0 && (
                <div>
                  <p className="text-[10px] text-slate-500 uppercase tracking-wider font-semibold mb-1.5">Unchanged — Already in Scope ({mergePreview.shared.length})</p>
                  <div className="space-y-0.5 max-h-[100px] overflow-y-auto">
                    {mergePreview.shared.map((item, i) => (
                      <div key={`${item.stage}-${item.discipline}-${item.name}`} className="flex items-center gap-2 py-0.5 px-2.5 text-[10px] text-slate-600">
                        <span>{item.stage}</span><span>·</span><span>{item.discipline}</span><span>·</span><span>{item.name}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Items only in current scope */}
              {mergePreview.existingOnly.length > 0 && (
                <div>
                  <p className="text-[10px] text-slate-500 uppercase tracking-wider font-semibold mb-1.5">Kept — Only in Current Scope ({mergePreview.existingOnly.length})</p>
                  <div className="space-y-0.5 max-h-[100px] overflow-y-auto">
                    {mergePreview.existingOnly.map((item, i) => (
                      <div key={`${item.stage}-${item.discipline}-${item.name}`} className="flex items-center gap-2 py-0.5 px-2.5 text-[10px] text-slate-500">
                        <span>{item.stage}</span><span>·</span><span>{item.discipline}</span><span>·</span><span>{item.name}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setMergePreview(null)} className="border-slate-700">Cancel</Button>
            <Button onClick={confirmMerge} disabled={saving || (mergePreview?.newItems?.length || 0) === 0} className="bg-blue-600 hover:bg-blue-500 text-white" data-testid="confirm-merge-btn">
              <Plus className="w-4 h-4 mr-1.5" />Merge {mergePreview?.newItems?.length || 0} New Items
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function FeeOverrideDialog({ open, onClose, projectId, level, stage, discipline, currentFee, cur, onUpdate }) {
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (open) { setAmount(""); setNote(""); } }, [open]);

  const levelLabel = level === "total" ? "Project Total" : level === "stage" ? `Stage: ${stage}` : `${stage} / ${discipline}`;

  const save = async () => {
    setSaving(true);
    try {
      await API.put(`/projects/${projectId}/pricing/fee-overrides`, {
        level, stage: stage || undefined, discipline: discipline || undefined,
        amount: parseFloat(amount), note,
      });
      toast.success("Fee override saved");
      onUpdate();
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to save override"); }
    finally { setSaving(false); }
  };

  const remove = async () => {
    setSaving(true);
    try {
      await API.put(`/projects/${projectId}/pricing/fee-overrides`, {
        level, stage: stage || undefined, discipline: discipline || undefined, amount: null,
      });
      toast.success("Override removed — reverted to calculated fee");
      onUpdate();
      onClose();
    } catch (e) { toast.error("Failed to remove override"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={v => { if (!v) onClose(); }}>
      <DialogContent className="bg-slate-900 border-slate-700 max-w-md" data-testid="fee-override-dialog">
        <DialogHeader>
          <DialogTitle className="text-sm font-semibold">Fee Override — {levelLabel}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 pt-2">
          <div className="flex items-center justify-between text-xs">
            <span className="text-slate-500">Calculated Fee</span>
            <span className="font-mono text-slate-300">{cur.symbol}{currentFee?.toLocaleString("en-GB", { minimumFractionDigits: 2 })}</span>
          </div>
          <div>
            <Label className="text-[10px] text-slate-500 uppercase tracking-wider font-semibold">Override Amount ({cur.symbol})</Label>
            <Input type="number" step="0.01" min="0" value={amount} onChange={e => setAmount(e.target.value)} className="mt-1 bg-slate-800 border-slate-700 h-9 text-sm font-mono" data-testid="override-amount-input" placeholder={`e.g. ${Math.round(currentFee || 0)}`} autoFocus />
          </div>
          <div>
            <Label className="text-[10px] text-slate-500 uppercase tracking-wider font-semibold">Reason / Note</Label>
            <Input value={note} onChange={e => setNote(e.target.value)} className="mt-1 bg-slate-800 border-slate-700 h-9 text-sm" data-testid="override-note-input" placeholder="e.g. Fixed fee agreed with client" />
          </div>
        </div>
        <DialogFooter className="flex gap-2 pt-2">
          <Button size="sm" onClick={save} disabled={saving || !amount} className="bg-blue-600 hover:bg-blue-500 text-white h-8 text-xs" data-testid="save-override-btn">Set Override</Button>
          <Button size="sm" variant="outline" onClick={remove} disabled={saving} className="border-slate-700 h-8 text-xs text-amber-400" data-testid="remove-override-btn">Revert to Calculated</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function GranularDiscountPanel({ projectId, pricing, cur, onUpdate }) {
  const [adding, setAdding] = useState(false);
  const [level, setLevel] = useState("project");
  const [stage, setStage] = useState("");
  const [discipline, setDiscipline] = useState("");
  const [discType, setDiscType] = useState("percentage");
  const [value, setValue] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  const discounts = pricing?.granular_discounts || [];
  const stages = Object.keys(pricing?.fee_by_stage || {}).sort();
  const disciplines = [...new Set(Object.values(pricing?.stage_discipline_matrix || {}).flatMap(s => Object.keys(s)))].sort();

  const save = async () => {
    if (!value || parseFloat(value) <= 0) { toast.error("Enter a positive discount value"); return; }
    if ((level === "stage" || level === "stage_discipline") && !stage) { toast.error("Select a stage"); return; }
    if (level === "stage_discipline" && !discipline) { toast.error("Select a discipline"); return; }
    setSaving(true);
    try {
      await API.put(`/projects/${projectId}/pricing/granular-discounts`, {
        level, stage: stage || undefined, discipline: discipline || undefined,
        type: discType, value: parseFloat(value), note,
      });
      toast.success("Discount added");
      setAdding(false); setValue(""); setNote(""); setStage(""); setDiscipline("");
      onUpdate();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to add discount"); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    try {
      await API.delete(`/projects/${projectId}/pricing/granular-discounts/${id}`);
      toast.success("Discount removed");
      onUpdate();
    } catch { toast.error("Failed to remove discount"); }
  };

  const levelLabel = (d) => {
    if (d.level === "project") return "Project-wide";
    if (d.level === "stage") return d.stage;
    return `${d.stage} / ${d.discipline}`;
  };

  return (
    <Card className="bg-slate-900 border border-slate-800 shadow-sm" data-testid="granular-discount-panel">
      <CardHeader className="border-b border-slate-800 bg-slate-800/50 py-3 px-4">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-semibold flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />Granular Discounts</CardTitle>
          <Button variant="outline" size="sm" onClick={() => setAdding(!adding)} className="border-slate-800 h-7 text-xs" data-testid="add-discount-btn">{adding ? "Cancel" : "Add Discount"}</Button>
        </div>
      </CardHeader>
      <CardContent className="p-4">
        {adding && (
          <div className="space-y-3 mb-4 pb-4 border-b border-slate-800">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[10px] text-slate-500 uppercase tracking-wider font-semibold">Level</Label>
                <Select value={level} onValueChange={v => { setLevel(v); setStage(""); setDiscipline(""); }}>
                  <SelectTrigger className="mt-1 bg-slate-800 border-slate-700 h-8 text-xs" data-testid="discount-level-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700">
                    <SelectItem value="project">Project-wide</SelectItem>
                    <SelectItem value="stage">Stage</SelectItem>
                    <SelectItem value="stage_discipline">Stage + Discipline</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[10px] text-slate-500 uppercase tracking-wider font-semibold">Type</Label>
                <Select value={discType} onValueChange={setDiscType}>
                  <SelectTrigger className="mt-1 bg-slate-800 border-slate-700 h-8 text-xs" data-testid="discount-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700">
                    <SelectItem value="percentage">Percentage (%)</SelectItem>
                    <SelectItem value="fixed">Fixed ({cur.symbol})</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            {(level === "stage" || level === "stage_discipline") && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-[10px] text-slate-500 uppercase tracking-wider font-semibold">Stage</Label>
                  <Select value={stage} onValueChange={setStage}>
                    <SelectTrigger className="mt-1 bg-slate-800 border-slate-700 h-8 text-xs" data-testid="discount-stage-select"><SelectValue placeholder="Select stage" /></SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-700">
                      {stages.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                {level === "stage_discipline" && (
                  <div>
                    <Label className="text-[10px] text-slate-500 uppercase tracking-wider font-semibold">Discipline</Label>
                    <Select value={discipline} onValueChange={setDiscipline}>
                      <SelectTrigger className="mt-1 bg-slate-800 border-slate-700 h-8 text-xs" data-testid="discount-discipline-select"><SelectValue placeholder="Select discipline" /></SelectTrigger>
                      <SelectContent className="bg-slate-800 border-slate-700">
                        {disciplines.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[10px] text-slate-500 uppercase tracking-wider font-semibold">Value {discType === "percentage" ? "(%)" : `(${cur.symbol})`}</Label>
                <Input type="number" step="0.01" min="0" value={value} onChange={e => setValue(e.target.value)} className="mt-1 bg-slate-800 border-slate-700 h-8 text-sm font-mono" data-testid="discount-value-input" placeholder={discType === "percentage" ? "e.g. 10" : "e.g. 2000"} />
              </div>
              <div>
                <Label className="text-[10px] text-slate-500 uppercase tracking-wider font-semibold">Note</Label>
                <Input value={note} onChange={e => setNote(e.target.value)} className="mt-1 bg-slate-800 border-slate-700 h-8 text-sm" data-testid="discount-note-input" placeholder="e.g. Client negotiation" />
              </div>
            </div>
            <Button size="sm" onClick={save} disabled={saving} className="bg-emerald-600 hover:bg-emerald-500 text-white h-7 text-xs" data-testid="save-discount-btn">Add Discount</Button>
          </div>
        )}
        {discounts.length === 0 && !adding && (
          <p className="text-xs text-slate-500 text-center py-2">No granular discounts applied. These are in addition to the base project discount %.</p>
        )}
        {discounts.length > 0 && (
          <div className="space-y-2">
            {discounts.map(d => (
              <div key={d.id} className="flex items-center justify-between py-2 px-3 rounded-md bg-slate-800/50 border border-slate-800">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="border-emerald-800 text-emerald-400 text-[9px] h-4 px-1.5">{levelLabel(d)}</Badge>
                    <span className="text-sm font-mono font-semibold text-emerald-400">
                      {d.type === "percentage" ? `${d.value}%` : `${cur.symbol}${d.value?.toLocaleString("en-GB")}`}
                    </span>
                  </div>
                  {d.note && <p className="text-[10px] text-slate-500 mt-0.5 truncate">{d.note}</p>}
                  {d.set_by && <p className="text-[9px] text-slate-600 mt-0.5">by {d.set_by}, {new Date(d.set_at).toLocaleDateString("en-GB")}</p>}
                </div>
                <Button variant="ghost" size="sm" onClick={() => remove(d.id)} className="h-6 w-6 p-0 text-slate-500 hover:text-rose-400" data-testid={`remove-discount-${d.id}`}><Trash2 className="w-3 h-3" /></Button>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function ManualAdjustmentPanel({ projectId, pricing, onUpdate, cur }) {
  const [editing, setEditing] = useState(false);
  const [amount, setAmount] = useState(pricing?.manual_adjustment?.amount || 0);
  const [note, setNote] = useState(pricing?.manual_adjustment?.note || "");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await API.put(`/projects/${projectId}/pricing/manual-adjustment`, { amount: parseFloat(amount) || 0, note });
      toast.success("Manual adjustment saved");
      setEditing(false);
      onUpdate();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to save adjustment"); }
    finally { setSaving(false); }
  };

  const clear = async () => {
    setSaving(true);
    try {
      await API.put(`/projects/${projectId}/pricing/manual-adjustment`, { amount: 0, note: "" });
      toast.success("Adjustment cleared");
      setEditing(false);
      setAmount(0);
      setNote("");
      onUpdate();
    } catch { toast.error("Failed to clear"); }
    finally { setSaving(false); }
  };

  const ma = pricing?.manual_adjustment;
  const hasAdj = ma && ma.amount !== 0;

  return (
    <Card className="bg-slate-900 border border-slate-800 shadow-sm" data-testid="manual-adjustment-panel">
      <CardHeader className="border-b border-slate-800 bg-slate-800/50 py-3 px-4">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-semibold flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-teal-400" />Commercial Adjustment</CardTitle>
          <Button variant="outline" size="sm" onClick={() => setEditing(!editing)} className="border-slate-800 h-7 text-xs" data-testid="edit-adjustment-btn">
            {editing ? "Cancel" : hasAdj ? "Edit" : "Add Adjustment"}
          </Button>
        </div>
      </CardHeader>
      <CardContent className="p-4">
        {!editing && !hasAdj && <p className="text-xs text-slate-500 text-center py-2">No manual adjustment applied. The final fee equals the risk-adjusted fee minus discount.</p>}
        {!editing && hasAdj && (
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-slate-200">{ma.amount >= 0 ? "+" : ""}{cur.symbol}{ma.amount?.toLocaleString("en-GB", { minimumFractionDigits: 2 })}</p>
              {ma.note && <p className="text-xs text-slate-500 mt-0.5">{ma.note}</p>}
              {ma.set_by && <p className="text-[10px] text-slate-600 mt-0.5">Set by {ma.set_by} on {new Date(ma.set_at).toLocaleDateString("en-GB")}</p>}
            </div>
          </div>
        )}
        {editing && (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[10px] text-slate-500 uppercase tracking-wider font-semibold">Amount ({cur.symbol})</Label>
                <Input type="number" step="0.01" value={amount} onChange={e => setAmount(e.target.value)} className="mt-1 bg-slate-900 border-slate-800 h-8 text-sm font-mono" data-testid="adjustment-amount-input" placeholder="e.g. 2000 or -1500" />
                <p className="text-[9px] text-slate-600 mt-0.5">Positive adds to fee, negative reduces</p>
              </div>
              <div>
                <Label className="text-[10px] text-slate-500 uppercase tracking-wider font-semibold">Note</Label>
                <Input value={note} onChange={e => setNote(e.target.value)} className="mt-1 bg-slate-900 border-slate-800 h-8 text-sm" data-testid="adjustment-note-input" placeholder="e.g. Client relationship discount" />
              </div>
            </div>
            <div className="flex gap-2">
              <Button size="sm" onClick={save} disabled={saving} className="bg-blue-600 hover:bg-blue-500 text-white h-7 text-xs" data-testid="save-adjustment-btn">Save Adjustment</Button>
              {hasAdj && <Button size="sm" variant="outline" onClick={clear} disabled={saving} className="border-slate-800 h-7 text-xs text-rose-400" data-testid="clear-adjustment-btn">Clear</Button>}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function StageDisciplineMatrix({ matrix, cur }) {
  const [viewMode, setViewMode] = useState("final_fee");
  const stages = Object.keys(matrix).sort();
  const disciplines = [...new Set(stages.flatMap(s => Object.keys(matrix[s])))].sort();
  
  const views = [
    { key: "hours", label: "Hours", dot: "bg-slate-400" },
    { key: "base_fee", label: "Base Fee", dot: "bg-blue-400" },
    { key: "contingency", label: "Contingency", dot: "bg-amber-400" },
    { key: "discount", label: "Discount", dot: "bg-slate-500" },
    { key: "final_fee", label: "Final Fee", dot: "bg-blue-400" },
  ];

  const getVal = (stage, disc) => matrix[stage]?.[disc]?.[viewMode] || 0;
  const isHours = viewMode === "hours";
  const fmt = v => {
    if (v === 0) return "—";
    if (isHours) return `${v.toLocaleString("en-GB", { minimumFractionDigits: 0 })}h`;
    return `${cur.symbol}${Math.abs(v).toLocaleString("en-GB", { minimumFractionDigits: 0 })}`;
  };

  // Percentage of grand total for each cell
  const grandTotal = stages.reduce((s, st) => s + disciplines.reduce((s2, d) => s2 + getVal(st, d), 0), 0) || 1;

  return (
    <Card className="bg-slate-900 border border-slate-800 shadow-sm" data-testid="stage-discipline-matrix">
      <CardHeader className="border-b border-slate-800 bg-slate-800/50 py-3 px-4">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <CardTitle className="text-sm font-semibold">Stage × Discipline Matrix</CardTitle>
          <div className="flex gap-1">
            {views.map(v => (
              <button key={v.key} onClick={() => setViewMode(v.key)} className={`text-[10px] px-2.5 py-1 rounded-md font-semibold transition-colors flex items-center gap-1 ${viewMode === v.key ? "bg-slate-700 text-slate-100" : "text-slate-500 hover:text-slate-300 hover:bg-slate-800"}`} data-testid={`matrix-view-${v.key}`}>
                <span className={`w-1.5 h-1.5 rounded-full ${v.dot}`} />{v.label}
              </button>
            ))}
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader><TableRow className="bg-slate-800/50">
              <TableHead className="text-xs font-semibold text-slate-400 px-4">Stage</TableHead>
              {disciplines.map(d => <TableHead key={d} className="text-xs font-semibold text-slate-400 px-3 text-right">{d}</TableHead>)}
              <TableHead className="text-xs font-semibold text-slate-200 px-4 text-right">Stage Total</TableHead>
              <TableHead className="text-xs font-semibold text-slate-500 px-3 text-right">%</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {stages.map(stage => {
                const stageTotal = disciplines.reduce((s, d) => s + getVal(stage, d), 0);
                const stagePct = grandTotal > 0 ? (stageTotal / grandTotal * 100).toFixed(1) : 0;
                return (
                  <TableRow key={stage}>
                    <TableCell className="px-4 py-2 text-sm font-medium">{stage}</TableCell>
                    {disciplines.map(d => <TableCell key={d} className="px-3 py-2 text-sm font-mono text-right text-slate-300">{fmt(getVal(stage, d))}</TableCell>)}
                    <TableCell className="px-4 py-2 text-sm font-mono text-right font-semibold">{fmt(stageTotal)}</TableCell>
                    <TableCell className="px-3 py-2 text-xs font-mono text-right text-slate-500">{stagePct}%</TableCell>
                  </TableRow>
                );
              })}
              <TableRow className="border-t-2 border-slate-700 bg-slate-800/30">
                <TableCell className="px-4 py-2 text-sm font-bold">Total</TableCell>
                {disciplines.map(d => {
                  const colTotal = stages.reduce((s, st) => s + getVal(st, d), 0);
                  return <TableCell key={d} className="px-3 py-2 text-sm font-mono text-right font-semibold">{fmt(colTotal)}</TableCell>;
                })}
                <TableCell className="px-4 py-2 text-sm font-mono text-right font-bold text-blue-400">{fmt(grandTotal)}</TableCell>
                <TableCell className="px-3 py-2 text-xs font-mono text-right text-slate-500">100%</TableCell>
              </TableRow>
              {/* Discipline % row */}
              <TableRow className="bg-slate-800/20">
                <TableCell className="px-4 py-1.5 text-[10px] text-slate-500 font-semibold uppercase tracking-wider">% Share</TableCell>
                {disciplines.map(d => {
                  const colTotal = stages.reduce((s, st) => s + getVal(st, d), 0);
                  const colPct = grandTotal > 0 ? (colTotal / grandTotal * 100).toFixed(1) : 0;
                  return <TableCell key={d} className="px-3 py-1.5 text-xs font-mono text-right text-slate-500">{colPct}%</TableCell>;
                })}
                <TableCell colSpan={2}></TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}


function PricingTab({ projectId, pricing, onUpdate, isDirector, permissions, activeRoles = [] }) {
  const confirm = useConfirm();
  const ROLES = activeRoles.length > 0 ? activeRoles : DEFAULT_ROLES;
  const cur = useCurrency();
  const canEditPricing = permissions?.can_edit_pricing !== false;
  const [factors, setFactors] = useState({
    complexity_factor: pricing?.complexity_factor || 1.0,
    coordination_factor: pricing?.coordination_factor || 1.0,
    programme_factor: pricing?.programme_factor || 1.0,
    discount_percentage: pricing?.discount_percentage || 0,
    fee_rounding: pricing?.fee_rounding || null,
    procurement_route_effect: pricing?.procurement_route_effect || "",
    programme_duration: pricing?.programme_duration || "Normal",
    size_scaling_enabled: pricing?.size_scaling_enabled !== false,
    active_effort_drivers: pricing?.applied_effort_drivers ? pricing.applied_effort_drivers.map(d => d.name) : [],
  });
  const [calculating, setCalculating] = useState(false);
  const [scenarios, setScenarios] = useState([]);
  const [showScenarios, setShowScenarios] = useState(false);
  const [settings, setSettings] = useState(null);
  const [snapshots, setSnapshots] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [healthScore, setHealthScore] = useState(null);
  const [deletingSnapshot, setDeletingSnapshot] = useState(null);
  const [contractors, setContractors] = useState([]);
  const [contractorLib, setContractorLib] = useState([]);
  const [showAddContractor, setShowAddContractor] = useState(false);
  const [newCon, setNewCon] = useState({ name: "", cost: "", markup_enabled: false, markup_percentage: 0, notes: "", from_library_id: null });
  const [expandedStages, setExpandedStages] = useState([]);
  const [overrideTarget, setOverrideTarget] = useState(null); // {level, stage, discipline, currentFee}

  const refreshSnapshots = useCallback(() => API.get(`/projects/${projectId}/pricing/snapshots`).then(r => setSnapshots(r.data || [])).catch(() => {}), [projectId]);
  const refreshContractors = useCallback(() => API.get(`/projects/${projectId}/contractors`).then(r => setContractors(r.data || [])).catch(() => {}), [projectId]);

  useEffect(() => {
    API.get("/admin/settings").then(r => setSettings(r.data)).catch(() => {});
    refreshSnapshots();
    refreshContractors();
    API.get("/admin/contractors").then(r => setContractorLib(r.data || [])).catch(() => {});
    if (isDirector) {
      API.get(`/projects/${projectId}/health-score`).then(r => setHealthScore(r.data)).catch(() => {});
    }
  }, [projectId, isDirector, refreshSnapshots, refreshContractors]);

  const calculate = async () => {
    setCalculating(true);
    try {
      await API.post(`/projects/${projectId}/pricing/calculate`, factors);
      toast.success("Pricing calculated");
      onUpdate();
      refreshSnapshots();
      if (isDirector) API.get(`/projects/${projectId}/health-score`).then(r => setHealthScore(r.data)).catch(() => {});
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to calculate pricing");
    } finally {
      setCalculating(false);
    }
  };

  const addScenario = () => {
    setScenarios(prev => [...prev, {
      complexity_factor: factors.complexity_factor,
      coordination_factor: factors.coordination_factor,
      programme_factor: 1.0,
      discount_percentage: factors.discount_percentage,
      procurement_route_effect: factors.procurement_route_effect,
      programme_duration: factors.programme_duration,
    }]);
  };

  const runScenarios = async () => {
    if (scenarios.length === 0) return;
    try {
      const res = await API.post(`/projects/${projectId}/pricing/scenarios`, { scenarios });
      setScenarios(res.data);
      toast.success("Scenarios calculated");
    } catch (e) {
      toast.error("Failed to compare scenarios");
    }
  };

  const p = pricing || {};
  const hasData = p.calculated;
  const procurementOptions = settings?.procurement_route_effects ? Object.keys(settings.procurement_route_effects) : ["Traditional", "Design and Build", "Two Stage"];
  const programmeOptions = settings?.programme_duration_multipliers ? Object.keys(settings.programme_duration_multipliers) : ["Normal", "Compressed", "Accelerated"];
  const discountThreshold = settings?.discount_threshold || 15;
  const discountExceeded = factors.discount_percentage > discountThreshold;

  const addContractor = async () => {
    if (!newCon.name.trim()) { toast.error("Contractor name required"); return; }
    const cost = parseFloat(newCon.cost) || 0;
    try {
      const res = await API.post(`/projects/${projectId}/contractors`, { ...newCon, cost });
      setContractors(res.data);
      setNewCon({ name: "", cost: "", markup_enabled: false, markup_percentage: 0, notes: "", from_library_id: null });
      setShowAddContractor(false);
      toast.success("Contractor added");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const updateContractor = async (c) => {
    try {
      const res = await API.put(`/projects/${projectId}/contractors/${c.id}`, c);
      setContractors(res.data);
      toast.success("Contractor updated");
    } catch { toast.error("Failed to update"); }
  };

  const removeContractor = async (cId) => {
    try {
      const res = await API.delete(`/projects/${projectId}/contractors/${cId}`);
      setContractors(res.data);
      toast.success("Contractor removed");
    } catch { toast.error("Failed"); }
  };

  const selectFromLibrary = (libItem) => {
    setNewCon({ name: libItem.name, cost: String(libItem.default_rate || 0), markup_enabled: false, markup_percentage: 0, notes: "", from_library_id: libItem.id });
  };

  const contractorCostTotal = contractors.reduce((s, c) => s + (c.cost || 0), 0);
  const contractorClientTotal = contractors.reduce((s, c) => s + (c.client_fee || c.cost || 0), 0);

  return (
    <div className="space-y-6" data-testid="pricing-tab">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h2 className="font-heading text-xl font-semibold text-slate-100">Pricing Summary</h2>
          {isDirector && healthScore && healthScore.color !== "gray" && (
            <HealthScoreBadge healthScore={healthScore} size="compact" />
          )}
          {p.formula_version && (
            <span className="text-[10px] font-mono px-2 py-0.5 rounded border border-slate-800 bg-slate-800/50 text-slate-500" data-testid="formula-version-badge" title={`Pricing calculated with formula ${p.formula_version}`}>Formula {p.formula_version}</span>
          )}
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" size="sm" onClick={() => setShowScenarios(!showScenarios)} className="border-slate-800" data-testid="toggle-scenarios-btn">Scenarios</Button>
          <Button variant="outline" size="sm" onClick={async () => {
            try {
              const r = await API.get(`/analytics/export/project-detail-excel/${projectId}`, { responseType: "blob" });
              const url = URL.createObjectURL(r.data);
              const a = document.createElement("a"); a.href = url; a.download = `project_${projectId.substring(0, 8)}_pricing.xlsx`; a.click(); URL.revokeObjectURL(url);
              toast.success("Excel exported");
            } catch { toast.error("Export failed"); }
          }} className="border-slate-800" data-testid="export-project-excel-btn">
            <Download className="w-3.5 h-3.5 mr-1.5" />Excel
          </Button>
          {snapshots.length > 1 && (
            <Button variant="outline" size="sm" onClick={() => setShowHistory(!showHistory)} className="border-slate-800" data-testid="toggle-history-btn">
              <History className="w-3.5 h-3.5 mr-1.5" />Estimate History
            </Button>
          )}
          {canEditPricing && (
            <Button onClick={calculate} disabled={calculating} data-testid="calculate-pricing-btn" className="bg-blue-600 hover:bg-blue-500/10 text-white font-bold">
              <Calculator className="w-4 h-4 mr-2" />{calculating ? "Calculating..." : "Calculate Pricing"}
            </Button>
          )}
        </div>
      </div>

      {/* Core Factors */}
      <Card className="bg-slate-900 border border-slate-800 shadow-sm">
        <CardHeader className="border-b border-slate-800 bg-slate-800/50 py-3 px-4"><CardTitle className="text-sm font-semibold">Pricing Factors</CardTitle></CardHeader>
        <CardContent className="p-4 space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { key: "complexity_factor", label: "Complexity", tip: "Multiplier for project technical complexity. 1.0 is standard. Higher values (1.1-1.3) for complex projects like hospitals, data centres." },
              { key: "coordination_factor", label: "Coordination", tip: "Accounts for multi-discipline coordination effort. Increase for projects with heavy MEP integration requirements." },
              { key: "discount_percentage", label: "Discount %", tip: "Percentage discount on the total fee. Discounts above the threshold (default 15%) auto-flag the project for approval." },
              { key: "fee_rounding", label: `Round to (${cur.symbol})`, tip: `Round the final fee to the nearest value. Leave blank for no rounding. E.g. 500 rounds to nearest ${cur.symbol}500.` },
            ].map(({ key, label, tip }) => (
              <div key={key}>
                <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500 flex items-center gap-1">
                  {label}
                  <InfoTooltip text={tip} />
                </Label>
                <Input
                  data-testid={`factor-${key}`}
                  type="number" step="0.05" min="0"
                  value={factors[key] ?? ""}
                  onChange={(e) => setFactors((prev) => ({ ...prev, [key]: parseFloat(e.target.value) || 0 }))}
                  className={`mt-1.5 font-mono bg-slate-900 border-slate-800 ${key === "discount_percentage" && discountExceeded ? "border-red-400 ring-1 ring-red-300" : ""}`}
                />
              </div>
            ))}
          </div>
          {discountExceeded && (
            <div className="flex items-center gap-2 px-3 py-2 bg-rose-500/10 border border-rose-500/20 rounded-md" data-testid="discount-warning">
              <AlertTriangle className="w-4 h-4 text-red-500 flex-shrink-0" />
              <span className="text-sm text-rose-400">Discount ({factors.discount_percentage}%) exceeds threshold ({discountThreshold}%). Project will be auto-flagged for approval.</span>
            </div>
          )}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 pt-2 border-t border-slate-100">
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Procurement Route</Label>
              <Select value={factors.procurement_route_effect || ""} onValueChange={(v) => setFactors(prev => ({ ...prev, procurement_route_effect: v }))}>
                <SelectTrigger className="mt-1.5 bg-slate-900 border-slate-800" data-testid="factor-procurement"><SelectValue placeholder="Auto from project" /></SelectTrigger>
                <SelectContent>
                  {procurementOptions.map(o => <SelectItem key={o} value={o}>{o} ({(settings?.procurement_route_effects?.[o] || 1.0).toFixed(2)}x)</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Programme Duration</Label>
              <Select value={factors.programme_duration || "Normal"} onValueChange={(v) => setFactors(prev => ({ ...prev, programme_duration: v }))}>
                <SelectTrigger className="mt-1.5 bg-slate-900 border-slate-800" data-testid="factor-programme-duration"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {programmeOptions.map(o => <SelectItem key={o} value={o}>{o} ({(settings?.programme_duration_multipliers?.[o] || 1.0).toFixed(2)}x)</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end gap-2">
              <label className="flex items-center gap-2 text-sm cursor-pointer mb-2" data-testid="size-scaling-toggle">
                <Checkbox checked={factors.size_scaling_enabled} onCheckedChange={(v) => setFactors(prev => ({ ...prev, size_scaling_enabled: !!v }))} />
                <span className="text-slate-400">Size Scaling</span>
              </label>
            </div>
          </div>
          {/* Effort Drivers — per-project selection */}
          {settings?.effort_drivers?.filter(d => d.active).length > 0 && (
            <div className="pt-3 border-t border-slate-800">
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2 block">
                Effort Drivers
                <span className="text-[10px] font-normal normal-case ml-1.5 text-slate-600">Select which drivers apply to this project</span>
              </Label>
              <div className="flex flex-wrap gap-2">
                {settings.effort_drivers.filter(d => d.active).map(driver => {
                  const isSelected = factors.active_effort_drivers?.includes(driver.name);
                  return (
                    <label key={driver.name} className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border cursor-pointer transition-all text-xs ${
                      isSelected ? "border-blue-500/40 bg-blue-500/10 text-blue-300" : "border-slate-800 bg-slate-950 text-slate-500 hover:border-slate-700"
                    }`} data-testid={`effort-driver-${driver.name.replace(/\s/g, "-").toLowerCase()}`}>
                      <Checkbox checked={isSelected} onCheckedChange={(v) => {
                        setFactors(prev => ({
                          ...prev,
                          active_effort_drivers: v
                            ? [...(prev.active_effort_drivers || []), driver.name]
                            : (prev.active_effort_drivers || []).filter(n => n !== driver.name)
                        }));
                      }} className="w-3.5 h-3.5" />
                      <span className="font-medium">{driver.name}</span>
                      <span className="font-mono text-[10px] text-slate-600">{driver.multiplier}x</span>
                    </label>
                  );
                })}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Scenarios */}
      {showScenarios && (
        <Card className="bg-slate-900 border border-blue-500/20 shadow-sm">
          <CardHeader className="border-b border-blue-500/20 bg-blue-950/40 py-3 px-4">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-semibold text-blue-300">Scenario Comparison</CardTitle>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={addScenario} className="border-blue-800 text-blue-300 hover:bg-blue-900/50 h-8" data-testid="add-scenario-btn"><Plus className="w-3 h-3 mr-1" />Add</Button>
                <Button size="sm" onClick={runScenarios} disabled={scenarios.length === 0} className="bg-blue-600 hover:bg-blue-500 text-white h-8" data-testid="run-scenarios-btn">Compare</Button>
              </div>
            </div>
            <p className="text-[11px] text-slate-500 mt-1">Read-only comparison — does not overwrite your base pricing.</p>
          </CardHeader>
          <CardContent className="p-4">
            {scenarios.length === 0 ? (
              <p className="text-sm text-slate-500 text-center py-6">Click "Add" to create scenarios with different multiplier combinations.</p>
            ) : (
              <div className="overflow-x-auto">
                {/* Base pricing reference */}
                {hasData && (
                  <div className="mb-3 p-3 rounded-lg bg-slate-800/50 border border-slate-700/50 flex flex-wrap gap-x-6 gap-y-1">
                    <span className="text-xs text-slate-500">Base Fee: <strong className="text-slate-200 font-mono">{cur.symbol}{p.total_fee?.toLocaleString("en-GB", { minimumFractionDigits: 2 })}</strong></span>
                    {isDirector && <span className="text-xs text-slate-500">Base Margin: <strong className="text-slate-200 font-mono">{p.margin_percentage}%</strong></span>}
                  </div>
                )}
                <Table>
                  <TableHeader>
                    <TableRow className="bg-blue-950/30 border-blue-900/30">
                      <TableHead className="text-xs font-semibold text-blue-400 px-3">#</TableHead>
                      <TableHead className="text-xs font-semibold text-blue-400 px-3">Complexity</TableHead>
                      <TableHead className="text-xs font-semibold text-blue-400 px-3">Coord.</TableHead>
                      <TableHead className="text-xs font-semibold text-blue-400 px-3">Duration</TableHead>
                      <TableHead className="text-xs font-semibold text-blue-400 px-3">Discount %</TableHead>
                      <TableHead className="text-xs font-semibold text-blue-400 px-3 text-right">Fee</TableHead>
                      <TableHead className="text-xs font-semibold text-blue-400 px-3 text-right">vs Base</TableHead>
                      {isDirector && <TableHead className="text-xs font-semibold text-blue-400 px-3 text-right">Margin %</TableHead>}
                      <TableHead className="w-8"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {scenarios.map((s, i) => {
                      const delta = s.total_fee && p.total_fee ? s.total_fee - p.total_fee : null;
                      const deltaPct = delta !== null && p.total_fee > 0 ? (delta / p.total_fee * 100).toFixed(1) : null;
                      return (
                        <TableRow key={`scenario-${i}`} className="border-blue-900/20 hover:bg-blue-950/20">
                          <TableCell className="px-3 font-mono text-xs text-blue-300 font-bold">S{i + 1}</TableCell>
                          <TableCell className="px-3"><Input type="number" step="0.1" value={s.complexity_factor} onChange={e => setScenarios(prev => prev.map((sc, j) => j === i ? { ...sc, complexity_factor: parseFloat(e.target.value) || 1 } : sc))} className="h-7 w-16 bg-slate-800 border-slate-700 text-xs font-mono text-center" /></TableCell>
                          <TableCell className="px-3"><Input type="number" step="0.1" value={s.coordination_factor} onChange={e => setScenarios(prev => prev.map((sc, j) => j === i ? { ...sc, coordination_factor: parseFloat(e.target.value) || 1 } : sc))} className="h-7 w-16 bg-slate-800 border-slate-700 text-xs font-mono text-center" /></TableCell>
                          <TableCell className="px-3"><Input type="text" value={s.programme_duration || "Normal"} onChange={e => setScenarios(prev => prev.map((sc, j) => j === i ? { ...sc, programme_duration: e.target.value } : sc))} className="h-7 w-20 bg-slate-800 border-slate-700 text-xs font-mono text-center" /></TableCell>
                          <TableCell className="px-3"><Input type="number" step="1" value={s.discount_percentage} onChange={e => setScenarios(prev => prev.map((sc, j) => j === i ? { ...sc, discount_percentage: parseFloat(e.target.value) || 0 } : sc))} className="h-7 w-16 bg-slate-800 border-slate-700 text-xs font-mono text-center" /></TableCell>
                          <TableCell className="px-3 font-mono text-xs text-right font-medium text-white">{s.total_fee ? `${cur.symbol}${s.total_fee.toLocaleString("en-GB", { minimumFractionDigits: 2 })}` : "—"}</TableCell>
                          <TableCell className="px-3 font-mono text-xs text-right">
                            {delta !== null ? (
                              <span className={delta > 0 ? "text-emerald-400" : delta < 0 ? "text-rose-400" : "text-slate-500"}>
                                {delta > 0 ? "+" : ""}{deltaPct}%
                              </span>
                            ) : "—"}
                          </TableCell>
                          {isDirector && <TableCell className="px-3 font-mono text-xs text-right">{s.margin_percentage != null ? `${s.margin_percentage}%` : "—"}</TableCell>}
                          <TableCell className="px-1"><button onClick={() => setScenarios(prev => prev.filter((_, j) => j !== i))} className="text-slate-500 hover:text-red-400 transition-colors"><Trash2 className="w-3.5 h-3.5" /></button></TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {hasData && (
        <>
          {/* Applied Multipliers Summary */}
          <Card className="bg-slate-900 border border-slate-800 shadow-sm">
            <CardHeader className="border-b border-slate-800 bg-slate-800/50 py-3 px-4"><CardTitle className="text-sm font-semibold">Applied Multipliers</CardTitle></CardHeader>
            <CardContent className="p-4 grid grid-cols-2 md:grid-cols-5 gap-4">
              {p.size_scaling_multiplier && p.size_scaling_multiplier !== 1 && (
                <div><span className="text-xs text-slate-500 block">Size Scaling</span><span className="font-mono text-sm font-bold text-slate-100" data-testid="size-scaling-value">{p.size_scaling_multiplier}x</span></div>
              )}
              {p.procurement_effect_multiplier && p.procurement_effect_multiplier !== 1 && (
                <div><span className="text-xs text-slate-500 block">Procurement ({p.procurement_route_effect})</span><span className="font-mono text-sm font-bold text-slate-100">{p.procurement_effect_multiplier}x</span></div>
              )}
              {p.programme_duration_multiplier && p.programme_duration_multiplier !== 1 && (
                <div><span className="text-xs text-slate-500 block">Programme ({p.programme_duration})</span><span className="font-mono text-sm font-bold text-slate-100">{p.programme_duration_multiplier}x</span></div>
              )}
              {p.effort_driver_multiplier && p.effort_driver_multiplier !== 1 && (
                <div data-testid="effort-driver-display"><span className="text-xs text-slate-500 block">Effort Drivers</span><span className="font-mono text-sm font-bold text-slate-100">{p.effort_driver_multiplier}x</span>
                  {p.applied_effort_drivers && p.applied_effort_drivers.length > 0 && (
                    <div className="mt-1 space-y-0.5">{p.applied_effort_drivers.map((d, i) => <span key={`driver-${d.name}`} className="block text-xs text-slate-400">{d.name} ({d.multiplier}x)</span>)}</div>
                  )}
                </div>
              )}
              <div><span className="text-xs text-slate-500 block">Combined Multiplier</span><span className="font-mono text-sm font-bold text-blue-400" data-testid="combined-multiplier">{p.combined_multiplier}x</span></div>
            </CardContent>
          </Card>

          {/* Fee by Role */}
          <Card className="bg-slate-900 border border-slate-800 shadow-sm overflow-hidden">
            <CardHeader className="border-b border-slate-800 bg-slate-800/50 py-3 px-4"><CardTitle className="text-sm font-semibold">Fee by Role</CardTitle></CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-800/50">
                    <TableHead className="text-xs uppercase tracking-wider font-semibold text-slate-500 px-4">Role</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider font-semibold text-slate-500 px-4 text-right">Hours (hrs)</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider font-semibold text-slate-500 px-4 text-right">Rate ({cur.symbol}/hr)</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider font-semibold text-slate-500 px-4 text-right">Fee ({cur.symbol})</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {ROLES.map((role) => {
                    const d = p.fee_by_role?.[role] || {};
                    return (
                      <TableRow key={role} data-testid={`role-fee-${role.split(" ")[0].toLowerCase()}`}>
                        <TableCell className="px-4 py-3 text-sm font-medium">{role}</TableCell>
                        <TableCell className="px-4 py-3 text-sm font-mono text-right">{d.hours || 0}</TableCell>
                        <TableCell className="px-4 py-3 text-sm font-mono text-right">{cur.symbol}{d.rate || 0}</TableCell>
                        <TableCell className="px-4 py-3 text-sm font-mono text-right font-medium">{cur.symbol}{(d.fee || 0).toLocaleString("en-GB", { minimumFractionDigits: 2 })}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>


          {/* Optional Services */}
          {p.optional_services && p.optional_services.length > 0 && (
            <Card className="bg-slate-900 border border-sky-500/20 shadow-sm overflow-hidden">
              <CardHeader className="border-b border-sky-500/20 bg-sky-500/10/30 py-3 px-4">
                <CardTitle className="text-sm font-semibold text-indigo-800">Optional Services ({cur.symbol}{p.optional_services_fee?.toLocaleString("en-GB", { minimumFractionDigits: 2 })})</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <Table>
                  <TableHeader><TableRow className="bg-sky-500/10/50">
                    <TableHead className="text-xs font-semibold text-sky-400 px-4">Service</TableHead>
                    <TableHead className="text-xs font-semibold text-sky-400 px-4 text-right">Hours</TableHead>
                    <TableHead className="text-xs font-semibold text-sky-400 px-4 text-right">Fee</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {p.optional_services.map((os, i) => (
                      <TableRow key={os.name || `opt-svc-${i}`}><TableCell className="px-4 py-2 text-sm">{os.name}</TableCell><TableCell className="px-4 py-2 text-sm font-mono text-right">{os.hours}</TableCell><TableCell className="px-4 py-2 text-sm font-mono text-right font-medium">{cur.symbol}{os.fee?.toLocaleString("en-GB", { minimumFractionDigits: 2 })}</TableCell></TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}

          {/* Totals */}
          <div className="space-y-6">
            {/* ─── G. Commercial Summary Cards ─── */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="pricing-summary-cards">
              {[
                { label: "Base Calculated Fee", value: p.adjusted_fee, color: "blue", icon: "calc" },
                { label: "Confidence Contingency", value: p.confidence_contingency_amount, color: p.confidence_band === "Low" ? "rose" : p.confidence_band === "Medium" ? "amber" : "emerald", sub: `${p.confidence_band || "—"} +${p.confidence_contingency_pct || 0}%`, icon: "shield" },
                { label: "Risk Adjusted Fee", value: (p.adjusted_fee || 0) + (p.confidence_contingency_amount || 0), color: "indigo", icon: "total" },
                { label: "Manual Adjustment", value: p.manual_adjustment_amount || 0, color: (p.manual_adjustment_amount || 0) >= 0 ? "teal" : "orange", sub: p.manual_adjustment?.note, icon: "adj" },
                { label: "Total Discount", value: -(p.discount_amount || 0), color: "slate", sub: p.discount_percentage > 0 ? `${p.discount_percentage}%` : undefined, icon: "disc" },
                { label: "Final Proposed Fee", value: p.total_fee, color: "blue", highlight: true, icon: "final" },
              ].map(c => (
                <Card key={c.label} className={`bg-slate-900 border ${c.highlight ? "border-blue-500/40 ring-1 ring-blue-500/20" : "border-slate-800"} shadow-sm`} data-testid={`summary-card-${c.icon}`}>
                  <CardContent className="p-3.5">
                    <p className="text-[10px] text-slate-500 uppercase tracking-wider font-semibold truncate">{c.label}</p>
                    <p className={`text-lg font-mono font-bold mt-1 ${c.highlight ? "text-blue-400" : "text-slate-100"}`}>
                      {c.value < 0 ? "-" : ""}{cur.symbol}{Math.abs(c.value || 0).toLocaleString("en-GB", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                    </p>
                    {c.sub && <p className={`text-[10px] mt-0.5 ${c.color === "rose" ? "text-rose-400" : c.color === "amber" ? "text-amber-400" : c.color === "emerald" ? "text-emerald-400" : "text-slate-500"} truncate`}>{c.sub}</p>}
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Director internal cards */}
            {isDirector && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="pricing-internal-cards">
                {[
                  { label: "Internal Cost", value: p.total_cost },
                  { label: "Final Margin", value: p.margin, color: (p.margin || 0) >= 0 ? "emerald" : "rose" },
                  { label: "Margin %", value: null, display: `${p.margin_percentage || 0}%`, color: (p.margin_percentage || 0) >= 20 ? "emerald" : (p.margin_percentage || 0) >= 10 ? "amber" : "rose" },
                  { label: "Confidence", value: null, display: p.confidence_band || "—", color: p.confidence_band === "High" ? "emerald" : p.confidence_band === "Medium" ? "amber" : "rose" },
                ].map(c => (
                  <Card key={c.label} className="bg-slate-900 border-l-2 border-l-orange-400/60 border border-slate-800 shadow-sm">
                    <CardContent className="p-3.5">
                      <p className="text-[10px] text-slate-500 uppercase tracking-wider font-semibold flex items-center gap-1"><AlertTriangle className="w-3 h-3 text-orange-400" />{c.label}</p>
                      <p className={`text-lg font-mono font-bold mt-1 ${c.color === "emerald" ? "text-emerald-400" : c.color === "rose" ? "text-rose-400" : c.color === "amber" ? "text-amber-400" : "text-slate-100"}`}>
                        {c.display || `${c.value < 0 ? "-" : ""}${cur.symbol}${Math.abs(c.value || 0).toLocaleString("en-GB", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`}
                      </p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}

            {/* ─── D. Fee Waterfall Breakdown ─── */}
            <Card className="bg-slate-900 border border-slate-800 shadow-sm" data-testid="fee-waterfall">
              <CardHeader className="border-b border-slate-800 bg-slate-800/50 py-3 px-4"><CardTitle className="text-sm font-semibold">Fee Derivation</CardTitle></CardHeader>
              <CardContent className="p-4 divide-y divide-slate-800">
                {[
                  { label: "Base Calculated Fee", value: p.adjusted_fee, type: "calc" },
                  { label: `Confidence Contingency (${p.confidence_band || "—"} +${p.confidence_contingency_pct || 0}%)`, value: p.confidence_contingency_amount, type: "contingency", badge: p.confidence_band },
                  { label: "Risk Adjusted Fee", value: (p.adjusted_fee || 0) + (p.confidence_contingency_amount || 0), type: "subtotal" },
                  { label: `Manual Adjustment${p.manual_adjustment?.note ? ` — ${p.manual_adjustment.note}` : ""}`, value: p.manual_adjustment_amount || 0, type: "adjustment" },
                  { label: `Discount (${p.discount_percentage || 0}%)`, value: -(p.discount_amount || 0), type: "discount" },
                  { label: "Final Proposed Fee", value: p.total_fee, type: "total" },
                ].map((row, idx) => (
                  <div key={`${row.type}-${row.label}`} className={`flex justify-between items-center py-2.5 ${row.type === "total" ? "py-3" : ""} ${row.type === "subtotal" ? "bg-slate-800/30 -mx-4 px-4" : ""}`}>
                    <span className={`text-sm flex items-center gap-2 ${row.type === "total" ? "font-semibold text-slate-100" : row.type === "subtotal" ? "font-medium text-slate-300" : "text-slate-500"}`}>
                      {row.type === "calc" && <span className="w-1.5 h-1.5 rounded-full bg-blue-400 shrink-0" />}
                      {row.type === "contingency" && <span className="w-1.5 h-1.5 rounded-full bg-amber-400 shrink-0" />}
                      {row.type === "adjustment" && <span className="w-1.5 h-1.5 rounded-full bg-teal-400 shrink-0" />}
                      {row.type === "discount" && <span className="w-1.5 h-1.5 rounded-full bg-slate-500 shrink-0" />}
                      {row.label}
                      {row.badge && <span className={`text-[9px] font-semibold px-1.5 py-0.5 rounded-full ${row.badge === "Low" ? "bg-rose-500/10 text-rose-400" : row.badge === "Medium" ? "bg-amber-500/10 text-amber-400" : "bg-emerald-500/10 text-emerald-400"}`}>{row.badge}</span>}
                    </span>
                    <span className={`font-mono font-medium ${row.type === "total" ? "text-lg font-bold text-blue-400" : row.type === "subtotal" ? "font-semibold text-slate-200" : row.value < 0 ? "text-rose-400" : "text-slate-100"} text-sm`}>
                      {row.value < 0 ? "-" : row.type === "contingency" || row.type === "adjustment" ? "+" : ""}{cur.symbol}{Math.abs(row.value || 0).toLocaleString("en-GB", { minimumFractionDigits: 2 })}
                    </span>
                  </div>
                ))}
                {p.discount_exceeded && (
                  <div className="flex justify-between py-2 bg-rose-500/10 -mx-4 px-4" data-testid="pricing-discount-flag">
                    <span className="text-sm text-rose-400 font-medium flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5" />Discount exceeds {p.discount_threshold}% threshold</span>
                    <span className="text-sm font-mono text-rose-400 font-bold">{p.discount_percentage}%</span>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* ─── F. Manual Adjustment Control ─── */}
            {canEditPricing && (
              <ManualAdjustmentPanel projectId={projectId} pricing={p} onUpdate={onUpdate} cur={cur} />
            )}

            {/* ─── F2. Granular Discounts ─── */}
            {canEditPricing && hasData && (
              <GranularDiscountPanel projectId={projectId} pricing={p} cur={cur} onUpdate={onUpdate} />
            )}

            {/* Fee Override Dialog */}
            {overrideTarget && (
              <FeeOverrideDialog
                open={!!overrideTarget}
                onClose={() => setOverrideTarget(null)}
                projectId={projectId}
                level={overrideTarget.level}
                stage={overrideTarget.stage}
                discipline={overrideTarget.discipline}
                currentFee={overrideTarget.currentFee}
                cur={cur}
                onUpdate={onUpdate}
              />
            )}

            {/* ─── A. Fee by Stage ─── */}
            {p.fee_by_stage && Object.keys(p.fee_by_stage).length > 0 && (() => {
              const stageOverrides = p.fee_overrides?.stages || {};
              const effectiveByStage = p.effective_by_stage || {};
              return (
              <Card className="bg-slate-900 border border-slate-800 shadow-sm" data-testid="fee-by-stage-enhanced">
                <CardHeader className="border-b border-slate-800 bg-slate-800/50 py-3 px-4"><CardTitle className="text-sm font-semibold">Fee by Stage {p.has_overrides && <Badge variant="outline" className="ml-2 border-amber-800 text-amber-400 text-[9px] h-4 px-1.5">Overrides Active</Badge>}</CardTitle></CardHeader>
                <CardContent className="p-0">
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader><TableRow className="bg-slate-800/50">
                        <TableHead className="text-xs font-semibold text-slate-400 px-4 w-5"></TableHead>
                        <TableHead className="text-xs font-semibold text-slate-400 px-3">Stage</TableHead>
                        <TableHead className="text-xs font-semibold text-slate-400 px-3 text-right">Hours</TableHead>
                        <TableHead className="text-xs font-semibold text-slate-500 px-2 text-right">%Hrs</TableHead>
                        <TableHead className="text-xs font-semibold text-blue-400 px-3 text-right"><span className="flex items-center justify-end gap-1"><span className="w-1.5 h-1.5 rounded-full bg-blue-400" />Calculated</span></TableHead>
                        <TableHead className="text-xs font-semibold text-slate-200 px-3 text-right">Effective Fee</TableHead>
                        <TableHead className="text-xs font-semibold text-slate-500 px-2 text-right">%Fee</TableHead>
                        <TableHead className="text-xs font-semibold text-slate-500 px-2 text-center w-10"></TableHead>
                      </TableRow></TableHeader>
                      <TableBody>
                        {Object.entries(p.fee_by_stage).sort(([a], [b]) => a.localeCompare(b)).map(([stage, d]) => {
                          const isExpanded = expandedStages.includes(stage);
                          const hasDetail = d.role_detail && Object.keys(d.role_detail).length > 0;
                          const eff = effectiveByStage[stage];
                          const hasOverride = !!stageOverrides[stage];
                          const effFee = eff?.effective_fee ?? d.final_fee;
                          const discAmt = eff?.discount_amount || 0;
                          return (
                            <React.Fragment key={stage}>
                              <TableRow className={`hover:bg-slate-800/40 transition-colors ${isExpanded ? "bg-slate-800/20" : ""} ${hasOverride ? "border-l-2 border-l-amber-500" : ""}`} data-testid={`stage-row-${stage.replace(/\s/g, "-").toLowerCase()}`}>
                                <TableCell className="px-2 py-2 text-center w-5 cursor-pointer" onClick={() => hasDetail && setExpandedStages(prev => isExpanded ? prev.filter(s => s !== stage) : [...prev, stage])}>{hasDetail && <span className={`text-[10px] text-slate-500 inline-block transition-transform ${isExpanded ? "rotate-90" : ""}`}>&#9654;</span>}</TableCell>
                                <TableCell className="px-3 py-2 text-sm font-medium">
                                  {stage}
                                  {hasOverride && <Badge variant="outline" className="ml-1.5 border-amber-800 text-amber-400 text-[8px] h-3.5 px-1">Override</Badge>}
                                  {discAmt > 0 && <Badge variant="outline" className="ml-1 border-emerald-800 text-emerald-400 text-[8px] h-3.5 px-1">-{cur.symbol}{discAmt.toLocaleString("en-GB")}</Badge>}
                                </TableCell>
                                <TableCell className="px-3 py-2 text-sm font-mono text-right text-slate-400">{d.hours}</TableCell>
                                <TableCell className="px-2 py-2 text-xs font-mono text-right text-slate-600">{d.hours_pct || 0}%</TableCell>
                                <TableCell className="px-3 py-2 text-sm font-mono text-right">
                                  <span className={hasOverride ? "line-through text-slate-600" : "text-slate-300"}>{cur.symbol}{d.final_fee?.toLocaleString("en-GB", { minimumFractionDigits: 0 })}</span>
                                </TableCell>
                                <TableCell className="px-3 py-2 text-sm font-mono text-right font-semibold text-slate-100">{cur.symbol}{effFee?.toLocaleString("en-GB", { minimumFractionDigits: 0 })}</TableCell>
                                <TableCell className="px-2 py-2 text-xs font-mono text-right text-slate-600">{d.fee_pct || 0}%</TableCell>
                                <TableCell className="px-2 py-2 text-center">
                                  <Button variant="ghost" size="sm" className="h-5 w-5 p-0 text-slate-600 hover:text-amber-400" onClick={(e) => { e.stopPropagation(); setOverrideTarget({ level: "stage", stage, currentFee: d.final_fee }); }} data-testid={`override-stage-${stage.replace(/\s/g, "-").toLowerCase()}`}><Pencil className="w-3 h-3" /></Button>
                                </TableCell>
                              </TableRow>
                              {isExpanded && (
                                <TableRow key={`${stage}-detail`} className="bg-slate-800/10">
                                  <TableCell colSpan={8} className="px-4 py-3">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                      {d.role_detail && Object.keys(d.role_detail).length > 0 && (
                                        <div>
                                          <p className="text-[10px] text-slate-500 uppercase tracking-wider font-semibold mb-1.5">Role Breakdown</p>
                                          <div className="space-y-1">
                                            {Object.entries(d.role_detail).sort(([,a],[,b]) => (b.hours || 0) - (a.hours || 0)).map(([role, rd]) => (
                                              <div key={role} className="flex items-center justify-between text-xs">
                                                <span className="text-slate-400">{role}</span>
                                                <div className="flex gap-3">
                                                  <span className="font-mono text-slate-300">{rd.hours}h</span>
                                                  <span className="font-mono text-slate-500 w-20 text-right">{cur.symbol}{rd.fee?.toLocaleString("en-GB", { minimumFractionDigits: 0 })}</span>
                                                </div>
                                              </div>
                                            ))}
                                          </div>
                                        </div>
                                      )}
                                      {d.discipline_hours && Object.keys(d.discipline_hours).length > 0 && (
                                        <div>
                                          <p className="text-[10px] text-slate-500 uppercase tracking-wider font-semibold mb-1.5">Discipline Breakdown</p>
                                          <div className="space-y-1">
                                            {Object.entries(d.discipline_hours).sort(([,a],[,b]) => b - a).map(([disc, hrs]) => {
                                              const sdKey = `${stage}|${disc}`;
                                              const sdOverride = p.fee_overrides?.stage_disciplines?.[sdKey];
                                              const sdEff = p.effective_matrix?.[stage]?.[disc];
                                              return (
                                                <div key={disc} className="flex items-center justify-between text-xs group">
                                                  <span className="text-slate-400">{disc}</span>
                                                  <div className="flex items-center gap-2">
                                                    <span className="font-mono text-slate-300">{hrs}h</span>
                                                    {sdOverride && <span className="font-mono text-amber-400 text-[10px]">→{cur.symbol}{sdOverride.amount?.toLocaleString("en-GB")}</span>}
                                                    {!sdOverride && d.discipline_fees?.[disc] != null && <span className="font-mono text-slate-500 w-20 text-right">{cur.symbol}{d.discipline_fees[disc]?.toLocaleString("en-GB", { minimumFractionDigits: 0 })}</span>}
                                                    <Button variant="ghost" size="sm" className="h-4 w-4 p-0 text-slate-700 hover:text-amber-400 opacity-0 group-hover:opacity-100 transition-opacity" onClick={(e) => { e.stopPropagation(); setOverrideTarget({ level: "stage_discipline", stage, discipline: disc, currentFee: d.discipline_fees?.[disc] || 0 }); }} data-testid={`override-sd-${stage.replace(/\s/g,"-")}-${disc.replace(/\s/g,"-")}`.toLowerCase()}><Pencil className="w-2.5 h-2.5" /></Button>
                                                  </div>
                                                </div>
                                              );
                                            })}
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  </TableCell>
                                </TableRow>
                              )}
                            </React.Fragment>
                          );
                        })}
                        {(() => {
                          const totalEffective = Object.values(effectiveByStage).reduce((s, e) => s + (e?.effective_fee ?? 0), 0) || Object.values(p.fee_by_stage).reduce((s, d) => s + (d.final_fee || 0), 0);
                          return (
                        <TableRow className="border-t-2 border-slate-700 bg-slate-800/30">
                          <TableCell></TableCell>
                          <TableCell className="px-3 py-2 text-sm font-bold">Total</TableCell>
                          <TableCell className="px-3 py-2 text-sm font-mono text-right font-semibold">{Object.values(p.fee_by_stage).reduce((s, d) => s + (d.hours || 0), 0)}</TableCell>
                          <TableCell className="px-2 py-2 text-xs font-mono text-right text-slate-500">100%</TableCell>
                          <TableCell className="px-3 py-2 text-sm font-mono text-right font-semibold text-slate-400">{cur.symbol}{Object.values(p.fee_by_stage).reduce((s, d) => s + (d.final_fee || 0), 0).toLocaleString("en-GB", { minimumFractionDigits: 0 })}</TableCell>
                          <TableCell className="px-3 py-2 text-sm font-mono text-right font-bold text-blue-400">{cur.symbol}{totalEffective.toLocaleString("en-GB", { minimumFractionDigits: 0 })}</TableCell>
                          <TableCell className="px-2 py-2 text-xs font-mono text-right text-slate-500">100%</TableCell>
                          <TableCell className="px-2 py-2 text-center">
                            <Button variant="ghost" size="sm" className="h-5 w-5 p-0 text-slate-600 hover:text-amber-400" onClick={() => setOverrideTarget({ level: "total", currentFee: totalEffective })} data-testid="override-total-btn"><Pencil className="w-3 h-3" /></Button>
                          </TableCell>
                        </TableRow>
                          );
                        })()}
                      </TableBody>
                    </Table>
                  </div>
                </CardContent>
              </Card>
              );
            })()}

            {/* ─── B. Fee by Discipline ─── */}
            {p.fee_by_discipline && Object.keys(p.fee_by_discipline).length > 0 && (
              <Card className="bg-slate-900 border border-slate-800 shadow-sm" data-testid="fee-by-discipline-enhanced">
                <CardHeader className="border-b border-slate-800 bg-slate-800/50 py-3 px-4"><CardTitle className="text-sm font-semibold">Fee by Discipline</CardTitle></CardHeader>
                <CardContent className="p-0">
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader><TableRow className="bg-slate-800/50">
                        <TableHead className="text-xs font-semibold text-slate-400 px-4">Discipline</TableHead>
                        <TableHead className="text-xs font-semibold text-slate-400 px-3 text-right">Hours</TableHead>
                        <TableHead className="text-xs font-semibold text-slate-500 px-2 text-right">%Hrs</TableHead>
                        <TableHead className="text-xs font-semibold text-blue-400 px-3 text-right"><span className="flex items-center justify-end gap-1"><span className="w-1.5 h-1.5 rounded-full bg-blue-400" />Base Fee</span></TableHead>
                        <TableHead className="text-xs font-semibold text-amber-400 px-3 text-right"><span className="flex items-center justify-end gap-1"><span className="w-1.5 h-1.5 rounded-full bg-amber-400" />Contingency</span></TableHead>
                        <TableHead className="text-xs font-semibold text-slate-400 px-3 text-right">Discount</TableHead>
                        <TableHead className="text-xs font-semibold text-slate-200 px-3 text-right">Final Fee</TableHead>
                        <TableHead className="text-xs font-semibold text-slate-500 px-2 text-right">%Fee</TableHead>
                      </TableRow></TableHeader>
                      <TableBody>
                        {Object.entries(p.fee_by_discipline).sort(([a], [b]) => a.localeCompare(b)).map(([disc, d]) => (
                          <TableRow key={disc} data-testid={`discipline-row-${disc.replace(/\s/g, "-").toLowerCase()}`}>
                            <TableCell className="px-4 py-2 text-sm font-medium">{disc}</TableCell>
                            <TableCell className="px-3 py-2 text-sm font-mono text-right text-slate-400">{d.hours}</TableCell>
                            <TableCell className="px-2 py-2 text-xs font-mono text-right text-slate-600">{d.hours_pct || 0}%</TableCell>
                            <TableCell className="px-3 py-2 text-sm font-mono text-right">{cur.symbol}{d.base_fee?.toLocaleString("en-GB", { minimumFractionDigits: 0 })}</TableCell>
                            <TableCell className="px-3 py-2 text-sm font-mono text-right text-amber-400">{d.contingency > 0 ? `+${cur.symbol}${d.contingency?.toLocaleString("en-GB", { minimumFractionDigits: 0 })}` : "—"}</TableCell>
                            <TableCell className="px-3 py-2 text-sm font-mono text-right text-slate-500">{d.discount > 0 ? `-${cur.symbol}${d.discount?.toLocaleString("en-GB", { minimumFractionDigits: 0 })}` : "—"}</TableCell>
                            <TableCell className="px-3 py-2 text-sm font-mono text-right font-semibold text-slate-100">{cur.symbol}{d.final_fee?.toLocaleString("en-GB", { minimumFractionDigits: 0 })}</TableCell>
                            <TableCell className="px-2 py-2 text-xs font-mono text-right text-slate-600">{d.fee_pct || 0}%</TableCell>
                          </TableRow>
                        ))}
                        <TableRow className="border-t-2 border-slate-700 bg-slate-800/30">
                          <TableCell className="px-4 py-2 text-sm font-bold">Total</TableCell>
                          <TableCell className="px-3 py-2 text-sm font-mono text-right font-semibold">{Object.values(p.fee_by_discipline).reduce((s, d) => s + (d.hours || 0), 0)}</TableCell>
                          <TableCell className="px-2 py-2 text-xs font-mono text-right text-slate-500">100%</TableCell>
                          <TableCell className="px-3 py-2 text-sm font-mono text-right font-semibold">{cur.symbol}{Object.values(p.fee_by_discipline).reduce((s, d) => s + (d.base_fee || 0), 0).toLocaleString("en-GB", { minimumFractionDigits: 0 })}</TableCell>
                          <TableCell className="px-3 py-2 text-sm font-mono text-right font-semibold text-amber-400">+{cur.symbol}{Object.values(p.fee_by_discipline).reduce((s, d) => s + (d.contingency || 0), 0).toLocaleString("en-GB", { minimumFractionDigits: 0 })}</TableCell>
                          <TableCell className="px-3 py-2 text-sm font-mono text-right font-semibold text-slate-500">-{cur.symbol}{Object.values(p.fee_by_discipline).reduce((s, d) => s + (d.discount || 0), 0).toLocaleString("en-GB", { minimumFractionDigits: 0 })}</TableCell>
                          <TableCell className="px-3 py-2 text-sm font-mono text-right font-bold text-blue-400">{cur.symbol}{Object.values(p.fee_by_discipline).reduce((s, d) => s + (d.final_fee || 0), 0).toLocaleString("en-GB", { minimumFractionDigits: 0 })}</TableCell>
                          <TableCell className="px-2 py-2 text-xs font-mono text-right text-slate-500">100%</TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* ─── C. Stage × Discipline Matrix ─── */}
            {p.stage_discipline_matrix && Object.keys(p.stage_discipline_matrix).length > 0 && (
              <StageDisciplineMatrix matrix={p.stage_discipline_matrix} cur={cur} />
            )}
          </div>
        </>
      )}

      {/* Contractors Section — always visible */}
      <Card className="bg-slate-900 border border-slate-800 shadow-sm" data-testid="contractors-section">
            <CardHeader className="border-b border-slate-800 bg-slate-800/50 py-3 px-4">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-semibold">Contractors / Sub-Consultants</CardTitle>
                <div className="flex items-center gap-2">
                  {contractors.length > 0 && (
                    <span className="text-xs font-mono text-slate-400">Total: {cur.symbol}{contractorClientTotal.toLocaleString("en-GB", { maximumFractionDigits: 0 })}</span>
                  )}
                  {canEditPricing && <Button size="sm" variant="outline" className="border-slate-800 h-7 text-xs" onClick={() => setShowAddContractor(!showAddContractor)} data-testid="add-contractor-btn"><Plus className="w-3 h-3 mr-1" />Add Contractor</Button>}
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-4 space-y-3">
              {/* Add contractor form */}
              {showAddContractor && (
                <div className="p-3 rounded-lg border border-blue-500/20 bg-blue-500/5 space-y-3" data-testid="add-contractor-form">
                  {contractorLib.length > 0 && (
                    <div>
                      <p className="text-[10px] text-slate-500 uppercase tracking-wider font-semibold mb-1.5">Select from Library</p>
                      <div className="flex flex-wrap gap-1.5">
                        {contractorLib.map(lib => (
                          <button key={lib.id} onClick={() => selectFromLibrary(lib)} className={`text-xs px-2.5 py-1 rounded border transition-colors ${newCon.from_library_id === lib.id ? "border-blue-500 bg-blue-500/20 text-blue-300" : "border-slate-700 bg-slate-800 text-slate-400 hover:border-slate-600"}`} data-testid={`lib-contractor-${lib.id}`}>{lib.name}</button>
                        ))}
                      </div>
                      <div className="border-b border-slate-800 my-2" />
                      <p className="text-[10px] text-slate-500 uppercase tracking-wider font-semibold mb-1.5">Or Add Ad-Hoc</p>
                    </div>
                  )}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div>
                      <Label className="text-[10px] text-slate-500 uppercase">Name</Label>
                      <Input value={newCon.name} onChange={e => setNewCon(p => ({...p, name: e.target.value}))} placeholder="e.g. Acoustic Consultant" className="mt-1 bg-slate-900 border-slate-800 h-8 text-sm" data-testid="contractor-name-input" />
                    </div>
                    <div>
                      <Label className="text-[10px] text-slate-500 uppercase">Cost ({cur.symbol})</Label>
                      <CellInput type="number" value={newCon.cost} onChange={e => setNewCon(p => ({...p, cost: e.target.value}))} placeholder="0" className="mt-1 bg-slate-900 border-slate-800 h-8 font-mono text-sm" data-testid="contractor-cost-input" />
                    </div>
                    <div>
                      <Label className="text-[10px] text-slate-500 uppercase">Markup</Label>
                      <div className="flex items-center gap-2 mt-1">
                        <button onClick={() => setNewCon(p => ({...p, markup_enabled: !p.markup_enabled}))} className={`w-8 h-5 rounded-full transition-colors flex items-center ${newCon.markup_enabled ? "bg-blue-600 justify-end" : "bg-slate-700 justify-start"}`} data-testid="contractor-markup-toggle">
                          <div className="w-3.5 h-3.5 bg-white rounded-full mx-0.5" />
                        </button>
                        {newCon.markup_enabled && <CellInput type="number" value={newCon.markup_percentage} onChange={e => setNewCon(p => ({...p, markup_percentage: parseFloat(e.target.value) || 0}))} placeholder="%" className="w-16 bg-slate-900 border-slate-800 h-8 font-mono text-sm" data-testid="contractor-markup-pct" />}
                        {newCon.markup_enabled && <span className="text-[10px] text-slate-500">%</span>}
                      </div>
                    </div>
                    <div className="flex items-end">
                      <Button size="sm" onClick={addContractor} className="bg-blue-600 hover:bg-blue-500 text-white h-8" data-testid="save-contractor-btn"><Plus className="w-3 h-3 mr-1" />Add</Button>
                    </div>
                  </div>
                </div>
              )}

              {/* Contractor list */}
              {contractors.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-800/30">
                      <TableHead className="text-[10px] uppercase font-semibold text-slate-500 px-3">Contractor</TableHead>
                      <TableHead className="text-[10px] uppercase font-semibold text-slate-500 px-3 text-right">Quoted Cost</TableHead>
                      <TableHead className="text-[10px] uppercase font-semibold text-slate-500 px-3 text-center">Markup</TableHead>
                      <TableHead className="text-[10px] uppercase font-semibold text-slate-500 px-3 text-right">Client Fee</TableHead>
                      <TableHead className="text-[10px] uppercase font-semibold text-slate-500 px-3 text-right">Actual Cost</TableHead>
                      <TableHead className="text-[10px] uppercase font-semibold text-slate-500 px-3 w-16"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {contractors.map(c => (
                      <TableRow key={c.id} data-testid={`project-contractor-${c.id}`}>
                        <TableCell className="px-3 py-2 text-sm font-medium">
                          {c.name}
                          {c.from_library_id && <span className="ml-1.5 text-[9px] text-blue-400 bg-blue-500/10 px-1 py-0.5 rounded">Library</span>}
                        </TableCell>
                        <TableCell className="px-3 py-2 text-right">
                          <CellInput type="number" value={c.cost} onChange={e => {
                            const cost = parseFloat(e.target.value) || 0;
                            const cf = c.markup_enabled ? cost * (1 + (c.markup_percentage || 0) / 100) : cost;
                            const updated = {...c, cost, client_fee: Math.round(cf * 100) / 100};
                            setContractors(prev => prev.map(x => x.id === c.id ? updated : x));
                            updateContractor(updated);
                          }} className="w-24 ml-auto font-mono text-right bg-slate-900 border-slate-800 h-7 text-xs" />
                        </TableCell>
                        <TableCell className="px-3 py-2 text-center">
                          <div className="flex items-center justify-center gap-1.5">
                            <button onClick={() => {
                              const updated = {...c, markup_enabled: !c.markup_enabled, client_fee: !c.markup_enabled ? c.cost * (1 + (c.markup_percentage || 0) / 100) : c.cost };
                              setContractors(prev => prev.map(x => x.id === c.id ? updated : x));
                              updateContractor(updated);
                            }} className={`w-7 h-4 rounded-full transition-colors flex items-center ${c.markup_enabled ? "bg-blue-600 justify-end" : "bg-slate-700 justify-start"}`} data-testid={`toggle-markup-${c.id}`}>
                              <div className="w-2.5 h-2.5 bg-white rounded-full mx-0.5" />
                            </button>
                            {c.markup_enabled && (
                              <CellInput type="number" value={c.markup_percentage} onChange={e => {
                                const mp = parseFloat(e.target.value) || 0;
                                const cf = c.cost * (1 + mp / 100);
                                const updated = {...c, markup_percentage: mp, client_fee: Math.round(cf * 100) / 100};
                                setContractors(prev => prev.map(x => x.id === c.id ? updated : x));
                                updateContractor(updated);
                              }} className="w-14 font-mono text-right bg-slate-900 border-slate-800 h-7 text-xs" />
                            )}
                            {c.markup_enabled && <span className="text-[10px] text-slate-500">%</span>}
                          </div>
                        </TableCell>
                        <TableCell className="px-3 py-2 text-right font-mono text-sm font-semibold text-slate-100">{cur.symbol}{Number(c.client_fee || c.cost || 0).toLocaleString("en-GB", { maximumFractionDigits: 0 })}</TableCell>
                        <TableCell className="px-3 py-2 text-right">
                          <CellInput type="number" value={c.actual_cost || ""} placeholder="—" onChange={async e => {
                            const actual = parseFloat(e.target.value) || 0;
                            setContractors(prev => prev.map(x => x.id === c.id ? {...x, actual_cost: actual} : x));
                            try { await API.put(`/projects/${projectId}/contractors/${c.id}/actual`, { actual_cost: actual }); } catch {}
                          }} className="w-24 ml-auto font-mono text-right bg-slate-900 border-slate-800 h-7 text-xs" data-testid={`actual-cost-${c.id}`} />
                        </TableCell>
                        <TableCell className="px-3 py-2 text-right">
                          <button onClick={() => removeContractor(c.id)} className="text-slate-600 hover:text-rose-400 p-1 rounded transition-colors" data-testid={`remove-contractor-${c.id}`}><Trash2 className="w-3 h-3" /></button>
                        </TableCell>
                      </TableRow>
                    ))}
                    {/* Totals row */}
                    <TableRow className="bg-slate-800/30 font-semibold">
                      <TableCell className="px-3 py-2 text-xs text-slate-400">Total Contractors</TableCell>
                      <TableCell className="px-3 py-2 text-right font-mono text-xs">{cur.symbol}{contractorCostTotal.toLocaleString("en-GB", { maximumFractionDigits: 0 })}</TableCell>
                      <TableCell />
                      <TableCell className="px-3 py-2 text-right font-mono text-xs text-slate-100">{cur.symbol}{contractorClientTotal.toLocaleString("en-GB", { maximumFractionDigits: 0 })}</TableCell>
                      <TableCell className="px-3 py-2 text-right font-mono text-xs text-slate-400">{cur.symbol}{contractors.reduce((s, c) => s + (c.actual_cost || 0), 0).toLocaleString("en-GB", { maximumFractionDigits: 0 })}</TableCell>
                      <TableCell />
                    </TableRow>
                  </TableBody>
                </Table>
              ) : !showAddContractor ? (
                <p className="text-xs text-slate-500 text-center py-4">No contractors added. Click "Add Contractor" to include sub-consultant costs.</p>
              ) : null}

              {/* Grand Total with Contractors */}
              {hasData && contractors.length > 0 && (
                <div className="mt-2 pt-3 border-t border-slate-800">
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-semibold text-slate-300">Project Total (incl. Contractors)</span>
                    <span className="text-lg font-mono font-bold text-slate-100" data-testid="total-with-contractors">
                      {cur.symbol}{((p.total_fee || 0) + contractorClientTotal).toLocaleString("en-GB", { maximumFractionDigits: 0 })}
                    </span>
                  </div>
                  {isDirector && (
                    <div className="flex justify-between items-center mt-1">
                      <span className="text-xs text-slate-500">Total Cost (incl. Contractors)</span>
                      <span className="text-xs font-mono text-slate-400">{cur.symbol}{((p.total_cost || 0) + contractorCostTotal).toLocaleString("en-GB", { maximumFractionDigits: 0 })}</span>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
      {showHistory && snapshots.length > 1 && (
        <Card className="bg-slate-900 border border-slate-800" data-testid="estimate-history-panel">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading text-sm font-semibold text-slate-100">Estimate Evolution</h3>
              {isDirector && (
                <Button variant="outline" size="sm" className="border-rose-500/30 text-rose-400 hover:bg-rose-500/10 hover:text-rose-300 h-7 text-xs" data-testid="clear-all-snapshots-btn"
                  onClick={async () => {
                    const ok = await confirm({ title: "Clear All Snapshots", description: `Clear all ${snapshots.length} pricing snapshots for this project?\n\nThis removes the estimate history and affects Quick Price Guidance data.\n\nThis cannot be undone.`, confirmText: "Clear All", variant: "destructive" });
                    if (!ok) return;
                    try {
                      await API.delete(`/projects/${projectId}/pricing/snapshots`);
                      setSnapshots([]);
                      setShowHistory(false);
                      toast.success("All snapshots cleared");
                    } catch { toast.error("Failed to clear snapshots"); }
                  }}>
                  <Trash2 className="w-3 h-3 mr-1" />Clear All ({snapshots.length})
                </Button>
              )}
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div>
                <p className="text-xs text-slate-500 mb-3 font-medium uppercase tracking-wider">Total Fee Over Time</p>
                <div className="h-52">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={snapshots.map((s, i) => ({
                      name: `v${s.version || i + 1}`,
                      fee: s.total_fee,
                      trigger: s.trigger?.replace(/_/g, " ") || "calc",
                      date: new Date(s.created_at).toLocaleDateString("en-GB", { day: "2-digit", month: "short" })
                    }))}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                      <XAxis dataKey="name" tick={{ fill: "#64748B", fontSize: 11 }} />
                      <YAxis tick={{ fill: "#64748B", fontSize: 11 }} tickFormatter={v => `${cur.symbol}${(v / 1000).toFixed(0)}k`} />
                      <Tooltip
                        contentStyle={{ background: "#fff", border: "1px solid #E2E8F0", borderRadius: 8, fontSize: 12 }}
                        formatter={(v) => [`${cur.symbol}${Number(v).toLocaleString("en-GB", { minimumFractionDigits: 0 })}`, "Fee"]}
                        labelFormatter={(_, payload) => payload?.[0]?.payload?.date ? `${payload[0].payload.date} — ${payload[0].payload.trigger}` : ""}
                      />
                      <Line type="monotone" dataKey="fee" stroke="#F97316" strokeWidth={2.5} dot={{ r: 4, fill: "#F97316", stroke: "#fff", strokeWidth: 2 }} activeDot={{ r: 6 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
              {isDirector && (
                <div>
                  <p className="text-xs text-slate-500 mb-3 font-medium uppercase tracking-wider">Margin % Over Time</p>
                  <div className="h-52">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={snapshots.map((s, i) => ({
                        name: `v${s.version || i + 1}`,
                        margin: s.margin_percentage,
                        date: new Date(s.created_at).toLocaleDateString("en-GB", { day: "2-digit", month: "short" })
                      }))}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                        <XAxis dataKey="name" tick={{ fill: "#64748B", fontSize: 11 }} />
                        <YAxis tick={{ fill: "#64748B", fontSize: 11 }} tickFormatter={v => `${v}%`} />
                        <Tooltip
                          contentStyle={{ background: "#fff", border: "1px solid #E2E8F0", borderRadius: 8, fontSize: 12 }}
                          formatter={(v) => [`${Number(v).toFixed(1)}%`, "Margin"]}
                        />
                        <Line type="monotone" dataKey="margin" stroke="#0F172A" strokeWidth={2.5} dot={{ r: 4, fill: "#0F172A", stroke: "#fff", strokeWidth: 2 }} activeDot={{ r: 6 }} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}
            </div>
            {/* Snapshot table */}
            <div className="mt-4 overflow-x-auto">
              <table className="w-full text-xs" data-testid="snapshot-history-table">
                <thead>
                  <tr className="border-b border-slate-800 text-slate-500">
                    <th className="text-left py-2 font-medium">Version</th>
                    <th className="text-left py-2 font-medium">Trigger</th>
                    <th className="text-right py-2 font-medium">Fee</th>
                    <th className="text-right py-2 font-medium">Hours</th>
                    {isDirector && <th className="text-right py-2 font-medium">Margin %</th>}
                    <th className="text-right py-2 font-medium">Confidence</th>
                    <th className="text-right py-2 font-medium">Date</th>
                    {isDirector && <th className="text-right py-2 font-medium w-8"></th>}
                  </tr>
                </thead>
                <tbody>
                  {snapshots.map((s, i) => (
                    <tr key={s.id || i} className="border-b border-slate-100 hover:bg-slate-800/50 transition-colors group">
                      <td className="py-2 font-mono font-semibold text-slate-100">v{s.version || i + 1}</td>
                      <td className="py-2 text-slate-400 capitalize">{(s.trigger || "calculation").replace(/_/g, " ")}</td>
                      <td className="py-2 text-right font-mono text-slate-100">{cur.symbol}{Number(s.total_fee || 0).toLocaleString("en-GB", { minimumFractionDigits: 0 })}</td>
                      <td className="py-2 text-right font-mono text-slate-400">{Number(s.total_hours || 0).toLocaleString()}</td>
                      {isDirector && <td className="py-2 text-right font-mono text-slate-400">{Number(s.margin_percentage || 0).toFixed(1)}%</td>}
                      <td className="py-2 text-right">
                        <span className={`inline-flex items-center text-[10px] font-semibold px-1.5 py-0.5 rounded ${
                          (s.confidence_score || 0) >= 3.5 ? "bg-emerald-500/10 text-emerald-400" :
                          (s.confidence_score || 0) >= 2 ? "bg-amber-500/10 text-amber-400" : "bg-rose-500/10 text-rose-400"
                        }`}>
                          {Number(s.confidence_score || 0).toFixed(1)}
                        </span>
                      </td>
                      <td className="py-2 text-right text-slate-400">{new Date(s.created_at).toLocaleDateString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}</td>
                      {isDirector && (
                        <td className="py-2 text-right">
                          <button
                            data-testid={`delete-snapshot-${s.id || i}`}
                            disabled={deletingSnapshot === (s.id || i)}
                            className="opacity-0 group-hover:opacity-100 transition-opacity text-slate-600 hover:text-rose-400 p-0.5 rounded"
                            onClick={async () => {
                              const ok = await confirm({ title: "Delete Snapshot", description: `Delete snapshot v${s.version || i + 1} (${cur.symbol}${Number(s.total_fee || 0).toLocaleString("en-GB")})?\n\nThis removes it from the estimate history and Quick Price data.`, confirmText: "Delete", variant: "destructive" });
                              if (!ok) return;
                              setDeletingSnapshot(s.id || i);
                              try {
                                await API.delete(`/projects/${projectId}/pricing/snapshots/${s.id}`);
                                refreshSnapshots();
                                toast.success(`Snapshot v${s.version || i + 1} deleted`);
                              } catch { toast.error("Failed to delete snapshot"); }
                              finally { setDeletingSnapshot(null); }
                            }}>
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
      {!hasData && <Card className="bg-slate-900 border border-slate-800"><CardContent className="p-12 text-center text-sm text-slate-400">No pricing calculated yet. Click "Calculate Pricing" to generate fee breakdown.</CardContent></Card>}
    </div>
  );
}

function RiskTab({ projectId, risk, pricing, onUpdate, permissions }) {
  const canEdit = permissions?.can_edit_risk !== false;
  const pricingHours = pricing?.total_hours || 0;
  const [conf, setConf] = useState({
    drawings_available: risk?.confidence?.drawings_available ?? 0,
    specifications_available: risk?.confidence?.specifications_available ?? 0,
    surveys_available: risk?.confidence?.surveys_available ?? 0,
    programme_clear: risk?.confidence?.programme_clear ?? 0,
  });
  const [tpe, setTpe] = useState(() => {
    const saved = risk?.three_point_estimate || {};
    const ml = saved.most_likely || 0;
    // Auto-populate most_likely from pricing if it's zero and pricing has hours
    return { optimistic: saved.optimistic || 0, most_likely: ml > 0 ? ml : pricingHours, pessimistic: saved.pessimistic || 0 };
  });
  const [mlOverridden, setMlOverridden] = useState(() => {
    const savedMl = risk?.three_point_estimate?.most_likely || 0;
    // If saved value exists AND differs from pricing hours, user has overridden
    return savedMl > 0 && pricingHours > 0 && savedMl !== pricingHours;
  });
  const [risks, setRisks] = useState(risk?.risks || []);
  const [narrative, setNarrative] = useState(risk?.narrative || "");
  const [saving, setSaving] = useState(false);

  // Sync most_likely when pricing changes (if user hasn't overridden)
  useEffect(() => {
    if (pricingHours > 0 && !mlOverridden) {
      setTpe(prev => ({ ...prev, most_likely: pricingHours }));
    }
  }, [pricingHours, mlOverridden]);

  const handleMlChange = (value) => {
    setTpe(prev => ({ ...prev, most_likely: value }));
    // If user types a value different from pricing, mark as overridden
    setMlOverridden(pricingHours > 0 && value !== pricingHours);
  };

  const resetMlToPricing = () => {
    if (pricingHours > 0) {
      setTpe(prev => ({ ...prev, most_likely: pricingHours }));
      setMlOverridden(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      await API.put(`/projects/${projectId}/risk`, { confidence: conf, three_point_estimate: tpe, risks, narrative });
      toast.success("Risk assessment saved");
      onUpdate();
    } catch { toast.error("Failed to save risk"); }
    finally { setSaving(false); }
  };

  const addRisk = () => {
    setRisks([...risks, { id: crypto.randomUUID(), category: "Scope Risk", description: "", impact_hours: 0, probability: "Medium" }]);
  };

  const removeRisk = (id) => setRisks(risks.filter((r) => r.id !== id));
  const updateRisk = (id, key, value) => setRisks(risks.map((r) => r.id === id ? { ...r, [key]: value } : r));

  const confFactors = [
    { key: "drawings_available", label: "Drawings Available" },
    { key: "specifications_available", label: "Specifications Available" },
    { key: "surveys_available", label: "Surveys Available" },
    { key: "programme_clear", label: "Programme Clarity" },
  ];
  const avgScore = confFactors.reduce((sum, f) => sum + (conf[f.key] || 0), 0) / confFactors.length;
  const confLevel = avgScore >= 3.5 ? "High" : avgScore >= 2.0 ? "Medium" : "Low";
  const confColor = confLevel === "High" ? "text-emerald-400 bg-emerald-500/10 border-emerald-500/20" : confLevel === "Medium" ? "text-amber-400 bg-amber-500/10 border-amber-500/20" : "text-rose-400 bg-rose-500/10 border-rose-500/20";
  const pert = tpe.most_likely > 0 ? ((tpe.optimistic + 4 * tpe.most_likely + tpe.pessimistic) / 6).toFixed(1) : "—";
  const totalRiskHours = risks.reduce((sum, r) => sum + (r.impact_hours || 0), 0);

  const scoreLabels = ["0 - None", "1 - Very Low", "2 - Low", "3 - Moderate", "4 - Good", "5 - Comprehensive"];

  return (
    <div className="space-y-6" data-testid="risk-tab">
      <div className="flex items-center justify-between">
        <h2 className="font-heading text-xl font-semibold text-slate-100">Risk & Confidence</h2>
        {canEdit && <Button onClick={save} disabled={saving} data-testid="save-risk-btn" className="bg-blue-600 hover:bg-blue-500 text-white">{saving ? "Saving..." : "Save Assessment"}</Button>}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Confidence */}
        <Card className="bg-slate-900 border border-slate-800 shadow-sm">
          <CardHeader className="border-b border-slate-800 bg-slate-800/50 py-3 px-4">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-semibold">Confidence Scoring (0-5 Scale)</CardTitle>
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold border ${confColor}`} data-testid="confidence-level">{confLevel} ({avgScore.toFixed(1)}/5)</span>
            </div>
          </CardHeader>
          <CardContent className="p-4 space-y-4">
            {confFactors.map(({ key, label }) => (
              <div key={key} data-testid={`conf-${key}`}>
                <div className="flex items-center justify-between mb-1.5">
                  <Label className="text-sm text-slate-300">{label}</Label>
                  <span className="font-mono text-xs font-bold text-slate-500">{conf[key]}/5</span>
                </div>
                <Select value={String(conf[key] || 0)} onValueChange={(v) => setConf(prev => ({ ...prev, [key]: parseInt(v) }))}>
                  <SelectTrigger className="bg-slate-900 border-slate-800 h-9" data-testid={`conf-select-${key}`}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {scoreLabels.map((label, i) => <SelectItem key={`score-${i}`} value={String(i)}>{label}</SelectItem>)}
                  </SelectContent>
                </Select>
                <div className="mt-1 flex gap-0.5">
                  {[0, 1, 2, 3, 4].map(i => (
                    <div key={`conf-seg-${i}`} className={`h-1.5 flex-1 rounded-full transition-colors ${i < conf[key] ? (conf[key] >= 4 ? "bg-emerald-400" : conf[key] >= 2 ? "bg-amber-400" : "bg-red-400") : "bg-slate-800"}`} />
                  ))}
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Three Point Estimate */}
        <Card className="bg-slate-900 border border-slate-800 shadow-sm">
          <CardHeader className="border-b border-slate-800 bg-slate-800/50 py-3 px-4">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-semibold">Three Point Estimate (Hours)</CardTitle>
              {pricingHours > 0 && <span className="text-[10px] text-slate-500 font-mono">Pricing: {pricingHours.toLocaleString()}h</span>}
            </div>
          </CardHeader>
          <CardContent className="p-4 space-y-3">
            {/* Optimistic */}
            <div className="border-l-4 border-l-emerald-400 pl-3">
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Optimistic</Label>
              <CellInput type="number" value={tpe.optimistic || ""} onChange={(e) => setTpe((prev) => ({ ...prev, optimistic: parseFloat(e.target.value) || 0 }))} className="mt-1 font-mono bg-slate-900 border-slate-800" data-testid="tpe-optimistic" />
            </div>
            {/* Most Likely — auto-populated from pricing */}
            <div className="border-l-4 border-l-blue-400 pl-3">
              <div className="flex items-center justify-between">
                <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Most Likely</Label>
                {pricingHours > 0 && !mlOverridden && (
                  <span className="inline-flex items-center gap-1 text-[10px] font-medium text-blue-400 bg-blue-500/10 px-1.5 py-0.5 rounded" data-testid="ml-linked-badge">
                    <Calculator className="w-2.5 h-2.5" />From Pricing
                  </span>
                )}
                {pricingHours > 0 && mlOverridden && (
                  <button onClick={resetMlToPricing} className="inline-flex items-center gap-1 text-[10px] font-medium text-amber-400 hover:text-blue-400 bg-amber-500/10 hover:bg-blue-500/10 px-1.5 py-0.5 rounded transition-colors" data-testid="ml-reset-btn" title="Reset to pricing engine hours">
                    <RefreshCw className="w-2.5 h-2.5" />Reset to {pricingHours.toLocaleString()}h
                  </button>
                )}
              </div>
              <CellInput type="number" value={tpe.most_likely || ""} onChange={(e) => handleMlChange(parseFloat(e.target.value) || 0)} className={`mt-1 font-mono bg-slate-900 ${mlOverridden ? "border-amber-500/40" : "border-slate-800"}`} data-testid="tpe-most_likely" />
            </div>
            {/* Pessimistic */}
            <div className="border-l-4 border-l-red-400 pl-3">
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Pessimistic</Label>
              <CellInput type="number" value={tpe.pessimistic || ""} onChange={(e) => setTpe((prev) => ({ ...prev, pessimistic: parseFloat(e.target.value) || 0 }))} className="mt-1 font-mono bg-slate-900 border-slate-800" data-testid="tpe-pessimistic" />
            </div>
            <div className="pt-2 border-t border-slate-800">
              <div className="flex justify-between"><span className="text-sm text-slate-500">PERT Expected</span><span className="text-sm font-mono font-bold" data-testid="pert-value">{pert}</span></div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Risk Register */}
      <Card className="bg-slate-900 border border-slate-800 shadow-sm">
        <CardHeader className="border-b border-slate-800 bg-slate-800/50 py-3 px-4">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm font-semibold">Risk Register ({totalRiskHours} additional hours)</CardTitle>
            <Button size="sm" onClick={addRisk} data-testid="add-risk-btn" className="bg-blue-600 hover:bg-blue-500 text-white h-8"><Plus className="w-3 h-3 mr-1" />Add Risk</Button>
          </div>
        </CardHeader>
        <CardContent className="p-4 space-y-3">
          {risks.length === 0 && <p className="text-sm text-slate-400 text-center py-4">No risks identified. Click "Add Risk" to begin.</p>}
          {risks.map((r) => (
            <div key={r.id} className="flex gap-3 items-start border border-slate-800 rounded-md p-3" data-testid={`risk-item-${r.id}`}>
              <Select value={r.category} onValueChange={(v) => updateRisk(r.id, "category", v)}>
                <SelectTrigger className="w-48 h-8 text-xs bg-slate-900 border-slate-800"><SelectValue /></SelectTrigger>
                <SelectContent>{RISK_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
              <Input value={r.description} onChange={(e) => updateRisk(r.id, "description", e.target.value)} placeholder="Description" className="flex-1 h-8 text-sm bg-slate-900 border-slate-800" />
              <CellInput type="number" value={r.impact_hours || ""} onChange={(e) => updateRisk(r.id, "impact_hours", parseFloat(e.target.value) || 0)} className="w-24 h-8 font-mono text-xs bg-slate-900 border-slate-800" placeholder="Hours" />
              <Select value={r.probability} onValueChange={(v) => updateRisk(r.id, "probability", v)}>
                <SelectTrigger className="w-28 h-8 text-xs bg-slate-900 border-slate-800"><SelectValue /></SelectTrigger>
                <SelectContent>{["Low", "Medium", "High"].map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
              </Select>
              <button onClick={() => removeRisk(r.id)} className="text-slate-400 hover:text-red-500 mt-1"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Narrative */}
      <Card className="bg-slate-900 border border-slate-800 shadow-sm">
        <CardHeader className="border-b border-slate-800 bg-slate-800/50 py-3 px-4"><CardTitle className="text-sm font-semibold">Risk Narrative (shown to client)</CardTitle></CardHeader>
        <CardContent className="p-4">
          <Textarea value={narrative} onChange={(e) => setNarrative(e.target.value)} rows={4} className="bg-slate-900 border-slate-800" placeholder="Describe the risk profile for this project..." data-testid="risk-narrative" />
        </CardContent>
      </Card>
    </div>
  );
}

function ApprovalTab({ projectId, project, audit, onUpdate, isDirector, permissions }) {
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const statusActions = {
    Draft: [{ action: "submit", label: "Submit for Review", color: "bg-blue-600 hover:bg-blue-700" }],
    "Ready for Review": [{ action: "start_review", label: "Start Review", color: "bg-amber-600 hover:bg-amber-700" }, { action: "revert_draft", label: "Revert to Draft", color: "bg-slate-600 hover:bg-slate-700" }],
    "Under Review": [
      { action: "approve", label: "Approve", color: "bg-emerald-500 hover:bg-emerald-700" },
      { action: "reject", label: "Reject", color: "bg-rose-500 hover:bg-red-700" },
      { action: "request_revision", label: "Request Revision", color: "bg-orange-600 hover:bg-orange-700" },
    ],
    "Revision Required": [{ action: "submit", label: "Resubmit for Review", color: "bg-blue-600 hover:bg-blue-700" }, { action: "revert_draft", label: "Revert to Draft", color: "bg-slate-600 hover:bg-slate-700" }],
    Approved: [{ action: "issue", label: "Issue to Client", color: "bg-indigo-600 hover:bg-indigo-700" }, { action: "close", label: "Close Project", color: "bg-slate-600 hover:bg-slate-700" }],
    Rejected: [{ action: "revert_draft", label: "Revert to Draft", color: "bg-slate-600 hover:bg-slate-700" }],
    Issued: [{ action: "close", label: "Close Project", color: "bg-slate-600 hover:bg-slate-700" }],
    Closed: [],
  };

  const handleAction = async (action) => {
    setSubmitting(true);
    try {
      await API.post(`/projects/${projectId}/approval`, { action, comment });
      toast.success("Status updated");
      setComment("");
      onUpdate();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Action failed");
    } finally {
      setSubmitting(false);
    }
  };

  const actions = statusActions[project.status] || [];

  return (
    <div className="space-y-6" data-testid="approval-tab">
      <h2 className="font-heading text-xl font-semibold text-slate-100">Approval Workflow</h2>

      <Card className="bg-slate-900 border border-slate-800 shadow-sm">
        <CardContent className="p-6">
          <div className="flex items-center gap-4 mb-6">
            <span className="text-sm text-slate-500">Current Status:</span>
            <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-bold border ${STATUS_COLORS[project.status]}`} data-testid="approval-current-status">{project.status}</span>
          </div>

          {actions.length > 0 && (
            <div className="space-y-4">
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Comment (optional)</Label>
                <Textarea value={comment} onChange={(e) => setComment(e.target.value)} rows={2} className="mt-1.5 bg-slate-900 border-slate-800" placeholder="Add a comment..." data-testid="approval-comment" />
              </div>
              <div className="flex gap-3 flex-wrap">
                {actions.map((a) => {
                  // Gate approval actions by permission
                  const isApprovalAction = ["approve", "reject", "request_revision", "issue", "close", "start_review"].includes(a.action);
                  const isSubmitAction = a.action === "submit";
                  const isRevertAction = a.action === "revert_draft";
                  if (isApprovalAction && !permissions?.can_approve) return null;
                  if (isSubmitAction && !permissions?.can_submit_review) return null;
                  if (isRevertAction && !permissions?.can_submit_review && !permissions?.can_approve) return null;
                  return (
                    <Button key={a.action} onClick={() => handleAction(a.action)} disabled={submitting} className={`${a.color} text-white`} data-testid={`approval-${a.action}`}>
                      <Send className="w-4 h-4 mr-2" />{a.label}
                    </Button>
                  );
                })}
                {permissions?.can_approve && project.status !== "Approved" && project.status !== "Issued" && project.status !== "Closed" && (
                  <Button onClick={() => handleAction("override")} disabled={submitting} className="bg-purple-600 hover:bg-purple-700 text-white border-2 border-purple-400" data-testid="approval-override">
                    <AlertTriangle className="w-4 h-4 mr-2" />Director Override
                  </Button>
                )}
              </div>
            </div>
          )}
          {actions.length === 0 && <p className="text-sm text-slate-400">No actions available for current status.</p>}
        </CardContent>
      </Card>

      {/* Audit Trail */}
      <Card className="bg-slate-900 border border-slate-800 shadow-sm">
        <CardHeader className="border-b border-slate-800 bg-slate-800/50 py-3 px-4"><CardTitle className="text-sm font-semibold">Audit Trail</CardTitle></CardHeader>
        <CardContent className="p-4">
          {audit.length > 0 ? (
            <div className="space-y-3">
              {audit.map((a, i) => (
                <div key={a.id || `audit-${a.timestamp}-${i}`} className="flex gap-3 items-start border-l-2 border-slate-800 pl-3" data-testid={`audit-item-${i}`}>
                  <div>
                    <p className="text-sm text-slate-300">{a.description}</p>
                    <p className="font-mono text-xs text-slate-400 mt-0.5">{new Date(a.timestamp).toLocaleString("en-GB")}</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-slate-400 text-center py-4">No audit records</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function ProposalsTab({ projectId, proposals, project, onUpdate, permissions }) {
  const confirm = useConfirm();
  const canGenerate = permissions?.can_generate_proposal !== false;
  const [generating, setGenerating] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({ intro_text: "", closing_text: "", draft_watermark: false });
  const [shares, setShares] = useState({});
  const [sharingId, setSharingId] = useState(null);
  const BACKEND = process.env.REACT_APP_BACKEND_URL;

  const generate = async () => {
    setGenerating(true);
    try {
      await API.post(`/projects/${projectId}/proposals`);
      toast.success("Proposal generated");
      onUpdate();
    } catch (e) {
      toast.error("Failed to generate proposal");
    } finally {
      setGenerating(false);
    }
  };

  const startEdit = (p) => {
    setEditingId(p.id);
    setEditData({ intro_text: p.intro_text || "", closing_text: p.closing_text || "", draft_watermark: p.draft_watermark || false });
  };

  const saveEdit = async () => {
    try {
      await API.put(`/proposals/${editingId}`, editData);
      toast.success("Proposal updated");
      setEditingId(null);
      onUpdate();
    } catch { toast.error("Failed to update"); }
  };

  const deleteProposal = async (proposal) => {
    const ok = await confirm({ title: "Delete Proposal", description: `Delete Proposal v${proposal.version}?\n\nThis cannot be undone.`, confirmText: "Delete", variant: "destructive" });
    if (!ok) return;
    try {
      await API.delete(`/proposals/${proposal.id}`);
      toast.success(`Proposal v${proposal.version} deleted`);
      onUpdate();
    } catch { toast.error("Failed to delete proposal"); }
  };

  const downloadProposal = async (proposalId, format) => {
    try {
      const res = await API.get(`/proposals/${proposalId}/download/${format}`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: format === "pdf" ? "application/pdf" : "application/vnd.openxmlformats-officedocument.wordprocessingml.document" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `proposal-v${proposals.find(p => p.id === proposalId)?.version || 1}.${format === "pdf" ? "pdf" : "docx"}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch { toast.error(`Failed to download ${format.toUpperCase()}`); }
  };

  // Share link management
  const createShareLink = async (proposalId) => {
    try {
      const r = await API.post(`/proposals/${proposalId}/share`, { proposal_id: proposalId, expires_days: 30 });
      toast.success("Share link created");
      // Copy to clipboard
      await navigator.clipboard.writeText(r.data.share_url);
      toast.info("Link copied to clipboard");
      loadShares(proposalId);
      setSharingId(proposalId);
    } catch { toast.error("Failed to create share link"); }
  };

  const loadShares = async (proposalId) => {
    try {
      const r = await API.get(`/proposals/${proposalId}/shares`);
      setShares(prev => ({ ...prev, [proposalId]: r.data.shares }));
    } catch {}
  };

  const revokeShare = async (shareId, proposalId) => {
    const ok = await confirm({ title: "Revoke Share Link", description: "This link will stop working immediately. Anyone with it will no longer be able to view the proposal.", confirmText: "Revoke", variant: "danger" });
    if (!ok) return;
    try {
      await API.delete(`/proposals/shares/${shareId}`);
      toast.success("Link revoked");
      loadShares(proposalId);
    } catch { toast.error("Failed to revoke"); }
  };

  const copyLink = async (url) => {
    await navigator.clipboard.writeText(url);
    toast.info("Link copied to clipboard");
  };

  return (
    <div className="space-y-6" data-testid="proposals-tab">
      {/* Rates Changed Warning */}
      {project?.rates_changed_at && proposals.length > 0 && (
        <div className="flex items-start gap-3 p-3 rounded-lg border border-amber-500/30 bg-amber-500/5" data-testid="rates-stale-warning">
          <AlertTriangle className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-amber-300">Rates Changed Since Last Proposal</p>
            <p className="text-xs text-amber-400/70 mt-0.5">
              Staff level rates were updated on {new Date(project.rates_changed_at).toLocaleDateString("en-GB")}. Existing proposals below were generated with previous rates. Recalculate pricing and generate a new proposal to reflect the latest rates.
            </p>
          </div>
        </div>
      )}

      <div className="flex items-center justify-between">
        <h2 className="font-heading text-xl font-semibold text-slate-100">Proposals</h2>
        {canGenerate && (
          <Button onClick={generate} disabled={generating} data-testid="generate-proposal-btn" className="bg-blue-600 hover:bg-blue-500/10 text-white font-bold">
            <FileText className="w-4 h-4 mr-2" />{generating ? "Generating..." : "Generate Proposal"}
          </Button>
        )}
      </div>

      {proposals.length === 0 ? (
        <Card className="bg-slate-900 border border-slate-800"><CardContent className="p-12 text-center text-sm text-slate-400">No proposals generated yet.</CardContent></Card>
      ) : (
        <div className="space-y-3">
          {proposals.map((p) => (
            <Card key={p.id} className="bg-slate-900 border border-slate-800 shadow-sm" data-testid={`proposal-${p.id}`}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div>
                      <p className="text-sm font-medium text-slate-100">Version {p.version}</p>
                      <p className="font-mono text-xs text-slate-400">{new Date(p.created_at).toLocaleString("en-GB")}</p>
                    </div>
                    {p.draft_watermark && <Badge className="bg-rose-500/10 text-rose-400 border-rose-500/20 text-xs">DRAFT</Badge>}
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    <Button size="sm" variant="outline" className="border-slate-800 h-8" onClick={() => startEdit(p)} data-testid={`edit-proposal-${p.id}`}>Edit Text</Button>
                    <Button size="sm" variant="outline" className="border-slate-800 h-8" onClick={() => downloadProposal(p.id, "pdf")} data-testid={`download-pdf-${p.id}`}><Download className="w-3 h-3 mr-1" />PDF</Button>
                    <Button size="sm" variant="outline" className="border-slate-800 h-8" onClick={() => downloadProposal(p.id, "docx")} data-testid={`download-docx-${p.id}`}><Download className="w-3 h-3 mr-1" />Word</Button>
                    <Button size="sm" variant="outline" className="border-blue-500/30 text-blue-400 hover:bg-blue-500/10 h-8" onClick={() => { if (sharingId === p.id) { setSharingId(null); } else { loadShares(p.id); setSharingId(p.id); } }} data-testid={`share-proposal-${p.id}`}>
                      <Share2 className="w-3 h-3 mr-1" />Share
                    </Button>
                    <Button size="sm" variant="outline" className="border-rose-500/30 text-rose-400 hover:bg-rose-500/10 hover:text-rose-300 h-8" onClick={() => deleteProposal(p)} data-testid={`delete-proposal-${p.id}`}><Trash2 className="w-3 h-3" /></Button>
                  </div>
                </div>

                {editingId === p.id && (
                  <div className="mt-4 space-y-4 border-t border-slate-800 pt-4" data-testid="proposal-editor">
                    <div>
                      <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Introduction Text</Label>
                      <Textarea value={editData.intro_text} onChange={(e) => setEditData(prev => ({ ...prev, intro_text: e.target.value }))} rows={3} className="mt-1.5 bg-slate-900 border-slate-800" data-testid="proposal-intro-text" />
                    </div>
                    <div>
                      <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Closing Text</Label>
                      <Textarea value={editData.closing_text} onChange={(e) => setEditData(prev => ({ ...prev, closing_text: e.target.value }))} rows={3} className="mt-1.5 bg-slate-900 border-slate-800" data-testid="proposal-closing-text" />
                    </div>
                    <div className="flex items-center justify-between">
                      <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="draft-watermark-toggle">
                        <Checkbox checked={editData.draft_watermark} onCheckedChange={(v) => setEditData(prev => ({ ...prev, draft_watermark: !!v }))} />
                        <span className="text-slate-400">Include DRAFT watermark</span>
                      </label>
                      <div className="flex gap-2">
                        <Button size="sm" variant="outline" onClick={() => setEditingId(null)} className="border-slate-800 h-8">Cancel</Button>
                        <Button size="sm" onClick={saveEdit} className="bg-blue-600 hover:bg-blue-500 text-white h-8" data-testid="save-proposal-text-btn">Save Changes</Button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Share Links Panel */}
                {sharingId === p.id && (
                  <div className="mt-4 border-t border-slate-800 pt-4 space-y-3" data-testid={`share-panel-${p.id}`}>
                    <div className="flex items-center justify-between">
                      <h4 className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Share Links</h4>
                      <Button size="sm" onClick={() => createShareLink(p.id)} className="bg-blue-600 hover:bg-blue-500 text-white h-7 text-xs" data-testid={`create-share-link-${p.id}`}>
                        <Link2 className="w-3 h-3 mr-1" />Create Link
                      </Button>
                    </div>
                    {(shares[p.id] || []).length === 0 ? (
                      <p className="text-xs text-slate-500 text-center py-3">No share links yet. Create one to send a read-only proposal view to your client.</p>
                    ) : (
                      <div className="space-y-2">
                        {(shares[p.id] || []).map(s => {
                          const isExpired = s.expires_at && new Date(s.expires_at) < new Date();
                          const isActive = s.active && !isExpired;
                          const shareUrl = `${window.location.origin}/shared/proposal/${s.share_token}`;
                          return (
                            <div key={s.id} className={`flex items-center gap-3 p-2.5 rounded-lg border ${isActive ? "border-blue-500/20 bg-blue-950/20" : "border-slate-800 bg-slate-900/50 opacity-60"}`} data-testid={`share-link-${s.id}`}>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                  {isActive ? <Globe className="w-3.5 h-3.5 text-blue-400 shrink-0" /> : <XCircle className="w-3.5 h-3.5 text-slate-600 shrink-0" />}
                                  <code className="text-xs text-slate-300 font-mono truncate block">{shareUrl}</code>
                                </div>
                                <div className="flex items-center gap-3 mt-1 text-[10px] text-slate-500">
                                  <span>{s.view_count || 0} view{s.view_count !== 1 ? "s" : ""}</span>
                                  {s.last_viewed_at && <span>Last viewed {new Date(s.last_viewed_at).toLocaleDateString("en-GB")}</span>}
                                  <span>Expires {new Date(s.expires_at).toLocaleDateString("en-GB")}</span>
                                  {!s.active && <Badge variant="outline" className="text-[9px] border-rose-800 text-rose-400">Revoked</Badge>}
                                  {isExpired && s.active && <Badge variant="outline" className="text-[9px] border-amber-800 text-amber-400">Expired</Badge>}
                                </div>
                              </div>
                              {isActive && (
                                <div className="flex gap-1.5 shrink-0">
                                  <Button size="sm" variant="outline" className="border-slate-700 h-7 px-2 text-xs" onClick={() => copyLink(shareUrl)} data-testid={`copy-share-${s.id}`}>
                                    <Copy className="w-3 h-3" />
                                  </Button>
                                  <Button size="sm" variant="outline" className="border-rose-500/30 text-rose-400 hover:bg-rose-500/10 h-7 px-2 text-xs" onClick={() => revokeShare(s.id, p.id)} data-testid={`revoke-share-${s.id}`}>
                                    <XCircle className="w-3 h-3" />
                                  </Button>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

function FilesTab({ projectId, files, onUpdate, permissions }) {
  const canUpload = permissions?.can_upload_files !== false;
  const [uploading, setUploading] = useState(false);
  const BACKEND = process.env.REACT_APP_BACKEND_URL;

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      await API.post(`/upload/${projectId}`, formData);
      toast.success("File uploaded");
      onUpdate();
    } catch (e) {
      const detail = e.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : (e.message || "Upload failed");
      toast.error(msg);
    }
    finally { setUploading(false); e.target.value = ""; }
  };

  const handleDelete = async (fileId) => {
    try {
      await API.delete(`/files/${fileId}`);
      toast.success("File deleted");
      onUpdate();
    } catch { toast.error("Failed to delete file"); }
  };

  return (
    <div className="space-y-6" data-testid="files-tab">
      <div className="flex items-center justify-between">
        <h2 className="font-heading text-xl font-semibold text-slate-100">Project Files</h2>
        {canUpload && (
          <label className="cursor-pointer">
            <Button disabled={uploading} className="bg-blue-600 hover:bg-blue-500 text-white" data-testid="upload-file-btn" asChild>
              <span><Upload className="w-4 h-4 mr-2" />{uploading ? "Uploading..." : "Upload File"}</span>
            </Button>
            <input type="file" className="hidden" onChange={handleUpload} data-testid="file-input" />
          </label>
        )}
      </div>

      {files.length === 0 ? (
        <Card className="bg-slate-900 border border-slate-800"><CardContent className="p-12 text-center text-sm text-slate-400">No files uploaded yet.</CardContent></Card>
      ) : (
        <Card className="bg-slate-900 border border-slate-800 shadow-sm overflow-hidden">
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-800/50">
                  <TableHead className="text-xs uppercase tracking-wider font-semibold text-slate-500 px-4">Filename</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider font-semibold text-slate-500 px-4">Type</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider font-semibold text-slate-500 px-4">Size</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider font-semibold text-slate-500 px-4">Uploaded</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider font-semibold text-slate-500 px-4 w-24"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {files.map((f) => (
                  <TableRow key={f.id} data-testid={`file-row-${f.id}`}>
                    <TableCell className="px-4 py-3 text-sm font-medium">{f.original_filename}</TableCell>
                    <TableCell className="px-4 py-3 text-xs text-slate-500">{f.content_type}</TableCell>
                    <TableCell className="px-4 py-3 font-mono text-xs text-slate-500">{(f.size / 1024).toFixed(1)} KB</TableCell>
                    <TableCell className="px-4 py-3 font-mono text-xs text-slate-500">{new Date(f.created_at).toLocaleDateString("en-GB")}</TableCell>
                    <TableCell className="px-4 py-3 flex gap-2">
                      <a href={`${BACKEND}/api/files/${f.id}/download`} target="_blank" rel="noopener noreferrer"><Download className="w-4 h-4 text-slate-400 hover:text-blue-500" /></a>
                      <button onClick={() => handleDelete(f.id)} data-testid={`delete-file-${f.id}`}><Trash2 className="w-4 h-4 text-slate-400 hover:text-red-500" /></button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}


function PricingRequestsTab({ projectId, project, scope, onUpdate, permissions, activeRoles = [], focusedRequestId, setFocusedRequestId }) {
  const { user, isDirector } = useAuth();
  const confirm = useConfirm();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showDetail, setShowDetail] = useState(null);
  const [detailData, setDetailData] = useState(null);
  const [sending, setSending] = useState(false);

  // Auto-open focused request from URL param
  useEffect(() => {
    if (focusedRequestId && requests.length > 0) {
      setShowDetail(focusedRequestId);
      if (setFocusedRequestId) setFocusedRequestId(null); // clear so it doesn't re-trigger
    }
  }, [focusedRequestId, requests, setFocusedRequestId]);

  // Create form
  const [discipline, setDiscipline] = useState("");
  const [targetRole, setTargetRole] = useState("");
  const [recipientName, setRecipientName] = useState("");
  const [recipientEmail, setRecipientEmail] = useState("");
  const [isInternal, setIsInternal] = useState(false);
  const [expiryHours, setExpiryHours] = useState(48);
  const [message, setMessage] = useState("");
  const [selectedItems, setSelectedItems] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [emailConfigured, setEmailConfigured] = useState(null);

  const disciplines = project?.disciplines || ["Mechanical", "Electrical", "Public Health"];
  const scopeItems = scope?.items?.filter(i => i.included !== false) || [];

  const fetchRequests = useCallback(async () => {
    try {
      const r = await API.get(`/projects/${projectId}/pricing-requests`);
      setRequests(r.data.requests || []);
    } catch { /* ignore */ } finally { setLoading(false); }
  }, [projectId]);

  useEffect(() => { fetchRequests(); API.get("/email/status").then(r => setEmailConfigured(r.data.configured)).catch(() => {}); }, [projectId, fetchRequests]);

  const filteredItems = scopeItems.filter(i => i.discipline === discipline);

  const toggleItem = (id) => {
    setSelectedItems(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };
  const selectAll = () => {
    setSelectedItems(filteredItems.map(i => i.id));
  };

  const handleCreate = async () => {
    if (!discipline || !targetRole || !recipientName || !recipientEmail || selectedItems.length === 0) {
      toast.error("Please fill in all fields and select at least one deliverable");
      return;
    }
    try {
      const res = await API.post(`/projects/${projectId}/pricing-requests`, {
        discipline, target_role: targetRole, recipient_name: recipientName, recipient_email: recipientEmail,
        is_internal: isInternal, selected_item_ids: selectedItems,
        expiry_hours: expiryHours, message,
      });
      toast.success("Pricing request created");
      setShowCreate(false);
      resetForm();
      fetchRequests();
      // Open detail to upload files
      setShowDetail(res.data.id);
      loadDetail(res.data.id);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create request");
    }
  };

  const resetForm = () => {
    setDiscipline(""); setTargetRole(""); setRecipientName(""); setRecipientEmail("");
    setIsInternal(false); setExpiryHours(48); setMessage(""); setSelectedItems([]);
  };

  const loadDetail = async (id) => {
    try {
      const r = await API.get(`/pricing-requests/${id}`);
      setDetailData(r.data);
    } catch { toast.error("Failed to load request details"); }
  };

  useEffect(() => { if (showDetail) loadDetail(showDetail); }, [showDetail]);

  const handleSend = async (id) => {
    setSending(true);
    try {
      const r = await API.post(`/pricing-requests/${id}/send`);
      toast.success(r.data.email_sent ? "Request sent via email" : "Request activated (email not configured)");
      fetchRequests();
      if (showDetail === id) loadDetail(id);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send");
    } finally { setSending(false); }
  };

  const handleExtend = async (id) => {
    try {
      await API.post(`/pricing-requests/${id}/extend`, { extra_hours: 48 });
      toast.success("Expiry extended by 48 hours");
      fetchRequests();
      if (showDetail === id) loadDetail(id);
    } catch { toast.error("Failed to extend"); }
  };

  const handleDelete = async (id) => {
    const ok = await confirm("Delete this pricing request?");
    if (!ok) return;
    try {
      await API.delete(`/pricing-requests/${id}`);
      toast.success("Request deleted");
      setShowDetail(null);
      fetchRequests();
    } catch { toast.error("Failed to delete"); }
  };

  const handleUploadFile = async (id, files) => {
    setUploading(true);
    let uploaded = 0;
    for (const file of Array.from(files)) {
      const form = new FormData();
      form.append("file", file);
      try {
        await API.post(`/pricing-requests/${id}/upload-file`, form, { timeout: 600000 });
        uploaded++;
      } catch (e) {
        const detail = e.response?.data?.detail;
        const msg = typeof detail === 'string' ? detail : (e.message || "Upload error");
        toast.error(`Upload failed: ${file.name} — ${msg}`);
      }
    }
    if (uploaded > 0) toast.success(`${uploaded} file(s) uploaded`);
    loadDetail(id);
    setUploading(false);
  };

  const handleDeleteFile = async (reqId, fileId) => {
    try {
      await API.delete(`/pricing-requests/${reqId}/files/${fileId}`);
      toast.success("File removed");
      loadDetail(reqId);
    } catch { toast.error("Failed to remove file"); }
  };

  const handleAccept = async (id, mode) => {
    try {
      const r = await API.post(`/pricing-requests/${id}/accept`, { mode });
      toast.success(mode === "auto" ? `${r.data.merged_count} item(s) merged into scope` : "Response accepted");
      fetchRequests();
      loadDetail(id);
      onUpdate();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to accept");
    }
  };

  const handleReject = async (id) => {
    const reason = prompt("Reason for revision (sent to the engineer):");
    if (reason === null) return;
    try {
      await API.post(`/pricing-requests/${id}/reject`, { reason, reopen: true });
      toast.success("Revision requested — link reopened");
      fetchRequests();
      loadDetail(id);
    } catch { toast.error("Failed to request revision"); }
  };

  const handleAddNewItems = async (id, itemIds) => {
    try {
      await API.post(`/pricing-requests/${id}/add-items`, { item_ids: itemIds });
      toast.success("Items added to request");
      loadDetail(id);
    } catch { toast.error("Failed to add items"); }
  };

  const STATUS_BADGE = {
    Draft: "bg-slate-800 text-slate-400 border-slate-700",
    Sent: "bg-blue-500/10 text-blue-400 border-blue-500/20",
    Viewed: "bg-cyan-500/10 text-cyan-400 border-cyan-500/20",
    InProgress: "bg-amber-500/10 text-amber-400 border-amber-500/20",
    Submitted: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20",
    Accepted: "bg-green-500/10 text-green-400 border-green-500/20",
    Revised: "bg-orange-500/10 text-orange-400 border-orange-500/20",
  };

  if (loading) return <div className="animate-pulse h-40 bg-slate-800 rounded-lg" />;

  return (
    <div className="space-y-5" data-testid="pricing-requests-tab">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-slate-100">Pricing Requests</h3>
          <p className="text-xs text-slate-500 mt-0.5">Send effort pro formas to engineers for discipline-specific pricing</p>
        </div>
        <Button size="sm" onClick={() => { resetForm(); setShowCreate(true); }} className="bg-blue-600 hover:bg-blue-700" data-testid="create-pricing-request-btn">
          <Plus className="w-3.5 h-3.5 mr-1.5" />New Request
        </Button>
      </div>

      {/* Requests List */}
      {requests.length === 0 ? (
        <Card className="bg-slate-900/40 border-slate-800">
          <CardContent className="p-8 text-center">
            <Mail className="w-10 h-10 text-slate-700 mx-auto mb-3" />
            <p className="text-sm text-slate-500">No pricing requests yet. Create one to collect effort estimates from engineers.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {requests.map(req => (
            <Card key={req.id} className="bg-slate-900/40 border-slate-800 hover:border-slate-700 transition-colors cursor-pointer" onClick={() => { setShowDetail(req.id); }} data-testid={`pricing-request-${req.id}`}>
              <CardContent className="p-3 flex items-center justify-between gap-3">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-8 h-8 rounded-full bg-slate-800 flex items-center justify-center flex-shrink-0">
                    <Mail className="w-4 h-4 text-slate-500" />
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-slate-200 truncate">{req.recipient_name}</span>
                      <Badge variant="outline" className="text-xs border-blue-500/30 text-blue-400">{req.discipline}</Badge>
                      <Badge variant="outline" className="text-xs border-emerald-500/30 text-emerald-400">{req.target_role || "All"}</Badge>
                      <Badge variant="outline" className={`text-xs ${STATUS_BADGE[req.status] || STATUS_BADGE.Draft}`}>{req.status}</Badge>
                    </div>
                    <p className="text-xs text-slate-500 truncate">{req.recipient_email} — {req.selected_item_ids?.length || 0} deliverables</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  {req.status === "Draft" && (
                    <Button variant="outline" size="sm" className="border-blue-500/30 text-blue-400 text-xs" onClick={e => { e.stopPropagation(); handleSend(req.id); }} disabled={sending} data-testid={`send-request-${req.id}`}>
                      <Send className="w-3 h-3 mr-1" />Send
                    </Button>
                  )}
                  {["Sent", "Viewed"].includes(req.status) && (
                    <Button variant="ghost" size="sm" className="text-xs text-slate-400" onClick={e => { e.stopPropagation(); handleExtend(req.id); }}>
                      <RotateCcw className="w-3 h-3 mr-1" />Extend
                    </Button>
                  )}
                  {req.scope_changes?.length > 0 && (
                    <Badge className="bg-amber-500/10 text-amber-400 border-amber-500/20 text-xs">Scope Changed</Badge>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="bg-slate-900 border-slate-800 max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-slate-100">Create Pricing Request</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {emailConfigured === false && (
              <div className="bg-amber-950/20 border border-amber-500/20 rounded-md px-3 py-2">
                <p className="text-xs text-amber-400"><AlertTriangle className="w-3 h-3 inline mr-1" />Email not configured. Requests will generate a link only. Configure email in Admin Settings.</p>
              </div>
            )}
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="text-xs font-medium text-slate-400 block mb-1">Discipline</label>
                <Select value={discipline} onValueChange={v => { setDiscipline(v); setSelectedItems([]); }}>
                  <SelectTrigger className="bg-slate-800 border-slate-700 text-slate-200" data-testid="select-discipline">
                    <SelectValue placeholder="Select discipline" />
                  </SelectTrigger>
                  <SelectContent>
                    {disciplines.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-medium text-slate-400 block mb-1">Role Level</label>
                <Select value={targetRole} onValueChange={setTargetRole}>
                  <SelectTrigger className="bg-slate-800 border-slate-700 text-slate-200" data-testid="select-role">
                    <SelectValue placeholder="Select role" />
                  </SelectTrigger>
                  <SelectContent>
                    {activeRoles.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-medium text-slate-400 block mb-1">Expiry</label>
                <Select value={String(expiryHours)} onValueChange={v => setExpiryHours(parseInt(v))}>
                  <SelectTrigger className="bg-slate-800 border-slate-700 text-slate-200" data-testid="select-expiry">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="24">24 hours</SelectItem>
                    <SelectItem value="48">48 hours</SelectItem>
                    <SelectItem value="72">72 hours</SelectItem>
                    <SelectItem value="168">1 week</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-medium text-slate-400 block mb-1">Recipient Name</label>
                <Input value={recipientName} onChange={e => setRecipientName(e.target.value)} placeholder="e.g. John Smith" className="bg-slate-800 border-slate-700 text-slate-200" data-testid="recipient-name" />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-400 block mb-1">Recipient Email</label>
                <Input value={recipientEmail} onChange={e => setRecipientEmail(e.target.value)} placeholder="john@example.com" type="email" className="bg-slate-800 border-slate-700 text-slate-200" data-testid="recipient-email" />
              </div>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-400 block mb-1">Message (optional)</label>
              <Textarea value={message} onChange={e => setMessage(e.target.value)} placeholder="Any notes for the engineer..." className="bg-slate-800 border-slate-700 text-slate-200 min-h-[60px]" data-testid="request-message" />
            </div>

            {/* Deliverable Selection — Grouped by Stage */}
            {discipline && (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-xs font-medium text-slate-400">Select Deliverables ({selectedItems.length}/{filteredItems.length})</label>
                  <Button variant="ghost" size="sm" onClick={selectAll} className="text-xs text-blue-400" data-testid="select-all-items">Select All</Button>
                </div>
                <div className="max-h-60 overflow-y-auto border border-slate-800 rounded-lg">
                  {filteredItems.length === 0 ? (
                    <p className="p-4 text-xs text-slate-500 text-center">No deliverables found for {discipline}. Build scope first.</p>
                  ) : (
                    (() => {
                      const stageGroups = {};
                      filteredItems.forEach(item => {
                        if (!stageGroups[item.stage]) stageGroups[item.stage] = [];
                        stageGroups[item.stage].push(item);
                      });
                      const sortedStages = Object.keys(stageGroups).sort();
                      return sortedStages.map(stage => {
                        const stageItems = stageGroups[stage];
                        const allSelected = stageItems.every(i => selectedItems.includes(i.id));
                        const toggleStage = () => {
                          if (allSelected) {
                            setSelectedItems(prev => prev.filter(id => !stageItems.some(i => i.id === id)));
                          } else {
                            setSelectedItems(prev => [...new Set([...prev, ...stageItems.map(i => i.id)])]);
                          }
                        };
                        return (
                          <div key={stage}>
                            <div className="flex items-center justify-between bg-slate-800/60 px-3 py-1.5 border-b border-slate-800/50 sticky top-0 z-10">
                              <div className="flex items-center gap-2">
                                <Checkbox checked={allSelected} onCheckedChange={toggleStage} data-testid={`select-stage-${stage.replace(/\s/g, "-")}`} />
                                <span className="text-xs font-semibold text-slate-300 uppercase tracking-wider">{stage}</span>
                                <span className="text-[10px] text-slate-500">({stageItems.length})</span>
                              </div>
                            </div>
                            {stageItems.map(item => (
                              <label key={item.id} className="flex items-center gap-3 px-3 pl-8 py-2 hover:bg-slate-800/40 cursor-pointer border-b border-slate-800/50 last:border-0">
                                <Checkbox checked={selectedItems.includes(item.id)} onCheckedChange={() => toggleItem(item.id)} data-testid={`select-item-${item.id}`} />
                                <div className="min-w-0">
                                  <p className="text-sm text-slate-200 truncate">{item.name}</p>
                                  {item.description && <p className="text-xs text-slate-500 truncate">{item.description}</p>}
                                </div>
                              </label>
                            ))}
                          </div>
                        );
                      });
                    })()
                  )}
                </div>
              </div>
            )}
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setShowCreate(false)} className="border-slate-700 text-slate-400">Cancel</Button>
            <Button onClick={handleCreate} className="bg-blue-600 hover:bg-blue-700" disabled={!discipline || !targetRole || !recipientName || !recipientEmail || selectedItems.length === 0} data-testid="create-request-submit">
              Create Request
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail Dialog */}
      <Dialog open={!!showDetail} onOpenChange={v => { if (!v) setShowDetail(null); }}>
        <DialogContent className="bg-slate-900 border-slate-800 max-w-3xl max-h-[85vh] overflow-y-auto">
          {detailData ? (
            <>
              <DialogHeader>
                <DialogTitle className="text-slate-100 flex items-center gap-2">
                  {detailData.discipline} — {detailData.recipient_name}
                  <Badge variant="outline" className="text-xs border-emerald-500/30 text-emerald-400">{detailData.target_role || "All Roles"}</Badge>
                  <Badge variant="outline" className={`text-xs ${STATUS_BADGE[detailData.status] || STATUS_BADGE.Draft}`}>{detailData.status}</Badge>
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div><span className="text-slate-500">Email:</span> <span className="text-slate-300 ml-1">{detailData.recipient_email}</span></div>
                  <div><span className="text-slate-500">Items:</span> <span className="text-slate-300 ml-1">{detailData.selected_item_ids?.length} deliverables</span></div>
                  <div><span className="text-slate-500">Expires:</span> <span className="text-slate-300 ml-1">{detailData.expires_at ? new Date(detailData.expires_at).toLocaleString() : "N/A"}</span></div>
                  <div><span className="text-slate-500">Status:</span> <span className="text-slate-300 ml-1">{detailData.status}</span></div>
                </div>

                {/* Scope change alert */}
                {(detailData.scope_changed || detailData.new_scope_items?.length > 0) && (
                  <Card className="bg-amber-950/20 border-amber-500/20">
                    <CardContent className="p-3">
                      <div className="flex items-center gap-2 mb-2">
                        <AlertTriangle className="w-4 h-4 text-amber-400" />
                        <span className="text-sm font-medium text-amber-400">Scope has changed since this request was sent</span>
                      </div>
                      {detailData.new_scope_items?.length > 0 && (
                        <div className="mt-2">
                          <p className="text-xs text-slate-400 mb-1">{detailData.new_scope_items.length} new {detailData.discipline} deliverable(s) not in this request:</p>
                          <div className="space-y-1">
                            {detailData.new_scope_items.map(item => (
                              <div key={item.id} className="flex items-center justify-between text-xs bg-slate-800/40 rounded px-2 py-1">
                                <span className="text-slate-300">{item.name} ({item.stage})</span>
                                <Button variant="ghost" size="sm" className="text-xs text-blue-400 h-6 px-2" onClick={() => handleAddNewItems(detailData.id, [item.id])}>
                                  <Plus className="w-3 h-3 mr-1" />Add
                                </Button>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                )}

                {/* Reference Files */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-xs font-medium text-slate-400">Reference Files ({detailData.reference_files?.length || 0})</label>
                    <label className="cursor-pointer">
                      <input type="file" multiple className="hidden" onChange={e => { if (e.target.files?.length) handleUploadFile(detailData.id, e.target.files); e.target.value = ""; }} data-testid="upload-ref-file" />
                      <span className="inline-flex items-center px-2 py-1 text-xs text-blue-400 bg-blue-500/10 rounded hover:bg-blue-500/20 border border-blue-500/20">
                        {uploading ? "Uploading..." : <><Upload className="w-3 h-3 mr-1" />Upload Files</>}
                      </span>
                    </label>
                  </div>
                  {detailData.reference_files?.map(f => (
                    <div key={f.id} className="flex items-center justify-between bg-slate-800/30 rounded px-3 py-1.5 mb-1">
                      <div className="flex items-center gap-2 text-xs text-slate-300"><FileText className="w-3 h-3 text-slate-500" />{f.filename}</div>
                      <Button variant="ghost" size="sm" className="text-xs text-red-400 h-6 px-2" onClick={() => handleDeleteFile(detailData.id, f.id)}><Trash2 className="w-3 h-3" /></Button>
                    </div>
                  ))}
                </div>

                {/* Current Items */}
                <div>
                  <label className="text-xs font-medium text-slate-400 block mb-1">Selected Deliverables</label>
                  <div className="max-h-40 overflow-y-auto border border-slate-800 rounded-lg">
                    {detailData.current_items?.map(item => (
                      <div key={item.id} className={`px-3 py-1.5 border-b border-slate-800/50 last:border-0 ${item._removed ? "opacity-40 line-through" : ""}`}>
                        <span className="text-sm text-slate-200">{item.name}</span>
                        <span className="text-xs text-slate-500 ml-2">{item.stage}</span>
                        {item._removed && <Badge className="ml-2 text-xs bg-red-500/10 text-red-400 border-red-500/20">Removed</Badge>}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Response Review */}
                {detailData.response && (
                  <Card className="bg-emerald-950/10 border-emerald-500/20">
                    <CardHeader className="py-3 px-4 border-b border-slate-800/50">
                      <CardTitle className="text-sm font-semibold text-emerald-400 flex items-center gap-2">
                        <CheckCircle className="w-4 h-4" />Submitted Estimates
                        <span className="text-xs text-slate-500 font-normal ml-auto">{detailData.response.submitted_at ? new Date(detailData.response.submitted_at).toLocaleString() : ""}</span>
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="p-0">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-slate-800/20">
                            <TableHead className="text-xs text-slate-500 px-3">Deliverable</TableHead>
                            {activeRoles.map(r => <TableHead key={r} className="text-xs text-slate-500 px-2 text-center w-20">{r.split(" ")[0]} <span className="text-slate-600 font-normal">(hrs)</span></TableHead>)}
                            <TableHead className="text-xs text-slate-500 px-2 w-32">Notes</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {Object.entries(detailData.response.estimates || {}).map(([itemId, est]) => {
                            const item = detailData.current_items?.find(i => i.id === itemId);
                            return (
                              <TableRow key={itemId} className="border-slate-800/30">
                                <TableCell className="text-sm text-slate-200 px-3">{item?.name || itemId}</TableCell>
                                {activeRoles.map(r => (
                                  <TableCell key={r} className="text-sm text-center text-slate-300 px-2">{est.hours?.[r] || "-"}</TableCell>
                                ))}
                                <TableCell className="text-xs text-slate-400 px-2">{est.notes || "-"}</TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                      {detailData.response.respondent_notes && (
                        <div className="px-4 py-2 border-t border-slate-800/30">
                          <p className="text-xs text-slate-500">General notes: <span className="text-slate-300">{detailData.response.respondent_notes}</span></p>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                )}

                {/* Action Buttons */}
                <div className="flex items-center gap-2 flex-wrap pt-2 border-t border-slate-800">
                  {detailData.status === "Draft" && (
                    <Button size="sm" onClick={() => handleSend(detailData.id)} disabled={sending} className="bg-blue-600 hover:bg-blue-700" data-testid="send-request-detail">
                      <Send className="w-3.5 h-3.5 mr-1.5" />{sending ? "Sending..." : "Send Request"}
                    </Button>
                  )}
                  {["Sent", "Viewed", "InProgress"].includes(detailData.status) && (
                    <>
                      <Button variant="outline" size="sm" onClick={() => handleSend(detailData.id)} disabled={sending} className="border-blue-500/30 text-blue-400">
                        <Send className="w-3.5 h-3.5 mr-1.5" />Resend
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => handleExtend(detailData.id)} className="border-slate-700 text-slate-400">
                        <RotateCcw className="w-3.5 h-3.5 mr-1.5" />Extend 48h
                      </Button>
                    </>
                  )}
                  {detailData.status === "Submitted" && (
                    <>
                      <Button size="sm" onClick={() => handleAccept(detailData.id, "auto")} className="bg-emerald-600 hover:bg-emerald-700" data-testid="accept-auto-btn">
                        <CheckCircle className="w-3.5 h-3.5 mr-1.5" />Accept & Merge into Scope
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => handleAccept(detailData.id, "manual")} className="border-emerald-500/30 text-emerald-400" data-testid="accept-manual-btn">
                        <Eye className="w-3.5 h-3.5 mr-1.5" />Accept (Manual Review)
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => handleReject(detailData.id)} className="border-amber-500/30 text-amber-400" data-testid="reject-btn">
                        <RotateCcw className="w-3.5 h-3.5 mr-1.5" />Request Revision
                      </Button>
                    </>
                  )}
                  {detailData.token && (
                    <Button variant="ghost" size="sm" className="text-xs text-slate-400 ml-auto" onClick={() => {
                      const url = `${window.location.origin}/p/${detailData.token}`;
                      navigator.clipboard.writeText(url);
                      toast.success("Link copied to clipboard");
                    }} data-testid="copy-link-btn">
                      <Link2 className="w-3.5 h-3.5 mr-1" />Copy Link
                    </Button>
                  )}
                  <Button variant="ghost" size="sm" className="text-xs text-red-400" onClick={() => handleDelete(detailData.id)} data-testid="delete-request-btn">
                    <Trash2 className="w-3.5 h-3.5 mr-1" />Delete
                  </Button>
                </div>
              </div>
            </>
          ) : (
            <div className="flex items-center justify-center py-8"><div className="w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" /></div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}



function TimesheetsTab({ projectId, project, pricing, permissions, activeRoles = [] }) {
  const confirm = useConfirm();
  const { user } = useAuth();
  const [entries, setEntries] = useState([]);
  const [summary, setSummary] = useState(null);
  const [weeks, setWeeks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showBatch, setShowBatch] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ date: new Date().toISOString().slice(0, 10), hours: "", stage: "", discipline: "", role: user?.role || "", note: "" });
  const [batchDays, setBatchDays] = useState([]);
  const [saving, setSaving] = useState(false);
  const [view, setView] = useState("entries"); // entries | summary | weeks
  const [reviewComment, setReviewComment] = useState("");

  const canLog = permissions?.can_log_hours !== false;
  const canReview = permissions?.can_review_timesheets || user?.role === "Director";
  const isDirector = user?.role === "Director" || user?.role === "Admin";

  const stages = pricing?.fee_by_stage ? Object.keys(pricing.fee_by_stage).sort() : [];
  const disciplines = pricing?.fee_by_discipline ? Object.keys(pricing.fee_by_discipline).sort() : [];
  const ROLES = activeRoles.length > 0 ? activeRoles : DEFAULT_ROLES;

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [entriesRes, summaryRes, weeksRes] = await Promise.all([
        API.get(`/projects/${projectId}/timesheets`),
        API.get(`/projects/${projectId}/timesheets/summary`),
        API.get(`/projects/${projectId}/timesheets/weeks`),
      ]);
      setEntries(entriesRes.data);
      setSummary(summaryRes.data);
      setWeeks(weeksRes.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [projectId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const resetForm = () => {
    setForm({ date: new Date().toISOString().slice(0, 10), hours: "", stage: "", discipline: "", role: user?.role || "", note: "" });
    setEditId(null);
    setShowForm(false);
  };

  const handleSubmit = async () => {
    if (!form.hours || parseFloat(form.hours) <= 0) { toast.error("Enter valid hours"); return; }
    if (!form.date) { toast.error("Select a date"); return; }
    setSaving(true);
    try {
      if (editId) {
        await API.put(`/timesheets/${editId}`, { ...form, hours: parseFloat(form.hours) });
        toast.success("Entry updated");
      } else {
        await API.post(`/projects/${projectId}/timesheets`, { ...form, hours: parseFloat(form.hours) });
        toast.success("Hours logged");
      }
      resetForm();
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    } finally { setSaving(false); }
  };

  // Batch entry: initialize weekdays for a given week start
  const initBatchWeek = () => {
    const monday = getMonday(new Date());
    const days = [];
    for (let i = 0; i < 5; i++) {
      const d = new Date(monday);
      d.setDate(d.getDate() + i);
      days.push({ date: d.toISOString().slice(0, 10), hours: "", stage: form.stage || "", discipline: form.discipline || "", role: form.role || user?.role || "", note: "" });
    }
    setBatchDays(days);
    setShowBatch(true);
  };

  const handleBatchSubmit = async () => {
    const valid = batchDays.filter(d => d.hours && parseFloat(d.hours) > 0);
    if (!valid.length) { toast.error("Enter hours for at least one day"); return; }
    setSaving(true);
    try {
      await API.post(`/projects/${projectId}/timesheets/batch`, { entries: valid.map(d => ({ ...d, hours: parseFloat(d.hours) })) });
      toast.success(`${valid.length} entries logged`);
      setShowBatch(false);
      setBatchDays([]);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Batch save failed");
    } finally { setSaving(false); }
  };

  const handleDuplicate = async (sourceDate) => {
    const target = new Date().toISOString().slice(0, 10);
    try {
      const res = await API.post(`/projects/${projectId}/timesheets/duplicate`, { source_date: sourceDate, target_date: target });
      toast.success(`${res.data.duplicated} entries duplicated to today`);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Duplicate failed");
    }
  };

  const handleEdit = (entry) => {
    setForm({ date: entry.date, hours: entry.hours.toString(), stage: entry.stage || "", discipline: entry.discipline || "", role: entry.role || "", note: entry.note || "" });
    setEditId(entry.id);
    setShowForm(true);
  };

  const handleDelete = async (entryId) => {
    const ok = await confirm({ title: "Delete Entry", description: "Delete this timesheet entry?", confirmText: "Delete", variant: "destructive" });
    if (!ok) return;
    try { await API.delete(`/timesheets/${entryId}`); toast.success("Deleted"); fetchData(); }
    catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  // Weekly workflow
  const handleSubmitWeek = async (weekStart) => {
    try {
      await API.post(`/projects/${projectId}/timesheets/weeks/submit`, { week_start: weekStart });
      toast.success("Week submitted for approval");
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || "Submit failed"); }
  };

  const handleApproveWeek = async (weekId) => {
    try {
      await API.put(`/timesheets/weeks/${weekId}/approve`, { comment: reviewComment });
      toast.success("Week approved"); setReviewComment(""); fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || "Approve failed"); }
  };

  const handleRejectWeek = async (weekId) => {
    try {
      await API.put(`/timesheets/weeks/${weekId}/reject`, { comment: reviewComment || "Revisions required" });
      toast.success("Week rejected"); setReviewComment(""); fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || "Reject failed"); }
  };

  const handleUnlockWeek = async (weekId) => {
    try {
      await API.put(`/timesheets/weeks/${weekId}/unlock`);
      toast.success("Week unlocked"); fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || "Unlock failed"); }
  };

  // Utilities
  const getMonday = (d) => { const dt = new Date(d); const day = dt.getDay(); const diff = dt.getDate() - day + (day === 0 ? -6 : 1); dt.setDate(diff); return dt; };
  const getCurrentWeekStart = () => getMonday(new Date()).toISOString().slice(0, 10);
  const varianceColor = (v) => v > 0 ? "text-red-400" : v < 0 ? "text-emerald-400" : "text-slate-400";
  const weekStatusColor = (s) => ({ Draft: "bg-slate-800 text-slate-400 border-slate-700", Submitted: "bg-amber-500/10 text-amber-400 border-amber-500/20", Approved: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20", Rejected: "bg-red-500/10 text-red-400 border-red-500/20" }[s] || "bg-slate-800 text-slate-400 border-slate-700");
  const DAY_NAMES = ["Mon", "Tue", "Wed", "Thu", "Fri"];

  // Group entries by date for duplication
  const uniqueDates = [...new Set(entries.filter(e => e.user_id === user?.id).map(e => e.date))].sort().reverse().slice(0, 5);

  if (loading) return <div className="flex items-center justify-center py-12"><div className="h-6 w-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div data-testid="timesheets-tab" className="space-y-4 md:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h2 className="font-heading text-xl font-semibold text-slate-100">Project Timesheets</h2>
          <p className="text-xs text-muted-foreground mt-0.5">
            {summary ? `${summary.total_actual_hours}h logged (${summary.approved_hours || 0}h approved) across ${summary.entry_count} entries` : "No hours logged yet"}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex bg-slate-800 rounded-md p-0.5 border border-slate-800">
            {["entries", "weeks", "summary"].map(v => (
              <button key={v} onClick={() => setView(v)} className={`px-3 py-1.5 text-xs font-medium rounded transition-colors capitalize ${view === v ? "bg-blue-600 text-white" : "text-slate-400 hover:text-slate-200"}`} data-testid={`timesheets-${v}-toggle`}>{v}</button>
            ))}
          </div>
          {canLog && (
            <>
              <Button size="sm" variant="outline" onClick={initBatchWeek} className="border-slate-700 text-slate-300" data-testid="batch-entry-btn">
                <Calendar className="w-3.5 h-3.5 mr-1.5" /> Week Entry
              </Button>
              <Button size="sm" onClick={() => { resetForm(); setShowForm(true); }} className="bg-blue-600 hover:bg-blue-500 text-white" data-testid="log-hours-btn">
                <Plus className="w-4 h-4 mr-1.5" /> Log Hours
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Totals Bar */}
      {summary && summary.total_estimated_hours > 0 && (
        <Card className="bg-slate-900 border border-slate-800">
          <CardContent className="p-4">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <div><p className="text-xs text-slate-500 uppercase tracking-wider font-medium">Estimated</p><p className="text-xl font-mono font-bold text-slate-100 mt-1">{summary.total_estimated_hours}h</p></div>
              <div><p className="text-xs text-slate-500 uppercase tracking-wider font-medium">Actual</p><p className="text-xl font-mono font-bold text-blue-400 mt-1">{summary.total_actual_hours}h</p></div>
              <div><p className="text-xs text-slate-500 uppercase tracking-wider font-medium">Approved</p><p className="text-xl font-mono font-bold text-emerald-400 mt-1">{summary.approved_hours || 0}h</p></div>
              <div><p className="text-xs text-slate-500 uppercase tracking-wider font-medium">Variance</p><p className={`text-xl font-mono font-bold mt-1 ${varianceColor(summary.total_variance)}`}>{summary.total_variance > 0 ? "+" : ""}{summary.total_variance}h</p></div>
              <div><p className="text-xs text-slate-500 uppercase tracking-wider font-medium">Progress</p>
                <div className="mt-2"><div className="w-full h-2 bg-slate-800 rounded-full overflow-hidden"><div className={`h-full rounded-full transition-all ${summary.total_actual_hours / summary.total_estimated_hours > 1 ? "bg-red-500" : summary.total_actual_hours / summary.total_estimated_hours > 0.8 ? "bg-amber-500" : "bg-emerald-500"}`} style={{ width: `${Math.min(100, (summary.total_actual_hours / summary.total_estimated_hours) * 100)}%` }} /></div>
                <p className="text-xs font-mono text-slate-500 mt-1">{Math.round((summary.total_actual_hours / summary.total_estimated_hours) * 100)}%</p></div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Single Entry Form */}
      {showForm && canLog && (
        <Card className="bg-slate-900 border border-blue-500/30 shadow-lg shadow-blue-500/5">
          <CardHeader className="border-b border-slate-800 py-3 px-4"><CardTitle className="text-sm font-semibold text-slate-100">{editId ? "Edit Entry" : "Log Hours"}</CardTitle></CardHeader>
          <CardContent className="p-4">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
              <div><Label className="text-xs text-slate-500 mb-1 block">Date *</Label><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="bg-slate-800 border-slate-700" data-testid="ts-date" /></div>
              <div><Label className="text-xs text-slate-500 mb-1 block">Hours *</Label><CellInput type="number" step="0.25" min="0.25" max="24" value={form.hours} onChange={(e) => setForm({ ...form, hours: e.target.value })} placeholder="0.0" className="bg-slate-800 border-slate-700 font-mono" data-testid="ts-hours" /></div>
              <div><Label className="text-xs text-slate-500 mb-1 block">Stage</Label>
                <Select value={form.stage} onValueChange={(v) => setForm({ ...form, stage: v })}><SelectTrigger className="bg-slate-800 border-slate-700" data-testid="ts-stage"><SelectValue placeholder="Select stage" /></SelectTrigger><SelectContent>{(stages.length > 0 ? stages : ["Stage 2 - Concept Design", "Stage 3 - Spatial Coordination", "Stage 4 - Technical Design", "Stage 5 - Manufacturing & Construction", "Stage 6 - Handover"]).map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select></div>
              <div><Label className="text-xs text-slate-500 mb-1 block">Discipline</Label>
                <Select value={form.discipline} onValueChange={(v) => setForm({ ...form, discipline: v })}><SelectTrigger className="bg-slate-800 border-slate-700" data-testid="ts-discipline"><SelectValue placeholder="Discipline" /></SelectTrigger><SelectContent>{(disciplines.length > 0 ? disciplines : ["Mechanical", "Electrical", "Public Health"]).map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent></Select></div>
              <div><Label className="text-xs text-slate-500 mb-1 block">Role</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}><SelectTrigger className="bg-slate-800 border-slate-700" data-testid="ts-role"><SelectValue placeholder="Role" /></SelectTrigger><SelectContent>{ROLES.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent></Select></div>
              <div><Label className="text-xs text-slate-500 mb-1 block">Note</Label><Input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="Task description" className="bg-slate-800 border-slate-700" data-testid="ts-note" /></div>
            </div>
            <div className="flex items-center gap-2 mt-4">
              <Button onClick={handleSubmit} disabled={saving} className="bg-blue-600 hover:bg-blue-500 text-white" data-testid="ts-submit">{saving ? "Saving..." : editId ? "Update" : "Log Hours"}</Button>
              <Button variant="outline" onClick={resetForm} className="border-slate-700" data-testid="ts-cancel">Cancel</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Batch Week Entry */}
      {showBatch && canLog && (
        <Card className="bg-slate-900 border border-blue-500/30 shadow-lg shadow-blue-500/5">
          <CardHeader className="border-b border-slate-800 py-3 px-4">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-semibold text-slate-100">Quick Week Entry</CardTitle>
              <Button variant="ghost" size="sm" onClick={() => setShowBatch(false)} className="text-slate-400 h-7"><span className="text-xs">Close</span></Button>
            </div>
          </CardHeader>
          <CardContent className="p-4">
            <div className="grid grid-cols-3 gap-3 mb-3">
              <div><Label className="text-xs text-slate-500 mb-1 block">Default Stage</Label>
                <Select value={batchDays[0]?.stage || ""} onValueChange={(v) => setBatchDays(prev => prev.map(d => ({ ...d, stage: v })))}><SelectTrigger className="bg-slate-800 border-slate-700"><SelectValue placeholder="Stage" /></SelectTrigger><SelectContent>{(stages.length > 0 ? stages : ["Stage 2 - Concept Design", "Stage 3 - Spatial Coordination", "Stage 4 - Technical Design"]).map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select></div>
              <div><Label className="text-xs text-slate-500 mb-1 block">Default Discipline</Label>
                <Select value={batchDays[0]?.discipline || ""} onValueChange={(v) => setBatchDays(prev => prev.map(d => ({ ...d, discipline: v })))}><SelectTrigger className="bg-slate-800 border-slate-700"><SelectValue placeholder="Discipline" /></SelectTrigger><SelectContent>{(disciplines.length > 0 ? disciplines : ["Mechanical", "Electrical", "Public Health"]).map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent></Select></div>
              <div><Label className="text-xs text-slate-500 mb-1 block">Default Role</Label>
                <Select value={batchDays[0]?.role || ""} onValueChange={(v) => setBatchDays(prev => prev.map(d => ({ ...d, role: v })))}><SelectTrigger className="bg-slate-800 border-slate-700"><SelectValue placeholder="Role" /></SelectTrigger><SelectContent>{ROLES.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent></Select></div>
            </div>
            <div className="grid grid-cols-5 gap-3">
              {batchDays.map((day, i) => (
                <div key={`batch-day-${i}`} className="bg-slate-800/50 rounded-lg p-3 border border-slate-800">
                  <p className="text-xs font-semibold text-slate-400 mb-1">{DAY_NAMES[i]}</p>
                  <p className="text-xs font-mono text-slate-500 mb-2">{day.date}</p>
                  <CellInput type="number" step="0.25" min="0" max="24" value={day.hours} onChange={(e) => { const next = [...batchDays]; next[i] = { ...next[i], hours: e.target.value }; setBatchDays(next); }} placeholder="0" className="bg-slate-900 border-slate-700 font-mono text-center h-10 text-lg" data-testid={`batch-hours-${i}`} />
                  <Input value={day.note} onChange={(e) => { const next = [...batchDays]; next[i] = { ...next[i], note: e.target.value }; setBatchDays(next); }} placeholder="Note" className="bg-slate-900 border-slate-700 mt-2 text-xs" />
                </div>
              ))}
            </div>
            <div className="flex items-center justify-between mt-4">
              <p className="text-xs text-slate-500 font-mono">Total: {batchDays.reduce((s, d) => s + (parseFloat(d.hours) || 0), 0).toFixed(1)}h</p>
              <Button onClick={handleBatchSubmit} disabled={saving} className="bg-blue-600 hover:bg-blue-500 text-white" data-testid="batch-submit">{saving ? "Saving..." : "Log Week"}</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Entries View */}
      {view === "entries" && (
        <Card className="bg-slate-900 border border-slate-800 overflow-hidden">
          {/* Quick duplicate */}
          {uniqueDates.length > 0 && canLog && (
            <div className="px-4 py-2 border-b border-slate-800 bg-slate-800/30 flex items-center gap-2 overflow-x-auto">
              <span className="text-xs text-slate-500 whitespace-nowrap">Duplicate from:</span>
              {uniqueDates.map(d => (
                <button key={d} onClick={() => handleDuplicate(d)} className="text-xs px-2 py-1 rounded bg-slate-800 border border-slate-700 text-slate-400 hover:text-blue-400 hover:border-blue-500/30 transition-colors whitespace-nowrap font-mono" data-testid={`dup-${d}`}>
                  {new Date(d).toLocaleDateString("en-GB", { day: "2-digit", month: "short" })}
                </button>
              ))}
            </div>
          )}
          <CardContent className="p-0">
            {entries.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                <Clock className="w-8 h-8 mx-auto mb-3 opacity-50" />
                <p className="text-sm">No timesheet entries yet</p>
                {canLog && <p className="text-xs mt-1">Click "Log Hours" or "Week Entry" to get started</p>}
              </div>
            ) : (
              <div className="table-scroll-wrapper">
                <Table>
                  <TableHeader><TableRow className="bg-slate-800/50">
                    <TableHead className="text-xs uppercase tracking-wider font-semibold text-slate-500 px-4">Date</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider font-semibold text-slate-500 px-4 text-right">Hours</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider font-semibold text-slate-500 px-4">Stage</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider font-semibold text-slate-500 px-4">Discipline</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider font-semibold text-slate-500 px-4">Role</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider font-semibold text-slate-500 px-4">Person</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider font-semibold text-slate-500 px-4">Note</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider font-semibold text-slate-500 px-4 text-center">Status</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider font-semibold text-slate-500 px-4 text-right">Actions</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {entries.map(e => {
                      const isOwn = e.user_id === user?.id;
                      const canEdit = (isOwn || isDirector) && !e.locked;
                      return (
                        <TableRow key={e.id} data-testid={`ts-entry-${e.id}`} className={`hover:bg-slate-800/30 ${e.locked ? "opacity-75" : ""}`}>
                          <TableCell className="px-4 py-2.5 text-sm font-mono whitespace-nowrap">{new Date(e.date).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}</TableCell>
                          <TableCell className="px-4 py-2.5 text-sm font-mono text-right font-bold text-blue-400">{e.hours}h</TableCell>
                          <TableCell className="px-4 py-2.5 text-xs text-slate-400 max-w-[140px] truncate">{e.stage || "—"}</TableCell>
                          <TableCell className="px-4 py-2.5 text-xs text-slate-400">{e.discipline || "—"}</TableCell>
                          <TableCell className="px-4 py-2.5 text-xs text-slate-400">{e.role || "—"}</TableCell>
                          <TableCell className="px-4 py-2.5 text-xs text-slate-400">{e.user_name}</TableCell>
                          <TableCell className="px-4 py-2.5 text-xs text-slate-500 max-w-[160px] truncate">{e.note || "—"}</TableCell>
                          <TableCell className="px-4 py-2.5 text-center">
                            {e.locked ? <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">Locked</span>
                            : e.week_status === "Submitted" ? <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-400 border border-amber-500/20">Pending</span>
                            : <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-800 text-slate-500 border border-slate-700">Draft</span>}
                          </TableCell>
                          <TableCell className="px-4 py-2.5 text-right">
                            {canEdit && (
                              <div className="flex items-center justify-end gap-1">
                                <button onClick={() => handleEdit(e)} className="p-1 text-slate-600 hover:text-blue-400 transition-colors" data-testid={`ts-edit-${e.id}`}><Pencil className="w-3.5 h-3.5" /></button>
                                <button onClick={() => handleDelete(e.id)} className="p-1 text-slate-600 hover:text-rose-400 transition-colors" data-testid={`ts-delete-${e.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                              </div>
                            )}
                            {e.locked && isDirector && <button onClick={() => { const w = weeks.find(wk => wk.user_id === e.user_id && wk.week_start === _getWeekStart(e.date)); if (w) handleUnlockWeek(w.id); }} className="p-1 text-slate-600 hover:text-amber-400 transition-colors" title="Director unlock"><History className="w-3.5 h-3.5" /></button>}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
          {/* Submit current week */}
          {canLog && entries.some(e => e.user_id === user?.id && !e.locked && e.week_status === "Draft") && (
            <div className="px-4 py-3 border-t border-slate-800 bg-slate-800/30 flex items-center justify-between">
              <p className="text-xs text-slate-500">Submit your current week for approval</p>
              <Button size="sm" onClick={() => handleSubmitWeek(getCurrentWeekStart())} className="bg-emerald-600 hover:bg-emerald-500 text-white" data-testid="submit-week-btn">
                <Send className="w-3.5 h-3.5 mr-1.5" /> Submit Week
              </Button>
            </div>
          )}
        </Card>
      )}

      {/* Weekly Approval View */}
      {view === "weeks" && (
        <div className="space-y-3">
          {weeks.length === 0 ? (
            <Card className="bg-slate-900 border border-slate-800"><CardContent className="py-12 text-center text-slate-500"><p className="text-sm">No weekly submissions yet</p></CardContent></Card>
          ) : weeks.map(w => (
            <Card key={w.id} className="bg-slate-900 border border-slate-800" data-testid={`week-${w.id}`}>
              <CardContent className="p-4">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <div className="flex items-center gap-4">
                    <div>
                      <p className="text-sm font-medium text-slate-200">
                        Week of {new Date(w.week_start).toLocaleDateString("en-GB", { day: "2-digit", month: "short" })} — {new Date(w.week_end).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}
                      </p>
                      <p className="text-xs text-slate-500 mt-0.5">{w.user_name} — {w.entry_count} entries</p>
                    </div>
                    <span className={`text-xs px-2 py-0.5 rounded border font-medium ${weekStatusColor(w.status)}`} data-testid={`week-status-${w.id}`}>{w.status}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-lg font-mono font-bold text-blue-400">{w.total_hours}h</span>
                    {canReview && w.status === "Submitted" && (
                      <div className="flex items-center gap-2">
                        <Input value={reviewComment} onChange={(e) => setReviewComment(e.target.value)} placeholder="Comment (optional)" className="bg-slate-800 border-slate-700 w-40 text-xs h-8" />
                        <Button size="sm" onClick={() => handleApproveWeek(w.id)} className="bg-emerald-600 hover:bg-emerald-500 text-white h-8" data-testid={`approve-week-${w.id}`}>Approve</Button>
                        <Button size="sm" variant="outline" onClick={() => handleRejectWeek(w.id)} className="border-red-500/30 text-red-400 hover:bg-red-500/10 h-8" data-testid={`reject-week-${w.id}`}>Reject</Button>
                      </div>
                    )}
                    {isDirector && w.status === "Approved" && (
                      <Button size="sm" variant="outline" onClick={() => handleUnlockWeek(w.id)} className="border-slate-700 text-slate-400 h-8" data-testid={`unlock-week-${w.id}`}><History className="w-3.5 h-3.5 mr-1" /> Unlock</Button>
                    )}
                  </div>
                </div>
                {w.review_comment && <p className="text-xs text-slate-500 mt-2 pl-4 border-l-2 border-slate-700">{w.reviewed_by}: {w.review_comment}</p>}
                {w.reviewed_at && <p className="text-xs text-slate-600 mt-1">{w.status === "Approved" ? "Approved" : "Reviewed"} {new Date(w.reviewed_at).toLocaleDateString("en-GB")} by {w.reviewed_by}</p>}
              </CardContent>
            </Card>
          ))}
          {summary?.weeks_summary && (
            <div className="grid grid-cols-4 gap-3 mt-2">
              {[["Draft", summary.weeks_summary.draft, "text-slate-400"], ["Submitted", summary.weeks_summary.submitted, "text-amber-400"], ["Approved", summary.weeks_summary.approved, "text-emerald-400"], ["Rejected", summary.weeks_summary.rejected, "text-red-400"]].map(([label, count, color]) => (
                <Card key={label} className="bg-slate-900 border border-slate-800"><CardContent className="p-3 text-center"><p className="text-xs text-slate-500">{label}</p><p className={`text-lg font-mono font-bold mt-1 ${color}`}>{count}</p></CardContent></Card>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Summary View */}
      {view === "summary" && summary && (
        <div className="space-y-4 md:space-y-6">
          {summary.stage_comparison?.length > 0 && (
            <Card className="bg-slate-900 border border-slate-800">
              <CardHeader className="border-b border-slate-800 py-3 px-4"><CardTitle className="text-sm font-semibold">Hours by Stage: Estimated vs Actual</CardTitle></CardHeader>
              <CardContent className="p-4">
                <div className="h-64"><ResponsiveContainer width="100%" height="100%"><BarChart data={summary.stage_comparison} barGap={2}><CartesianGrid strokeDasharray="3 3" stroke="#1e293b" /><XAxis dataKey="stage" tick={{ fill: "#64748b", fontSize: 10 }} angle={-15} textAnchor="end" height={60} interval={0} /><YAxis tick={{ fill: "#64748b", fontSize: 11 }} /><Tooltip contentStyle={{ background: "#0f172a", border: "1px solid #1e293b", borderRadius: "6px", fontSize: "12px" }} /><Legend wrapperStyle={{ fontSize: "11px" }} /><Bar dataKey="estimated" fill="#334155" name="Estimated" radius={[3, 3, 0, 0]} /><Bar dataKey="actual" fill="#3b82f6" name="Actual" radius={[3, 3, 0, 0]} /></BarChart></ResponsiveContainer></div>
                <div className="table-scroll-wrapper mt-4"><Table><TableHeader><TableRow className="bg-slate-800/50">
                  <TableHead className="text-xs uppercase text-slate-500 px-3">Stage</TableHead><TableHead className="text-xs uppercase text-slate-500 px-3 text-right">Estimated</TableHead><TableHead className="text-xs uppercase text-slate-500 px-3 text-right">Actual</TableHead><TableHead className="text-xs uppercase text-slate-500 px-3 text-right">Variance</TableHead><TableHead className="text-xs uppercase text-slate-500 px-3 text-right">%</TableHead>
                </TableRow></TableHeader><TableBody>{summary.stage_comparison.map(s => (
                  <TableRow key={s.stage}><TableCell className="px-3 py-2 text-xs">{s.stage}</TableCell><TableCell className="px-3 py-2 text-xs font-mono text-right">{s.estimated}h</TableCell><TableCell className="px-3 py-2 text-xs font-mono text-right text-blue-400">{s.actual}h</TableCell><TableCell className={`px-3 py-2 text-xs font-mono text-right font-bold ${varianceColor(s.variance)}`}>{s.variance > 0 ? "+" : ""}{s.variance}h</TableCell><TableCell className={`px-3 py-2 text-xs font-mono text-right ${varianceColor(s.variance_pct)}`}>{s.variance_pct > 0 ? "+" : ""}{s.variance_pct}%</TableCell></TableRow>
                ))}</TableBody></Table></div>
              </CardContent>
            </Card>
          )}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {summary.discipline_comparison?.length > 0 && (
              <Card className="bg-slate-900 border border-slate-800"><CardHeader className="border-b border-slate-800 py-3 px-4"><CardTitle className="text-sm font-semibold">By Discipline</CardTitle></CardHeader><CardContent className="p-4"><div className="h-48"><ResponsiveContainer width="100%" height="100%"><BarChart data={summary.discipline_comparison} layout="vertical" margin={{ left: 20 }}><CartesianGrid strokeDasharray="3 3" stroke="#1e293b" /><XAxis type="number" tick={{ fill: "#64748b", fontSize: 11 }} /><YAxis dataKey="discipline" type="category" tick={{ fill: "#64748b", fontSize: 10 }} width={100} /><Tooltip contentStyle={{ background: "#0f172a", border: "1px solid #1e293b", borderRadius: "6px", fontSize: "12px" }} /><Bar dataKey="estimated" fill="#334155" name="Estimated" radius={[0, 3, 3, 0]} /><Bar dataKey="actual" fill="#3b82f6" name="Actual" radius={[0, 3, 3, 0]} /></BarChart></ResponsiveContainer></div></CardContent></Card>
            )}
            {summary.role_comparison?.length > 0 && (
              <Card className="bg-slate-900 border border-slate-800"><CardHeader className="border-b border-slate-800 py-3 px-4"><CardTitle className="text-sm font-semibold">By Role</CardTitle></CardHeader><CardContent className="p-4"><div className="h-48"><ResponsiveContainer width="100%" height="100%"><BarChart data={summary.role_comparison} layout="vertical" margin={{ left: 20 }}><CartesianGrid strokeDasharray="3 3" stroke="#1e293b" /><XAxis type="number" tick={{ fill: "#64748b", fontSize: 11 }} /><YAxis dataKey="role" type="category" tick={{ fill: "#64748b", fontSize: 10 }} width={120} /><Tooltip contentStyle={{ background: "#0f172a", border: "1px solid #1e293b", borderRadius: "6px", fontSize: "12px" }} /><Bar dataKey="estimated" fill="#334155" name="Estimated" radius={[0, 3, 3, 0]} /><Bar dataKey="actual" fill="#3b82f6" name="Actual" radius={[0, 3, 3, 0]} /></BarChart></ResponsiveContainer></div></CardContent></Card>
            )}
          </div>
          {summary.by_user && Object.keys(summary.by_user).length > 0 && (
            <Card className="bg-slate-900 border border-slate-800"><CardHeader className="border-b border-slate-800 py-3 px-4"><CardTitle className="text-sm font-semibold">By Team Member</CardTitle></CardHeader><CardContent className="p-4"><div className="grid grid-cols-2 md:grid-cols-4 gap-3">{Object.entries(summary.by_user).sort((a, b) => b[1] - a[1]).map(([name, hours]) => (<div key={name} className="bg-slate-800/50 rounded-lg p-3 border border-slate-800"><p className="text-xs text-slate-500 truncate">{name}</p><p className="text-lg font-mono font-bold text-blue-400 mt-1">{hours}h</p></div>))}</div></CardContent></Card>
          )}
        </div>
      )}
    </div>
  );
}

function _getWeekStart(dateStr) {
  const d = new Date(dateStr);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  d.setDate(diff);
  return d.toISOString().slice(0, 10);
}